import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useLang } from '../contexts/LangContext'
import HelpTip from '../components/HelpTip'
import { RoadmapConfigPanel, RoadmapTimeline } from './eaviews/RoadmapView'
import { DashboardBuilder, DashboardGrid, DashboardWidget } from './eaviews/DashboardBuilder'

import SemanticViewBuilder from './eaviews/SemanticViewBuilder'
import { useSearchParams } from 'react-router-dom'
import { CollectionsPanel } from './eaviews/CollectionsPanel'
import { LandscapeView } from './eaviews/LandscapeView'
import { StateMenu, EvolutionSummary, allStates, comparableWith, stateTitle, StateLine } from './eaviews/ArchitectureStates'
import { InsightsStrip, ViewInsight } from './eaviews/InsightsStrip'
import { layoutByQuestion, layoutByDomain } from './eaviews/canvasLayout'
import { exportAsJSON, exportNodesAsCSV, exportMatrixAsCSV, exportRoadmapAsCSV, exportGraphAsSVG, exportGraphAsPNG, exportGraphAsPDF, exportNodesAsPDF, exportMatrixAsPDF, exportRoadmapAsPDF, exportGraphAsPPTX, exportNodesAsPPTX, exportMatrixAsPPTX, exportRoadmapAsPPTX } from './eaviews/exportUtils'
import { determineTableMode, buildRelationshipTable, buildMatrix, cellInteraction, arrangeMatrix, MatrixOrder } from './eaviews/tableMatrixUtils'
import { buildCapabilityMapDisplay, computeCapabilityOverlayCount, buildCapabilityDrilldown, buildHeatmapDisplay, buildTreeDisplay, buildCardContext } from './eaviews/capabilityHeatmapTreeCardsUtils'
import { buildGraphIndexes, chooseFocusObject, computeInitialVisibleSet, expandNeighbors, expandAllNextPathHops, collapseBranch, pruneDanglingRelationships, computePathHighlight, applyGraphFilters, ExpandDirection } from './eaviews/graphDisclosureUtils'
import { buildScenarioLineageTree, getScenarioLineagePath, chooseVisualizationAfterScenarioSwitch } from './eaviews/scenarioSelectorUtils'
import { buildChangeSummaryRows, buildRelationshipChangeRows, buildComparisonMatrix, applyComparisonFilters, buildComparisonGraphDataset, buildComparisonCapabilityMap, buildHeatmapComparison, buildComparisonTree, buildComparisonCards, CHANGE_TYPE_SYMBOL } from './eaviews/comparisonUtils'
import { buildPropertyFieldDisplay, requiresCurrentEditConfirmation, isScenarioLocked, determineAssetActions, canIntroduce, buildRelationshipAuthoringRows } from './eaviews/authoringUtils'
import { resolveEvidenceRef, buildProposedChangeRows, canApproveProposal, CLAIM_STYLE } from './eaviews/aiAssistUtils'

const API = process.env.REACT_APP_API_URL || 'https://archmindworks.com/api/v1'

function useViewsApi() {
  const { token } = useAuth() as any
  const h = () => ({ Authorization: `Bearer ${token || localStorage.getItem('ea_token') || ''}`, 'Content-Type': 'application/json' })
  const get = (p: string) => fetch(`${API}${p}`, { headers: h() }).then(r => r.json())
  const post = (p: string, b?: any) => fetch(`${API}${p}`, { method: 'POST', headers: h(), body: b ? JSON.stringify(b) : undefined }).then(r => r.json())
  const put = (p: string, b: any) => fetch(`${API}${p}`, { method: 'PUT', headers: h(), body: JSON.stringify(b) }).then(r => r.json())
  // Phase 5C: added for scenario authoring's PATCH endpoints
  // (setPropertyOverrides, setScenarioStatus) - matches put's exact
  // shape, since PATCH/PUT differ only in HTTP semantics, not in how
  // this client needs to send them.
  const patch = (p: string, b: any) => fetch(`${API}${p}`, { method: 'PATCH', headers: h(), body: JSON.stringify(b) }).then(r => r.json())
  // Phase 5C: del gained an optional body - removeRelationship is
  // identified by its canonical (source, target, type) key, not a
  // single path id, so it's a body-based DELETE (a standard, valid HTTP
  // pattern) rather than reshaping an already-verified backend route to
  // avoid needing one. Return value made robust to both an empty
  // response body (the two pre-existing callers' endpoints return
  // nothing - r.json() rejects on an empty body, caught here and falls
  // back to the original r.ok boolean, preserving their exact existing
  // behavior) and a real JSON body (Phase 5C's new endpoints return
  // {delta, warnings}/{undone} - the warnings array is exactly what the
  // authoring UI needs to surface).
  const del = (p: string, b?: any) => fetch(`${API}${p}`, { method: 'DELETE', headers: h(), body: b ? JSON.stringify(b) : undefined })
    .then(r => r.json().catch(() => r.ok))
  return { get, post, put, patch, del }
}

const S = {
  page: { padding: 0, height: '100%', display: 'flex', flexDirection: 'column' as const, background: 'var(--navy)' },
  header: { padding: '20px 28px 16px', display: 'flex', alignItems: 'center', gap: 16, borderBottom: '1px solid var(--border)' },
  tabs: { display: 'flex', gap: 2, padding: '0 28px', borderBottom: '1px solid var(--border)', background: 'var(--navy-light)' },
  tab: (a: boolean) => ({ padding: '10px 18px', fontSize: 13, fontWeight: a ? 600 : 400, color: a ? 'var(--accent)' : 'var(--text-dim)', borderBottom: a ? '2px solid var(--accent)' : '2px solid transparent', borderTop: 'none', borderLeft: 'none', borderRight: 'none', cursor: 'pointer', background: 'none' }),
  content: { flex: 1, overflow: 'auto', padding: '24px 28px' },
  card: { background: 'var(--navy-light)', border: '1px solid var(--border)', borderRadius: 10, padding: 20 },
  btn: (v: 'primary'|'secondary'|'danger' = 'secondary') => ({ padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none', background: v === 'primary' ? 'var(--accent)' : v === 'danger' ? '#e74c3c22' : 'var(--navy-mid)', color: v === 'primary' ? 'var(--navy)' : v === 'danger' ? '#e74c3c' : 'var(--text)' }),
  input: { width: '100%', padding: '8px 12px', background: 'var(--navy)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none' },
  label: { fontSize: 11, color: 'var(--text-dim)', fontWeight: 600, marginBottom: 4, display: 'block' },
  badge: (c: string) => ({ padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: c + '22', color: c }),
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 },
  statCard: { background: 'var(--navy-light)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 20px' },
  row: { display: 'flex', alignItems: 'center', gap: 12 },
}

const VIZ_ICONS: Record<string,string> = { GRAPH:'🕸', MATRIX:'⊞', HEATMAP:'🔥', CAPABILITY_MAP:'⬛', TABLE:'≡', ROADMAP:'🗺', LANDSCAPE:'🗾', DASHBOARD:'📊' }
// Renderers this viewer has. With a viewpoint contract the strip offers only
// the contract's visualizations the result supports; custom views keep the
// full strip.
const RENDERABLE_MODES = ['LANDSCAPE','GRAPH','CAPABILITY_MAP','HEATMAP','MATRIX','TREE','CARDS','TABLE']
const LEGACY_MODES = ['GRAPH','CAPABILITY_MAP','HEATMAP','MATRIX','TREE','CARDS','TABLE']
const CATEGORY_COLOR: Record<string,string> = { Business:'#3498db', Application:'#e67e22', Data:'#1abc9c', Technology:'#e74c3c', Security:'#9b59b6', 'Cross-Domain':'#f39c12', Strategic:'#2ecc71', Governance:'#7f8c8d', Custom:'#64748B' }
const STATE_COLOR: Record<string,string> = { CURRENT:'#2ecc71', TARGET:'#3498db', TRANSITION:'#f39c12', BASELINE:'#7f8c8d', PLANNED:'#9b59b6' }
const STATUS_COLOR: Record<string,string> = { DRAFT:'#f39c12', PUBLISHED:'#2ecc71', ARCHIVED:'#7f8c8d' }
const DOMAIN_COLOR: Record<string,string> = { BUSINESS:'#3498db', APPLICATION:'#e67e22', DATA:'#1abc9c', TECHNOLOGY:'#e74c3c', SECURITY:'#9b59b6', STRATEGIC:'#2ecc71', BENEFICIARY_EXPERIENCE:'#16a085', CROSS_CUTTING:'#7f8c8d' }
const TYPE_COLOR: Record<string,string> = { CAPABILITY:'#3498db', APPLICATION:'#e67e22', DATA_ENTITY:'#1abc9c', TECH_COMPONENT:'#e74c3c', SECURITY_CONTROL:'#9b59b6', EA_PRINCIPLE:'#2ecc71', INTEGRATION:'#f39c12' }

// ── Force-directed auto-layout ──────────────────────────────────────────────
//
// A hand-rolled physics simulation (repulsion between every node pair,
// spring attraction along edges) rather than a library dependency - kept
// intentionally simple and synchronous. Capped to graphs of
// FORCE_LAYOUT_MAX_NODES or fewer: beyond that, an O(n²)-per-iteration
// simulation would visibly block the main thread regardless of
// implementation quality, and a genuinely non-blocking version would need
// Web Workers - out of scope for a first version. Iteration count also
// scales down as node count grows, so mid-size graphs (dozens of nodes)
// stay responsive without needing the hard cutoff.
const FORCE_LAYOUT_MAX_NODES = 150
function computeForceLayout(nodes: any[], edges: any[], existingPositions: Record<string, { x: number; y: number }>): Record<string, { x: number; y: number }> {
  const pos: Record<string, { x: number; y: number }> = {}
  nodes.forEach((n, i) => {
    const angle = (i / Math.max(1, nodes.length)) * Math.PI * 2
    pos[n.id] = existingPositions[n.id] ? { ...existingPositions[n.id] } : { x: 500 + Math.cos(angle) * 250, y: 350 + Math.sin(angle) * 250 }
  })
  const iterations = Math.max(30, Math.min(200, Math.round(15000 / Math.max(1, nodes.length))))
  const IDEAL_EDGE_LENGTH = 190
  const REPULSION = 14000
  for (let iter = 0; iter < iterations; iter++) {
    const forces: Record<string, { x: number; y: number }> = {}
    nodes.forEach(n => { forces[n.id] = { x: 0, y: 0 } })
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i].id, b = nodes[j].id
        const dx = pos[a].x - pos[b].x, dy = pos[a].y - pos[b].y
        const dist = Math.sqrt(dx * dx + dy * dy) || 1
        const force = REPULSION / (dist * dist)
        const fx = (dx / dist) * force, fy = (dy / dist) * force
        forces[a].x += fx; forces[a].y += fy
        forces[b].x -= fx; forces[b].y -= fy
      }
    }
    for (const e of edges) {
      if (!pos[e.sourceId] || !pos[e.targetId]) continue
      const dx = pos[e.targetId].x - pos[e.sourceId].x, dy = pos[e.targetId].y - pos[e.sourceId].y
      const dist = Math.sqrt(dx * dx + dy * dy) || 1
      const force = (dist - IDEAL_EDGE_LENGTH) * 0.02
      const fx = (dx / dist) * force, fy = (dy / dist) * force
      forces[e.sourceId].x += fx; forces[e.sourceId].y += fy
      forces[e.targetId].x -= fx; forces[e.targetId].y -= fy
    }
    for (const n of nodes) {
      pos[n.id].x += Math.max(-30, Math.min(30, forces[n.id].x)) * 0.5
      pos[n.id].y += Math.max(-30, Math.min(30, forces[n.id].y)) * 0.5
    }
  }
  return pos
}

// ── View Library (predefined viewpoints) ──────────────────────────────────────
// Real bug fixed here (production, Sept 2026): this used to await
// Promise.all([GET list, POST seed]) with no .catch() at all - once the
// backend's seed endpoint got slow (library grew from 12 to 60
// viewpoints; see the backend fix in the same incident), a timeout on
// the POST rejected the whole Promise.all silently, and the page hung
// on "Loading" forever with no error shown. Fixed two ways: (1) the
// list load and the seed call are now independent - a failed/slow seed
// never blocks the list from rendering, since GET already returns
// whatever's been seeded from any PRIOR load; (2) seeding only runs
// once per browser session (sessionStorage guard) rather than on every
// single page visit, since it re-upserts the entire library every time
// for no benefit once a session has already done it once.
const VIEWPOINTS_SEEDED_KEY = 'ea_viewpoints_seeded_session'

// Every gap the compatibility check found, in plain words.
export function compatGapText(compat: any): string {
  const parts: string[] = []
  if (compat?.missingRootTypes?.length) parts.push(`No data for: ${compat.missingRootTypes.join(', ')}`)
  if (compat?.missingRelatedTypes?.length) parts.push(`No related data for: ${compat.missingRelatedTypes.join(', ')}`)
  for (const r of compat?.missingRelationships || []) parts.push(`No ${r.via || 'relationships'} between ${r.from} and ${r.to}`)
  return parts.join(' · ')
}

function ViewLibrary({ api, onCreate, onOpen }: { api: any, onCreate: (v: any) => void, onOpen?: (view: any) => void }) {
  const { t, isAR } = useLang()
  const auth = useAuth() as any
  const canCustomize = typeof auth?.hasPermission === 'function' ? auth.hasPermission('Views.Create') : true
  const [search, setSearch] = useState('')
  const [leadershipOnly, setLeadershipOnly] = useState(false)
  const [opening, setOpening] = useState<string | null>(null)
  // Direct open: straight to the view, in a private workspace - no builder.
  const openDirect = (vp: any) => {
    if (!onOpen) { onCreate(vp); return }
    setOpening(vp.id)
    Promise.resolve(api.post(`/ea-views/open-viewpoint/${vp.id}`)).then((view: any) => {
      if (view?.id) onOpen(view)
    }).finally(() => setOpening(null))
  }
  const [viewpoints, setViewpoints] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [filterCat, setFilterCat] = useState('')
  const [filterStakeholder, setFilterStakeholder] = useState('')
  const [compatibility, setCompatibility] = useState<Record<string, any>>({})

  const loadList = useCallback(() => {
    api.get('/ea-views/viewpoints').then((vps: any) => {
      setViewpoints(Array.isArray(vps) ? vps : [])
      setLoading(false)
      setLoadError(false)
      const list: any[] = Array.isArray(vps) ? vps : []
      // Fired once per viewpoint after the list itself has loaded, not
      // blocking the initial render - compatibility badges fill in
      // progressively rather than delaying the whole library.
      list.forEach((vp: any) => {
        api.get(`/ea-views/viewpoints/${vp.id}/compatibility`).then((c: any) => {
          if (c) setCompatibility(prev => ({ ...prev, [vp.id]: c }))
        }).catch(() => {})
      })
    }).catch(() => { setLoading(false); setLoadError(true) })
  }, [api])

  useEffect(() => {
    if (sessionStorage.getItem(VIEWPOINTS_SEEDED_KEY)) {
      // Already seeded once this session - just load the list, which
      // already reflects everything a prior seed run inserted. Skips
      // the ~60-entry upsert round trip entirely on every subsequent
      // page visit within the session.
      loadList()
      return
    }
    api.post('/ea-views/viewpoints/seed')
      .then(() => sessionStorage.setItem(VIEWPOINTS_SEEDED_KEY, '1'))
      .catch(() => {})
      // Load the list regardless of whether seeding itself succeeded -
      // a slow/failed seed should never block showing whatever the
      // library already has (which, for any tenant that's ever loaded
      // this page before, is everything).
      .finally(loadList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const categories = Array.from(new Set(viewpoints.map(v => v.category)))
  const allStakeholders = Array.from(new Set(viewpoints.flatMap(v => v.stakeholders || []))).sort()
  const q = search.trim().toLowerCase()
  const filtered = viewpoints.filter(v =>
    (!filterCat || v.category === filterCat) &&
    (!filterStakeholder || (v.stakeholders || []).includes(filterStakeholder)) &&
    (!leadershipOnly || (v.contract?.audience || []).includes('EXECUTIVE')) &&
    (!q || [v.name, v.nameAr, v.description, v.contract?.question, v.contract?.questionAr].some((x: any) => String(x || '').toLowerCase().includes(q))))

  const COMPAT_BADGE: Record<string, { label: string; color: string }> = {
    COMPATIBLE: { label: '✓ Compatible', color: '#2ecc71' },
    PARTIAL: { label: '! Partial', color: '#f39c12' },
    NOT_COMPATIBLE: { label: '✕ Not Compatible', color: '#e74c3c' },
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div><div style={{ fontSize: 18, fontWeight: 700 }}>View Library</div><div style={{ fontSize: 13, color: 'var(--text-dim)' }}>Browse predefined EA views — select one to activate or customize</div></div>
        <button style={S.btn('primary')} onClick={() => onCreate(null)}>+ Custom View</button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' as const, alignItems: 'center' }}>
        <button style={{ ...S.btn(), background: !filterCat ? 'var(--accent)' : undefined, color: !filterCat ? 'var(--navy)' : undefined }} onClick={() => setFilterCat('')}>All</button>
        {categories.map(c => <button key={c} style={{ ...S.btn(), background: filterCat === c ? CATEGORY_COLOR[c] : undefined, color: filterCat === c ? '#fff' : undefined }} onClick={() => setFilterCat(c)}>{c}</button>)}
        <label htmlFor="library-search" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>{t('eaviews.lib_search')}</label>
        <input id="library-search" style={{ ...S.input, maxWidth: 260 }} placeholder={t('eaviews.lib_search')} value={search} onChange={e => setSearch(e.target.value)} />
        <input id="library-leadership" type="checkbox" checked={leadershipOnly} onChange={e => setLeadershipOnly(e.target.checked)} />
        <label htmlFor="library-leadership" style={{ fontSize: 12 }}>{t('eaviews.lib_leadership')}</label>
        <HelpTip text={t('eaviews.lib_help')} />
        {allStakeholders.length > 0 && (
          <select style={{ ...S.input, maxWidth: 180, marginLeft: 'auto' }} value={filterStakeholder} onChange={e => setFilterStakeholder(e.target.value)}>
            <option value="">All Stakeholders</option>
            {allStakeholders.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
      </div>

      {loading ? <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40 }}>Loading...</div> : loadError ? (
        <div style={{ textAlign: 'center', color: 'var(--text-dim)', padding: 40 }}>
          Could not load the View Library. <button style={S.btn('primary')} onClick={() => { setLoading(true); loadList() }}>Retry</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {filtered.map(vp => {
            const compat = compatibility[vp.id]
            const badge = compat ? COMPAT_BADGE[compat.status] : null
            return (
            <div key={vp.id} style={{ ...S.card, cursor: 'pointer', transition: 'all 0.15s' }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = CATEGORY_COLOR[vp.category] || 'var(--accent)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: 8, background: (CATEGORY_COLOR[vp.category] || '#3498db') + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                  {VIZ_ICONS[vp.defaultVisualization] || '📊'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{vp.name}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' as const }}>
                    <span style={S.badge(CATEGORY_COLOR[vp.category] || '#3498db')}>{vp.category}</span>
                    <span style={S.badge('#7f8c8d')}>{(vp.defaultVisualization||'').replace(/_/g,' ')}</span>
                    {badge && <span style={S.badge(badge.color)} title={compat.status === 'PARTIAL' || compat.status === 'NOT_COMPATIBLE' ? compatGapText(compat) : undefined}>{badge.label}</span>}
                  </div>
                </div>
              </div>
              {vp.contract?.question && <div data-testid="viewpoint-question" style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.5, marginBottom: 6 }}>{isAR && vp.contract.questionAr ? vp.contract.questionAr : vp.contract.question}</div>}
              <div style={{ fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.6, marginBottom: 12 }}>{vp.description}</div>
              {vp.purpose && <div style={{ fontSize: 11, color: 'var(--text-dim)', fontStyle: 'italic', marginBottom: 12 }}>Purpose: {vp.purpose}</div>}
              {(vp.stakeholders?.length > 0 || vp.concerns?.length > 0) && (
                <div style={{ marginBottom: 12 }}>
                  {vp.stakeholders?.length > 0 && <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4 }}>👤 {vp.stakeholders.join(', ')}</div>}
                  {vp.concerns?.length > 0 && <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' as const }}>{vp.concerns.map((c: string) => <span key={c} style={{ ...S.badge('#9b59b6'), fontSize: 10 }}>{c}</span>)}</div>}
                </div>
              )}
              <div style={{ display: 'flex', gap: 6 }}>
                <button style={{ ...S.btn('primary'), flex: 1, fontSize: 12 }} disabled={opening === vp.id} onClick={() => openDirect(vp)}>{opening === vp.id ? '⏳' : t('eaviews.lib_open')}</button>
                {canCustomize && <button style={{ ...S.btn(), fontSize: 12 }} onClick={() => onCreate(vp)}>{t('eaviews.lib_customize')}</button>}
              </div>
            </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── My Views list ─────────────────────────────────────────────────────────────
function MyViews({ api, onOpen, initialArchitectureState }: { api: any, onOpen: (v: any) => void, initialArchitectureState?: string }) {
  const [views, setViews] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [filterCat, setFilterCat] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterArchState, setFilterArchState] = useState(initialArchitectureState || '')
  const [favOnly, setFavOnly] = useState(false)

  // Syncs to a CHANGED initialArchitectureState, not just its value at
  // first mount - matching ObjectContextViewer's prop-sync pattern
  // (assetId in its useCallback dependency array) rather than a plain
  // useState initial value, which would silently miss a second "Related
  // Architecture Views" link click from ADM while already on this tab
  // (setTab('my-views') to an already-current value doesn't force a
  // remount, so a useState-only initial value would never update).
  useEffect(() => { if (initialArchitectureState) setFilterArchState(initialArchitectureState) }, [initialArchitectureState])

  const load = useCallback(() => {
    setLoading(true)
    api.get('/ea-views').then((d: any) => { setViews(Array.isArray(d) ? d : []); setLoading(false) })
  }, [api])

  useEffect(() => { load() }, [load])

  const toggleFav = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    await api.post(`/ea-views/${id}/favorite`)
    load()
  }

  const deleteView = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    if (!window.confirm('Delete this view?')) return
    await api.del(`/ea-views/${id}`)
    load()
  }

  const cloneView = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    await api.post(`/ea-views/${id}/clone`)
    load()
  }

  const filtered = views.filter(v =>
    (!filterCat || v.category === filterCat) &&
    (!filterStatus || v.status === filterStatus) &&
    (!filterArchState || v.architectureState === filterArchState) &&
    (!favOnly || v.isFavorite)
  )
  const categories = [...new Set(views.map(v => v.category))]

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>My Views</div>
        <div style={S.row}>
          <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>{filtered.length} / {views.length}</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <select style={{ ...S.input, maxWidth: 160 }} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select style={{ ...S.input, maxWidth: 140 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="DRAFT">Draft</option>
          <option value="PUBLISHED">Published</option>
        </select>
        <select style={{ ...S.input, maxWidth: 150 }} value={filterArchState} onChange={e => setFilterArchState(e.target.value)}>
          <option value="">All Architecture States</option>
          {['BASELINE', 'CURRENT', 'TARGET', 'TRANSITION', 'PLANNED'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button style={{ ...S.btn(), background: favOnly ? '#f39c1222' : undefined, color: favOnly ? '#f39c12' : undefined }} onClick={() => setFavOnly(!favOnly)}>⭐ Favorites</button>
      </div>

      {loading ? <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40 }}>Loading...</div> : (
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 6 }}>
          {filtered.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: 'var(--text-dim)', padding: 40 }}>No views yet. Go to View Library to activate a predefined view, or create a custom view.</div>}
          {filtered.map(v => (
            <div key={v.id} onClick={() => onOpen(v)} style={{ ...S.card, padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14, transition: 'all 0.15s' }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--accent)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: (CATEGORY_COLOR[v.category] || '#3498db') + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                {VIZ_ICONS[v.visualization] || '📊'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {v.isFavorite && <span style={{ color: '#f39c12' }}>⭐</span>}
                  {v.name}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-dim)', display: 'flex', gap: 8, marginTop: 2 }}>
                  <span style={S.badge(CATEGORY_COLOR[v.category] || '#3498db')}>{v.category}</span>
                  <span style={S.badge(STATUS_COLOR[v.status])}>{v.status}</span>
                  <span style={S.badge(STATE_COLOR[v.architectureState] || '#7f8c8d')}>{v.architectureState}</span>
                  <span style={{ color: 'var(--text-dim)' }}>👁 {v.viewCount}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button style={{ ...S.btn(), padding: '3px 10px', fontSize: 12 }} onClick={e => toggleFav(e, v.id)}>⭐</button>
                <button style={{ ...S.btn(), padding: '3px 10px', fontSize: 12 }} onClick={e => cloneView(e, v.id)} title="Clone this view">⧉</button>
                <button style={{ ...S.btn('danger'), padding: '3px 10px', fontSize: 12 }} onClick={e => deleteView(e, v.id)}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── View Viewer (graph + matrix + heatmap + capability map) ───────────────────
function ViewViewer({ api, view: viewProp, onBack, onRefresh }: { api: any, view: any, onBack: () => void, onRefresh: () => void }) {
  const { t, isAR } = useLang()
  // Progressive disclosure: actions appear only for people who can use them.
  // (TENANT_ADMIN passes every check inside hasPermission.)
  const auth = useAuth() as any
  const can = (code: string) => typeof auth?.hasPermission === 'function' ? auth.hasPermission(code) : true
  // publish()/approveView()/rejectView()/requestApproval() all return the
  // updated view from the backend, but the parent's onRefresh only
  // refreshes dashboard stats, not this specific view object it passed
  // down as a prop - without this, clicking e.g. Approve would show no
  // visible change until navigating away and back, which looks like the
  // action silently failed. viewOverrides holds whatever fields the most
  // recent action changed, merged over the (possibly stale) prop for
  // display; reset whenever the user opens a genuinely different view.
  const [viewOverrides, setViewOverrides] = useState<Partial<any>>({})
  useEffect(() => { setViewOverrides({}) }, [viewProp.id])
  const view = { ...viewProp, ...viewOverrides }
  // Phase 5A Section 10: ?scenario=<id> URL state - read once on mount as
  // the initial scenario override (falls back safely to the view's own
  // saved scenario if the id is invalid/cross-tenant, since the backend
  // still validates it), updated on every successful switch so a
  // refresh/shared link reopens the same scenario. Kept lightweight
  // (this component's own useSearchParams call, same hook already used
  // elsewhere in this file) rather than threading scenario state through
  // routing config.
  const [searchParams, setSearchParams] = useSearchParams()

  const [data, setData] = useState<any>(null)
  // Phase 4A: canonical ViewDataset + its eligibility evaluation, fetched
  // in the SAME call as `data` (see load() below) - avoids a second,
  // duplicate fetch. Only Table and Matrix consume these; every other
  // renderer (Graph/CapabilityMap/Heatmap/Tree/Cards) continues reading
  // `data` exactly as before, completely unaware this exists.
  const [dataset, setDataset] = useState<any>(null)
  // Viewpoint Contract presentation from /dataset: the question, the
  // visualizations worth offering for this result, and what an empty
  // result means. null for a custom view (no contract).
  const [contract, setContract] = useState<any>(null)
  const [presentation, setPresentation] = useState<any>(null)
  const [insights, setInsights] = useState<ViewInsight[]>([])
  // Meta Model change impact for this view (backend semanticReview).
  const [semanticReview, setSemanticReview] = useState<any>(null)
  // A fact the user selected: the view narrows to the objects behind it.
  const [focusInsight, setFocusInsight] = useState<ViewInsight | null>(null)
  const [stateChange, setStateChange] = useState<any>(null)
  const [eligibility, setEligibility] = useState<any>(null)
  // Phase 5A: scenario selector state. The "saved/default" scenario
  // lives on `view.scenarioId` itself (already in scope, via
  // viewOverrides for optimistic updates after Set-as-Default) - not
  // duplicated here. `committedScenarioId`/`committedData*` are only
  // ever updated together, atomically, when a scenario-switch request
  // both succeeds AND is still the latest one issued (Section 4's
  // "transactional state transition" - the label the user sees and the
  // dataset actually displayed must never disagree). `pendingScenarioId`
  // is shown as a loading indicator on the requested-but-not-yet-
  // committed scenario; `scenarioSwitchError` surfaces a failed switch
  // without ever touching the still-valid committed state.
  const [scenarios, setScenarios] = useState<any[]>([])
  // The view's lines of change (Current -> Transition -> Target per
  // programme). null until loaded; the old scenario tree is the fallback.
  const [stateLines, setStateLines] = useState<StateLine[] | null>(null)
  const [evolution, setEvolution] = useState<any>(null)
  const [committedScenarioId, setCommittedScenarioId] = useState<string | null>(null)
  const [pendingScenarioId, setPendingScenarioId] = useState<string | null>(null)
  const [scenarioSwitchError, setScenarioSwitchError] = useState<string | null>(null)
  const [vizAutoSwitchNotice, setVizAutoSwitchNotice] = useState<string | null>(null)
  const [savingDefaultScenario, setSavingDefaultScenario] = useState(false)
  // Race-condition protection (Section F/3): a ref, not state, since the
  // "is my response still the latest?" check inside an async callback
  // needs a synchronous read of the CURRENT value at resolution time -
  // state updates are batched/async and cannot give that guarantee, a
  // ref can. Incremented once per switchScenario() call; a response only
  // commits if its own captured token still matches this ref's current
  // value when the fetch resolves.
  const scenarioRequestTokenRef = React.useRef(0)
  // Phase 4A: which matrix cell is currently drilled into (Section 10) -
  // null when no drill-down panel is open. Holds the cell's row/column
  // object plus its real backing items (relationships for DIRECT, paths
  // for PATH) - never re-queried, reused directly from the already-built
  // matrix.
  const [matrixDrilldown, setMatrixDrilldown] = useState<any>(null)
  // Live bug report fix: the Matrix view showed a fixed 30-row/20-column
  // slice with a "Showing X of Y" label but no way to actually page
  // through the rest - the exact reported "limited number of objects
  // or doesn't have paging" bug. Independent row/column page state
  // (matrix rows and columns are two different axes, each pageable on
  // its own).
  const [matrixRowPage, setMatrixRowPage] = useState(1)
  // P5 matrix reading aids and canvas layout choice.
  const [matrixOrder, setMatrixOrder] = useState<MatrixOrder>('LINKED')
  const [matrixHideEmptyCols, setMatrixHideEmptyCols] = useState(false)
  const [canvasLayout, setCanvasLayout] = useState<'QUESTION' | 'DOMAIN'>('QUESTION')
  const [matrixColPage, setMatrixColPage] = useState(1)
  // ── Comparison mode (Phase 5B) ────────────────────────────────────────
  //
  // Entirely separate from the single-scenario viewer state above -
  // comparisonMode toggles a distinct rendering path, never mutating
  // dataset/eligibility/committedScenarioId. Race protection uses the
  // exact same ref-token pattern as switchScenario (Section F/3 -
  // "highest-risk part") - a comparison request's response only commits
  // if its captured token still matches the latest issued one.
  const [comparisonMode, setComparisonMode] = useState(false)
  const [comparisonLeftId, setComparisonLeftId] = useState<string | null>(null)
  const [comparisonRightId, setComparisonRightId] = useState<string | null>(null)
  const [comparisonData, setComparisonData] = useState<any>(null)
  const [comparisonLoading, setComparisonLoading] = useState(false)
  const [comparisonError, setComparisonError] = useState<string | null>(null)
  const [comparisonView, setComparisonView] = useState<'TABLE' | 'MATRIX' | 'GRAPH' | 'CAPABILITY_MAP' | 'HEATMAP' | 'TREE' | 'CARDS'>('TABLE')
  // Section 21: default emphasizes changes - UNCHANGED excluded from the
  // initial filter set (unlike the empty-set-means-"show all" convention
  // used elsewhere in this component for object/relationship-type
  // filters), since a comparison view's whole purpose is surfacing what
  // changed. The user can still check UNCHANGED explicitly.
  const [comparisonChangeFilter, setComparisonChangeFilter] = useState<Set<string>>(new Set(['ADDED', 'REMOVED', 'MODIFIED']))
  const comparisonRequestTokenRef = React.useRef(0)
  const urlComparisonKeyRef = React.useRef<string | null>(null)
  const [comparisonGraphVisible, setComparisonGraphVisible] = useState<any>(null)
  const [comparisonGraphFocusId, setComparisonGraphFocusId] = useState<string | null>(null)
  const [comparisonHeatmapMetric, setComparisonHeatmapMetric] = useState<string>('')
  // ── Scenario Authoring (Phase 5C) ────────────────────────────────────
  //
  // Entirely separate from comparisonMode above - never active at the
  // same time (the toggle buttons are mutually exclusive in the UI).
  // authoringConfirmedCurrent is the single gate for "preventing
  // accidental editing of Current architecture" (Section: Current-edit
  // safeguard) - reset whenever authoring mode is entered/exited or the
  // active scenario changes, so confirming once doesn't silently carry
  // over to a later Current-editing session.
  const [authoringMode, setAuthoringMode] = useState(false)
  const [authoringConfirmedCurrent, setAuthoringConfirmedCurrent] = useState(false)
  const [authoringBusy, setAuthoringBusy] = useState(false)
  const [authoringError, setAuthoringError] = useState<string | null>(null)
  const [authoringWarnings, setAuthoringWarnings] = useState<any[]>([])
  const [removedAssets, setRemovedAssets] = useState<any[]>([])
  const [editingPropertiesFor, setEditingPropertiesFor] = useState<any>(null)
  const [propertyFormValues, setPropertyFormValues] = useState<Record<string, string>>({})
  const [showIntroducePicker, setShowIntroducePicker] = useState(false)
  const [introduceSearch, setIntroduceSearch] = useState('')
  const [introduceResults, setIntroduceResults] = useState<any[]>([])
  const [showAddRelForm, setShowAddRelForm] = useState(false)
  const [addRelForm, setAddRelForm] = useState<{ sourceId: string; targetId: string; relationshipType: string }>({ sourceId: '', targetId: '', relationshipType: '' })
  const [showNewScenarioForm, setShowNewScenarioForm] = useState(false)
  const [newScenarioForm, setNewScenarioForm] = useState<{ name: string; type: 'TRANSITION' | 'TARGET' | 'BASELINE'; parentScenarioId: string; horizonDate: string }>({ name: '', type: 'TARGET', parentScenarioId: '', horizonDate: '' })
  // ── AI Assist (Phase 5D) ──────────────────────────────────────────────
  //
  // Entirely separate from comparisonMode/authoringMode above - never
  // active at the same time (mutually exclusive toggle buttons). Race
  // safety (requirement 9) uses the identical ref-token pattern already
  // proven for switchScenario/runComparison - a response only commits if
  // its captured token still matches the latest issued one, and switching
  // the active scenario/view invalidates any in-flight or stale AI
  // result outright (never leaves a stale explanation's evidenceRefs
  // pointing at a dataset that's since changed - requirement 2).
  const [aiAssistMode, setAiAssistMode] = useState(false)
  const [aiTab, setAiTab] = useState<'explain' | 'risks' | 'gaps' | 'question' | 'propose'>('explain')
  const [aiAssistQuestion, setAiAssistQuestion] = useState('')
  const [aiExplanation, setAiExplanation] = useState<any>(null)
  const [aiAssistLoading, setAiAssistLoading] = useState(false)
  const [aiAssistError, setAiAssistError] = useState<string | null>(null)
  const aiRequestTokenRef = React.useRef(0)
  const [aiProposalInstruction, setAiProposalInstruction] = useState('')
  const [aiCurrentProposal, setAiCurrentProposal] = useState<any>(null)
  const [aiProposalBusy, setAiProposalBusy] = useState(false)
  const [aiProposalError, setAiProposalError] = useState<string | null>(null)
  const [aiProposalNotice, setAiProposalNotice] = useState<string | null>(null)
  const [aiComparisonExplanation, setAiComparisonExplanation] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [vizMode, setVizMode] = useState<string>(view.visualization || 'GRAPH')
  const vizModeRef = React.useRef(vizMode)
  vizModeRef.current = vizMode
  const [selected, setSelected] = useState<any>(null)
  const [showSharePanel, setShowSharePanel] = useState(false)
  const [shareData, setShareData] = useState<any>(null)
  const [heatmapField, setHeatmapField] = useState('status')
  const [collapsedTreeNodes, setCollapsedTreeNodes] = useState<Set<string>>(new Set())
  // Phase 4B: which related-object type Capability Map's overlay counts
  // (Section 2's "Applications supporting capability: N" example) - a
  // single selectable overlay at a time, matching "prefer one selected
  // metric/overlay at a time" rather than a full analytics designer.
  const [capabilityOverlayType, setCapabilityOverlayType] = useState('Application')
  const [capabilityDrilldown, setCapabilityDrilldown] = useState<any>(null)

  // Roadmap-specific state. Always declared (Rules of Hooks), only
  // exercised when view.visualization === 'ROADMAP' - see the isRoadmap
  // branch near the bottom of this component's render.
  const isRoadmap = view.visualization === 'ROADMAP'
  const [roadmapItems, setRoadmapItems] = useState<any[]>([])
  const [roadmapLoading, setRoadmapLoading] = useState(true)
  const [roadmapNeedsConfig, setRoadmapNeedsConfig] = useState(false)

  const loadRoadmap = useCallback(() => {
    if (!isRoadmap) return
    setRoadmapLoading(true)
    setRoadmapNeedsConfig(false)
    api.get(`/ea-views/${view.id}/roadmap`)
      .then((d: any) => {
        if (d?.error || d?.statusCode === 400) { setRoadmapNeedsConfig(true); setRoadmapLoading(false); return }
        setRoadmapItems(d?.items || [])
        setRoadmapLoading(false)
      })
      .catch(() => { setRoadmapNeedsConfig(true); setRoadmapLoading(false) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.id, isRoadmap])

  useEffect(() => { if (isRoadmap) loadRoadmap() }, [isRoadmap, loadRoadmap])

  const saveRoadmapConfig = async (cfg: { startField: string; endField: string; groupByField?: string }) => {
    await api.put(`/ea-views/${view.id}`, { roadmapConfig: cfg })
    loadRoadmap()
  }

  // Dashboard-specific state. Same always-declared-unconditionally
  // approach as the roadmap state above (Rules of Hooks).
  const isDashboard = view.visualization === 'DASHBOARD'
  const [dashboardWidgets, setDashboardWidgets] = useState<DashboardWidget[]>([])
  const [dashboardResults, setDashboardResults] = useState<Record<string, any>>({})
  const [dashboardLoading, setDashboardLoading] = useState(true)
  const [editingDashboard, setEditingDashboard] = useState(false)

  const loadDashboard = useCallback(() => {
    if (!isDashboard) return
    setDashboardLoading(true)
    api.get(`/ea-views/${view.id}/dashboard`)
      .then((d: any) => {
        setDashboardWidgets(d?.widgets || [])
        setDashboardResults(d?.results || {})
        setDashboardLoading(false)
      })
      .catch(() => { setDashboardWidgets([]); setDashboardResults({}); setDashboardLoading(false) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.id, isDashboard])

  useEffect(() => { if (isDashboard) loadDashboard() }, [isDashboard, loadDashboard])

  const saveDashboardWidgets = async (widgets: DashboardWidget[]) => {
    await api.put(`/ea-views/${view.id}`, { dashboardConfig: { widgets } })
    setEditingDashboard(false)
    loadDashboard()
  }

  // Graph canvas state
  const [positions, setPositions] = useState<Record<string,{x:number,y:number}>>({})
  const [dragging, setDragging] = useState<{id:string,ox:number,oy:number}|null>(null)
  const [pan, setPan] = useState({x:0,y:0})
  const [panStart, setPanStart] = useState<{mx:number,my:number,px:number,py:number}|null>(null)
  const [zoom, setZoom] = useState(1)
  const [graphSearch, setGraphSearch] = useState('')
  // Phase 4C: progressive disclosure state - visibleObjectIds/
  // visibleRelationshipIds/revealedBy from graphDisclosureUtils, built
  // purely client-side over the already-fetched dataset. null means "not
  // yet initialized for this dataset" (see the useEffect below), not
  // "empty" - an empty Set is a valid, real state.
  const [graphVisibleState, setGraphVisibleState] = useState<any>(null)
  const [graphFocusId, setGraphFocusId] = useState<string | null>(null)
  const [graphRelTypeFilter, setGraphRelTypeFilter] = useState<Set<string>>(new Set())
  const [graphObjectTypeFilter, setGraphObjectTypeFilter] = useState<Set<string>>(new Set())
  const [graphHighlightedPathId, setGraphHighlightedPathId] = useState<string | null>(null)
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set())
  const [layoutRunning, setLayoutRunning] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const graphContainerRef = React.useRef<HTMLDivElement>(null)
  const graphSvgRef = React.useRef<SVGSVGElement>(null)
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [showVersionHistory, setShowVersionHistory] = useState(false)
  const [versions, setVersions] = useState<any[]>([])
  const [versionsLoading, setVersionsLoading] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    setScenarioSwitchError(null)
    setVizAutoSwitchNotice(null)
    // Section 3/16: one list call for the selector, alongside the one
    // /dataset call for the initial scenario-resolved result - never a
    // per-scenario or per-renderer fetch. A fresh view load always
    // starts a new "latest wins" token generation too, so any switch
    // request left over from a previous view can never commit here.
    const myToken = ++scenarioRequestTokenRef.current
    api.get('/ea-views/scenarios').then((s: any) => { if (myToken === scenarioRequestTokenRef.current) setScenarios(Array.isArray(s) ? s : []) })
    // Phase 4A: single fetch to the new /dataset endpoint, not a second
    // call alongside /execute - `d.legacy` is byte-compatible with what
    // /execute always returned (verified by a dedicated backend test),
    // so every existing renderer below reading `data` is completely
    // unaffected. `d.dataset`/`d.eligibility` are new, additive state
    // only Table/Matrix read. Phase 5A: ?scenario=<id> from the URL, if
    // present, is sent as the initial scenarioId override - api.post
    // never rejects on an HTTP error status (the backend's own 404 for
    // an invalid/cross-tenant id resolves normally), so failure is
    // detected the same way switchScenario does (!d?.dataset), then
    // safely retried without the override rather than leaving the
    // viewer stuck on an error for what's still a perfectly valid View.
    const urlScenarioId = searchParams.get('scenario')
    const fetchDataset = (scenarioOverride?: string) => api.post(`/ea-views/${view.id}/dataset`, scenarioOverride ? { scenarioId: scenarioOverride } : {})
    const applyDatasetResult = (d: any) => {
      if (myToken !== scenarioRequestTokenRef.current) return // a newer load()/switchScenario() has already superseded this
      setData(d?.legacy ?? d)
      setDataset(d?.dataset ?? null)
      setEligibility(d?.eligibility ?? null)
      setContract(d?.contract ?? null)
      setPresentation(d?.presentation ?? null)
      setInsights(Array.isArray(d?.insights) ? d.insights : [])
      setSemanticReview(d?.semanticReview ?? null)
      setFocusInsight(null)
      if (d?.presentation?.primary && !(d.presentation.available || []).includes(vizModeRef.current)) setVizMode(d.presentation.primary)
      setCommittedScenarioId(d?.dataset?.context?.scenario?.id ?? null)
      // Phase 4B: initialize the selected heatmap metric from
      // VisualizationEligibility's own recommendation rather than a fixed
      // 'status' default - 'status' isn't guaranteed to be a metric this
      // dataset actually offers, and the eligibility engine already
      // picked the best candidate.
      const heatmapRecommendedMetric = d?.eligibility?.eligible?.find((v: any) => v.visualization === 'HEATMAP')?.recommendedConfig?.metricKey
      if (heatmapRecommendedMetric) setHeatmapField(heatmapRecommendedMetric)
      const legacyNodes = d?.legacy?.nodes
      if (legacyNodes) {
        // Canvas reads left to right as the view's question does: primary
        // objects, then each related group / path hop (see canvasLayout).
        setPositions(d?.dataset?.objects?.length ? layoutByQuestion(legacyNodes, d.dataset.objects, d.dataset.paths || []) : layoutByDomain(legacyNodes))
        setCanvasLayout('QUESTION')
      }
      setLoading(false)
    }
    fetchDataset(urlScenarioId || undefined).then((d: any) => {
      if (urlScenarioId && !d?.dataset) { fetchDataset().then(applyDatasetResult); return } // invalid/cross-tenant URL scenario - fall back safely to the view's own default rather than getting stuck
      applyDatasetResult(d)
    })
  }, [view.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load() }, [load])

  // ── Scenario switching (Phase 5A) ────────────────────────────────────
  //
  // Section 3/4's core requirements: latest-request-wins race safety, and
  // a strictly transactional commit - the scenario label and the
  // displayed dataset are only ever updated TOGETHER, in the same commit,
  // never independently. A failed or superseded request never touches
  // committedScenarioId/data/dataset/eligibility at all, so a still-valid
  // prior scenario stays fully displayed and correctly labeled.
  const switchScenario = (scenarioId: string) => {
    const myToken = ++scenarioRequestTokenRef.current
    setPendingScenarioId(scenarioId)
    setScenarioSwitchError(null)
    setVizAutoSwitchNotice(null)
    api.post(`/ea-views/${view.id}/dataset`, { scenarioId }).then((d: any) => {
      if (myToken !== scenarioRequestTokenRef.current) return // superseded by a newer switch - never commit a stale response, even a successful one
      if (!d?.dataset) { setScenarioSwitchError('Failed to switch scenario. Showing the previous scenario.'); setPendingScenarioId(null); return }
      // Single, atomic commit - every piece of "what's currently shown"
      // updates together, in one React batch, so there is never a render
      // where the label and the data disagree (Section 4).
      setData(d.legacy ?? d)
      setDataset(d.dataset)
      setEligibility(d.eligibility ?? null)
      setContract(d.contract ?? null)
      setPresentation(d.presentation ?? null)
      setInsights(Array.isArray(d.insights) ? d.insights : [])
      setSemanticReview(d.semanticReview ?? null)
      setFocusInsight(null)
      setCommittedScenarioId(d.dataset?.context?.scenario?.id ?? scenarioId)
      setPendingScenarioId(null)
      // Phase 5A Section 10: URL reflects the committed scenario only -
      // never the requested-but-not-yet-resolved one, keeping the same
      // label/data-never-disagree guarantee extended to the URL too.
      setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('scenario', d.dataset?.context?.scenario?.id ?? scenarioId); return next })
      // Section 7: eligibility fallback with an unobtrusive, specific
      // explanation - never a silent renderer swap, never leaving the
      // user on an invalid one either.
      const { vizMode: nextVizMode, changed } = chooseVisualizationAfterScenarioSwitch(d.eligibility, vizMode)
      if (changed) {
        const scenarioName = scenarios.find(s => s.id === (d.dataset?.context?.scenario?.id ?? scenarioId))?.name || 'this scenario'
        setVizAutoSwitchNotice(nextVizMode
          ? `${VIZ_ICONS[vizMode] ? vizMode.replace(/_/g, ' ') : vizMode} is unavailable for ${scenarioName}. Switched to ${nextVizMode.replace(/_/g, ' ')}.`
          : `No visualization is available for ${scenarioName}.`)
        if (nextVizMode) setVizMode(nextVizMode)
      }
      // Section 6/H: reset stale, scenario-specific selections rather than
      // carrying old object IDs into a new scenario's data. Matrix drill-
      // down and heatmap metric are handled here since they reference
      // specific dataset content directly; Graph's own focus/highlight
      // reset already happens via its existing dataset-driven useEffect
      // (chooseFocusObject already prefers retaining the prior focus only
      // when it still exists in the new dataset - see that effect).
      const newObjectIds = new Set((d.dataset?.objects ?? []).map((o: any) => o.id))
      if (selected && !newObjectIds.has(selected.id)) setSelected(null)
      if (matrixDrilldown && (!newObjectIds.has(matrixDrilldown.rowObj?.id) || !newObjectIds.has(matrixDrilldown.colObj?.id))) setMatrixDrilldown(null)
      const newMetricKeys = new Set((d.dataset?.metrics ?? []).map((m: any) => m.key))
      if (!newMetricKeys.has(heatmapField)) {
        const recommended = d.eligibility?.eligible?.find((v: any) => v.visualization === 'HEATMAP')?.recommendedConfig?.metricKey
        if (recommended) setHeatmapField(recommended)
      }
    }).catch(() => {
      if (myToken !== scenarioRequestTokenRef.current) return
      setScenarioSwitchError('Failed to switch scenario. Showing the previous scenario.')
      setPendingScenarioId(null)
    })
  }

  // Section 9/D: an explicit user action only - never invoked by
  // switchScenario itself. Reuses the existing view-update endpoint/
  // permission (scenarioId is already an updatable field there, with its
  // own tenant-scoped validation) rather than a new one. Uses
  // viewOverrides (the established optimistic-update pattern in this
  // component) so the "saved" indicator updates immediately on success,
  // without a full view reload; a failure leaves viewOverrides untouched,
  // so the UI never falsely claims the default changed.
  const setAsDefaultScenario = async (scenarioId: string) => {
    setSavingDefaultScenario(true)
    try {
      const result = await api.put(`/ea-views/${view.id}`, { scenarioId })
      if (result?.id) setViewOverrides((prev: any) => ({ ...prev, scenarioId }))
    } finally {
      setSavingDefaultScenario(false)
    }
  }

  // ── Comparison request (Phase 5B) ────────────────────────────────────
  //
  // One /compare request per pair - never per-renderer (Section 25/5).
  // Race protection mirrors switchScenario exactly: a token captured at
  // request time only commits if it's still the latest issued when the
  // response arrives, so rapidly changing the left/right pair can never
  // let an earlier, slower request overwrite a later, faster one.
  useEffect(() => {
    let cancelled = false
    Promise.resolve(api.get(`/ea-views/${view.id}/states`)).then((d: any) => {
      if (!cancelled) setStateLines(Array.isArray(d?.lines) ? d.lines : null)
    }).catch(() => { if (!cancelled) setStateLines(null) })
    return () => { cancelled = true }
  }, [api, view.id, view.scenarioId])

  useEffect(() => {
    setStateChange(null)
    if (!stateLines || !committedScenarioId) return
    const currentId = stateLines[0]?.states[0]?.scenarioId
    if (!currentId || currentId === committedScenarioId) return
    if (!comparableWith(stateLines, currentId).some(x => x.scenarioId === committedScenarioId)) return
    let cancelled = false
    Promise.resolve(api.get(`/ea-views/${view.id}/evolution?from=${encodeURIComponent(currentId)}&to=${encodeURIComponent(committedScenarioId)}`))
      .then((d: any) => { if (!cancelled && d?.summary) setStateChange(d.summary) }).catch(() => {})
    return () => { cancelled = true }
  }, [api, view.id, stateLines, committedScenarioId])

  const runComparison = (leftId: string, rightId: string) => {
    const myToken = ++comparisonRequestTokenRef.current
    setEvolution(null)
    // Forward along a line of change: also show what changes, in
    // architecture terms (introduced / retired / restored / changed).
    if (stateLines && comparableWith(stateLines, leftId).some(x => x.scenarioId === rightId)) {
      Promise.resolve(api.get(`/ea-views/${view.id}/evolution?from=${encodeURIComponent(leftId)}&to=${encodeURIComponent(rightId)}`)).then((d: any) => {
        if (myToken === comparisonRequestTokenRef.current && d?.summary) setEvolution(d)
      }).catch(() => {})
    }
    setComparisonLoading(true)
    setComparisonError(null)
    api.post(`/ea-views/${view.id}/compare`, { leftScenarioId: leftId, rightScenarioId: rightId }).then((d: any) => {
      if (myToken !== comparisonRequestTokenRef.current) return // superseded by a newer comparison request
      if (!d?.objects) { setComparisonError('Failed to load comparison. Showing the previous result.'); return }
      setComparisonData(d)
      setComparisonLeftId(leftId)
      setComparisonRightId(rightId)
    }).catch(() => {
      if (myToken !== comparisonRequestTokenRef.current) return
      setComparisonError('Failed to load comparison. Showing the previous result.')
    }).finally(() => {
      if (myToken === comparisonRequestTokenRef.current) setComparisonLoading(false)
    })
  }

  // ADM decision links preselect a semantically compatible scenario pair
  // for this exact View definition. The existing comparison endpoint remains
  // authoritative; this only opens the established comparison mode directly.
  useEffect(() => {
    const leftId = searchParams.get('compareLeft')
    const rightId = searchParams.get('compareRight')
    if (!leftId || !rightId || scenarios.length === 0) return
    const key = `${view.id}:${leftId}:${rightId}`
    if (urlComparisonKeyRef.current === key) return
    const eligible = scenarios.some(item => item.id === leftId) && scenarios.some(item => item.id === rightId)
    if (!eligible) {
      setComparisonMode(true)
      setComparisonError('This comparison is unavailable because one of the linked scenarios is no longer eligible.')
      urlComparisonKeyRef.current = key
      return
    }
    urlComparisonKeyRef.current = key
    setComparisonMode(true)
    setAuthoringMode(false)
    setAiAssistMode(false)
    runComparison(leftId, rightId)
  }, [scenarios, searchParams, view.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Authoring actions (Phase 5C) ─────────────────────────────────────
  //
  // A single wrapper for every mutating call: enforces the Current-edit
  // confirmation gate (Section: preventing accidental editing) before
  // anything is sent, then on success refreshes via the exact same
  // switchScenario() used for normal scenario switching - reusing its
  // proven race-protection and atomic commit rather than building a
  // second, parallel refresh path (Section: "every authoring action
  // refreshes the resolved scenario/view correctly"). The backend's own
  // presence-check-before-action (already proven in Phase 5C's backend
  // tests) is what actually prevents duplicate/contradictory deltas;
  // this wrapper's job is only to surface the result, never to
  // second-guess it.
  const activeScenarioObj = scenarios.find((s: any) => s.id === committedScenarioId)
  // Edit/States split: editing the shown state is separate from viewing it
  // and appears only for people who author change (editing Current keeps
  // its explicit confirmation step).
  const canAuthorState = can('Scenario.Author')
  const canSnapshot = can('Views.CreateSnapshot')
  // A viewpoint opened directly is a private workspace until kept.
  const isWorkspace = !!(view as any).isWorkspace
  const keepView = async () => {
    const name = window.prompt(t('eaviews.keep_prompt'), view.name)
    if (name === null) return
    const kept = await api.post(`/ea-views/${view.id}/keep`, { name })
    if (kept?.id) setViewOverrides(prev => ({ ...prev, ...kept }))
  }

  const runAuthoringAction = async (action: () => Promise<any>) => {
    if (requiresCurrentEditConfirmation(activeScenarioObj) && !authoringConfirmedCurrent) {
      setAuthoringError('Confirm editing Current architecture first (see the warning banner above).')
      return
    }
    setAuthoringBusy(true)
    setAuthoringError(null)
    try {
      const result = await action()
      if (result?.statusCode) {
        // NestJS's standard error response shape - api.post/patch/del
        // never reject on an HTTP error status, so this is how a
        // validation/lifecycle/tenant-ownership failure actually surfaces.
        setAuthoringError(Array.isArray(result?.issues) ? result.issues.map((i: any) => i.message).join('; ') : (result?.message || 'The action failed.'))
        return
      }
      setAuthoringWarnings(result?.warnings ?? [])
      if (committedScenarioId) switchScenario(committedScenarioId)
    } catch {
      setAuthoringError('The action failed. Nothing was changed.')
    } finally {
      setAuthoringBusy(false)
    }
  }

  const introduceAsset = (assetId: string) => runAuthoringAction(() => api.post(`/ea-views/scenarios/${committedScenarioId}/assets/${assetId}/introduce`))
  const removeAssetFromScenario = (assetId: string) => runAuthoringAction(() => api.post(`/ea-views/scenarios/${committedScenarioId}/assets/${assetId}/remove`))
  const restoreAssetInScenario = (assetId: string) => runAuthoringAction(() => api.post(`/ea-views/scenarios/${committedScenarioId}/assets/${assetId}/restore`))
  const undoAssetDelta = (assetId: string) => runAuthoringAction(() => api.del(`/ea-views/scenarios/${committedScenarioId}/assets/${assetId}`))
  const savePropertyOverrides = (assetId: string, overrides: Record<string, any>) => runAuthoringAction(() => api.patch(`/ea-views/scenarios/${committedScenarioId}/assets/${assetId}/properties`, { overrides }))
  const addScenarioRelationship = (sourceId: string, targetId: string, relationshipType: string) => runAuthoringAction(() => api.post(`/ea-views/scenarios/${committedScenarioId}/relationships`, { sourceId, targetId, relationshipType }))
  const removeScenarioRelationship = (sourceId: string, targetId: string, relationshipType: string) => runAuthoringAction(() => api.del(`/ea-views/scenarios/${committedScenarioId}/relationships`, { sourceId, targetId, relationshipType }))
  const setScenarioLifecycleStatus = (status: 'DRAFT' | 'APPROVED') => runAuthoringAction(() => api.patch(`/ea-views/scenarios/${committedScenarioId}/status`, { status }))

  const createScenario = async () => {
    setAuthoringBusy(true); setAuthoringError(null)
    try {
      const result = await api.post('/ea-views/scenarios', { ...newScenarioForm, horizonDate: newScenarioForm.horizonDate || undefined })
      if (!result?.id) { setAuthoringError(result?.message || 'Failed to create scenario.'); return }
      setShowNewScenarioForm(false)
      setNewScenarioForm({ name: '', type: 'TARGET', parentScenarioId: '', horizonDate: '' })
      api.get('/ea-views/scenarios').then((s: any) => { if (Array.isArray(s)) setScenarios(s) })
      switchScenario(result.id)
    } catch {
      setAuthoringError('Failed to create scenario.')
    } finally {
      setAuthoringBusy(false)
    }
  }

  const searchRepositoryToIntroduce = (query: string) => {
    setIntroduceSearch(query)
    api.get(`/ea-repository/assets?search=${encodeURIComponent(query)}`).then((r: any) => setIntroduceResults(Array.isArray(r) ? r : [])).catch(() => setIntroduceResults([]))
  }

  // ── AI Assist actions (Phase 5D) ──────────────────────────────────────
  //
  // Every response is checked for NestJS's standard {statusCode,...}
  // error shape (api.post never rejects on an HTTP error status) - the
  // established pattern from every prior authoring/comparison action in
  // this file, reused rather than inventing a second error-detection
  // convention just for AI calls.
  const runAiExplain = (action: 'explain' | 'risks' | 'gaps' | 'question') => {
    const myToken = ++aiRequestTokenRef.current
    setAiTab(action)
    setAiAssistLoading(true)
    setAiAssistError(null)
    api.post(`/ea-views/${view.id}/ai/explain`, { action, question: action === 'question' ? aiAssistQuestion : undefined, ...(committedScenarioId ? { scenarioId: committedScenarioId } : {}) }).then((d: any) => {
      if (myToken !== aiRequestTokenRef.current) return // superseded by a newer AI request or scenario/view switch
      if (d?.statusCode) { setAiAssistError(d.message || 'The AI assistant could not generate a response.'); return }
      setAiExplanation(d)
    }).catch(() => {
      if (myToken !== aiRequestTokenRef.current) return
      setAiAssistError('The AI assistant could not generate a response.')
    }).finally(() => {
      if (myToken === aiRequestTokenRef.current) setAiAssistLoading(false)
    })
  }

  // Requirement 8: uses the View's already-loaded comparisonData's own
  // left/right scenario ids - the exact ComparisonDataset the backend
  // already computed, never a frontend reconstruction of comparison
  // semantics. Only callable once a real comparison has actually been
  // run (comparisonMode's own Compare button), matching Phase 5B's
  // existing data flow rather than triggering a second, independent
  // comparison fetch.
  const runAiExplainComparison = () => {
    if (!comparisonLeftId || !comparisonRightId) return
    const myToken = ++aiRequestTokenRef.current
    setAiAssistLoading(true)
    setAiAssistError(null)
    api.post(`/ea-views/${view.id}/ai/compare-explain`, { leftScenarioId: comparisonLeftId, rightScenarioId: comparisonRightId }).then((d: any) => {
      if (myToken !== aiRequestTokenRef.current) return
      if (d?.statusCode) { setAiAssistError(d.message || 'The AI assistant could not generate a comparison explanation.'); return }
      setAiComparisonExplanation(d)
    }).catch(() => {
      if (myToken !== aiRequestTokenRef.current) return
      setAiAssistError('The AI assistant could not generate a comparison explanation.')
    }).finally(() => {
      if (myToken === aiRequestTokenRef.current) setAiAssistLoading(false)
    })
  }

  // Generates a ScenarioChangeProposal only - never writes a scenario
  // delta itself (requirement 7: AI Assist must never accidentally allow
  // repository-base mutations; only this structured proposal object is
  // ever produced here, and it requires a separate, explicit
  // approve/execute step below to become a real change).
  const runAiPropose = () => {
    if (!committedScenarioId || !aiProposalInstruction.trim()) return
    const myToken = ++aiRequestTokenRef.current
    setAiProposalBusy(true)
    setAiProposalError(null)
    setAiProposalNotice(null)
    api.post(`/ea-views/${view.id}/ai/propose`, { scenarioId: committedScenarioId, instruction: aiProposalInstruction }).then((d: any) => {
      if (myToken !== aiRequestTokenRef.current) return
      if (d?.statusCode) { setAiProposalError(d.message || 'The AI assistant could not generate a valid proposal.'); return }
      setAiCurrentProposal(d)
    }).catch(() => {
      if (myToken !== aiRequestTokenRef.current) return
      setAiProposalError('The AI assistant could not generate a valid proposal.')
    }).finally(() => {
      if (myToken === aiRequestTokenRef.current) setAiProposalBusy(false)
    })
  }

  const approveAiProposal = () => {
    if (!aiCurrentProposal) return
    setAiProposalBusy(true)
    setAiProposalError(null)
    api.post(`/ea-views/scenarios/${aiCurrentProposal.scenarioId}/proposals/${aiCurrentProposal.id}/approve`).then((d: any) => {
      if (d?.statusCode) { setAiProposalError(d.message || 'Failed to approve the proposal.'); return }
      setAiCurrentProposal(d)
    }).catch(() => setAiProposalError('Failed to approve the proposal.'))
      .finally(() => setAiProposalBusy(false))
  }

  const rejectAiProposal = () => {
    if (!aiCurrentProposal) return
    setAiProposalBusy(true)
    setAiProposalError(null)
    api.post(`/ea-views/scenarios/${aiCurrentProposal.scenarioId}/proposals/${aiCurrentProposal.id}/reject`, {}).then((d: any) => {
      if (d?.statusCode) { setAiProposalError(d.message || 'Failed to reject the proposal.'); return }
      setAiCurrentProposal(d)
    }).catch(() => setAiProposalError('Failed to reject the proposal.'))
      .finally(() => setAiProposalBusy(false))
  }

  // Requirement: "refresh of the active scenario/view using the existing
  // proven refresh mechanisms rather than creating another refresh path" -
  // on success, calls switchScenario() exactly the way Phase 5A/5C's own
  // authoring actions already do, reusing its proven race-protection and
  // atomic commit rather than a third, parallel refresh implementation.
  // On failure (including a stale-fingerprint rejection - requirement 4),
  // the proposal's own status may have already changed on the backend
  // (executeProposal's own catch path writes REJECTED before re-throwing) -
  // re-fetched here so the displayed status never lags behind what the
  // backend actually recorded.
  const executeAiProposal = () => {
    if (!aiCurrentProposal) return
    setAiProposalBusy(true)
    setAiProposalError(null)
    setAiProposalNotice(null)
    const proposalId = aiCurrentProposal.id
    const scenarioId = aiCurrentProposal.scenarioId
    api.post(`/ea-views/scenarios/${scenarioId}/proposals/${proposalId}/execute`).then((d: any) => {
      if (d?.statusCode) {
        setAiProposalError(d.message || 'Failed to execute the proposal.')
        api.get(`/ea-views/scenarios/${scenarioId}/proposals/${proposalId}`).then((refreshed: any) => { if (!refreshed?.statusCode) setAiCurrentProposal(refreshed) })
        return
      }
      setAiCurrentProposal(d)
      setAiProposalNotice('The proposal was applied successfully. Refreshing the scenario…')
      if (committedScenarioId) switchScenario(committedScenarioId)
    }).catch(() => setAiProposalError('Failed to execute the proposal.'))
      .finally(() => setAiProposalBusy(false))
  }

  // Requirement 2: resolves an evidenceRef only against the actually-
  // loaded dataset, never fetching or guessing - reuses the existing
  // detail-panel state (`selected`) rather than building a second
  // highlighting mechanism. A ref that fails to resolve (e.g. a
  // COMPARISON_CLASSIFICATION ref this single-dataset resolver doesn't
  // cover) simply does nothing - it never falls back to selecting an
  // unrelated item.
  const highlightEvidence = (ref: any) => {
    const resolved = resolveEvidenceRef(ref, dataset)
    if (resolved) setSelected(resolved)
  }

  // Comparison Graph: initialize progressive disclosure fresh for each
  // newly-loaded comparison (Section 13/14) - reuses the exact same
  // Phase 4C functions the single-scenario Graph uses, over the dataset
  // shape buildComparisonGraphDataset produces.
  useEffect(() => {
    if (!comparisonData) { setComparisonGraphVisible(null); setComparisonGraphFocusId(null); return }
    const cgd = buildComparisonGraphDataset(comparisonData)
    const indexes = buildGraphIndexes(cgd)
    const focusId = chooseFocusObject(cgd, null)
    setComparisonGraphFocusId(focusId)
    setComparisonGraphVisible(focusId ? computeInitialVisibleSet(indexes, focusId) : null)
  }, [comparisonData])

  // Resets the Current-edit confirmation whenever authoring mode is
  // entered/exited or the active scenario changes - confirming once must
  // never silently carry over to a later Current-editing session
  // (Section: preventing accidental editing of Current architecture).
  useEffect(() => { setAuthoringConfirmedCurrent(false); setAuthoringError(null); setAuthoringWarnings([]) }, [authoringMode, committedScenarioId])

  // Invalidates any AI result the moment the active scenario changes -
  // an explanation's evidenceRefs are only ever resolved against the
  // dataset that was loaded when it was generated (requirement 2:
  // "unresolved evidence must not silently highlight an unrelated
  // item"). A stale explanation left visible against a since-switched
  // scenario would risk exactly that, so it's cleared outright rather
  // than kept and re-resolved against different data.
  useEffect(() => { setAiExplanation(null); setAiComparisonExplanation(null); setAiCurrentProposal(null); setAiAssistError(null); setAiProposalError(null); setAiProposalNotice(null) }, [committedScenarioId, view.id])

  // Fetches the current scenario's removed-asset list whenever authoring
  // mode is on and the resolved dataset changes - a removed asset never
  // appears in `dataset.objects` at all (Section: Restore needs
  // something to restore), so this is the only source for the Restore
  // picker.
  useEffect(() => {
    if (!authoringMode || !committedScenarioId) { setRemovedAssets([]); return }
    api.get(`/ea-views/scenarios/${committedScenarioId}/removed-assets`).then((r: any) => setRemovedAssets(Array.isArray(r) ? r : [])).catch(() => setRemovedAssets([]))
  }, [authoringMode, committedScenarioId, dataset]) // eslint-disable-line react-hooks/exhaustive-deps

  const publish = async () => {
    const updated = await api.post(`/ea-views/${view.id}/publish`)
    setViewOverrides(prev => ({ ...prev, ...updated }))
    onRefresh()
  }

  // ── Version History ───────────────────────────────────────────────────
  const loadVersions = async () => {
    setVersionsLoading(true)
    const v = await api.get(`/ea-views/${view.id}/versions`)
    setVersions(Array.isArray(v) ? v : [])
    setVersionsLoading(false)
  }
  const openVersionHistory = () => { setShowVersionHistory(true); loadVersions() }
  const restoreVersion = async (versionId: string) => {
    if (!window.confirm('Restore this version? Your current configuration will be saved as a new version first, so nothing is lost.')) return
    const updated = await api.post(`/ea-views/${view.id}/versions/${versionId}/restore`)
    setViewOverrides(prev => ({ ...prev, ...updated }))
    setShowVersionHistory(false)
    load()
  }

  // ── Approval Workflow ─────────────────────────────────────────────────
  const requestApproval = async () => {
    const updated = await api.post(`/ea-views/${view.id}/request-approval`)
    setViewOverrides(prev => ({ ...prev, ...updated }))
  }
  const approveView = async () => {
    const notes = window.prompt('Approval notes (optional):') || undefined
    const updated = await api.post(`/ea-views/${view.id}/approve`, { notes })
    setViewOverrides(prev => ({ ...prev, ...updated }))
    onRefresh()
  }
  const rejectView = async () => {
    const notes = window.prompt('Reason for rejection:')
    if (notes === null) return // cancelled
    const updated = await api.post(`/ea-views/${view.id}/reject`, { notes })
    setViewOverrides(prev => ({ ...prev, ...updated }))
  }

  const takeSnapshot = async () => {
    const name = window.prompt('Snapshot name:', `${view.name} - ${new Date().toLocaleDateString()}`)
    if (!name) return
    // Captures the architecture state being viewed, not the view's default.
    await api.post(`/ea-views/${view.id}/snapshots`, committedScenarioId ? { name, scenarioId: committedScenarioId } : { name })
    alert('Snapshot saved!')
  }

  const shareView = async () => {
    const result = await api.post(`/ea-views/${view.id}/share`, { expiryDays: 30 })
    setShareData(result)
    setShowSharePanel(true)
  }

  // Filter nodes
  // Live bug report fix: previously filtered/derived options from the
  // raw n.domain value (the same inconsistent EaAsset.domain column
  // already fixed in the Repository module - duplicate-looking options
  // for the same real domain, and NORA-native GOVERNANCE/MOTIVATION
  // shown as if they were real ArchMind operating domains). Now uses
  // the resolved n.operatingDomain (added server-side in
  // view-query.service.ts's toViewNodes, the same mechanism that
  // already resolves semanticType) - falls back to the raw domain only
  // for a node whose type has no meta-model match at all, so nothing
  // becomes unfilterable.
  // A selected fact keeps its objects and their direct neighbours, so the
  // relationships behind the fact stay visible.
  const focusIds = useMemo(() => {
    if (!focusInsight?.objectIds?.length) return null
    const ids = new Set<string>(focusInsight.objectIds)
    for (const e of (data?.edges || [])) {
      if (focusInsight.objectIds.includes(e.sourceId)) ids.add(e.targetId)
      if (focusInsight.objectIds.includes(e.targetId)) ids.add(e.sourceId)
    }
    return ids
  }, [focusInsight, data])
  // A selected fact narrows the view to its objects; otherwise everything.
  const filteredNodes = (data?.nodes || []).filter((n: any) => !focusIds || focusIds.has(n.id))

  // ── Graph progressive disclosure (Phase 4C) ──────────────────────────
  //
  // Indexes built once per dataset (Section 27's explicit indexing
  // guidance), reused across every expand/collapse/filter interaction -
  // no re-scanning dataset.relationships/paths on every click.
  const graphIndexes = useMemo(() => buildGraphIndexes(dataset), [dataset])

  // Initializes (or re-initializes, on a genuinely new dataset) the
  // focused, bounded initial view - never the full dataset by default
  // (Section 2). Re-runs when the dataset itself changes (a fresh view
  // load, or a scenario switch), not on every render. Phase 5A: prefers
  // retaining the PRIOR graph focus (via a ref, since graphFocusId isn't
  // a dependency here and a closure would otherwise capture a stale
  // value) over the generic `selected` object - Section 6's "if focused
  // Capability A exists in both scenarios, focus may remain" refers
  // specifically to the graph's own focus concept, not whatever object
  // happens to be selected for the detail panel at the moment.
  const priorGraphFocusIdRef = React.useRef<string | null>(null)
  useEffect(() => { priorGraphFocusIdRef.current = graphFocusId }, [graphFocusId])
  useEffect(() => {
    if (!dataset) { setGraphVisibleState(null); setGraphFocusId(null); return }
    const focusId = chooseFocusObject(dataset, priorGraphFocusIdRef.current ?? selected?.id)
    setGraphFocusId(focusId)
    setGraphVisibleState(focusId ? computeInitialVisibleSet(graphIndexes, focusId) : null)
    setGraphHighlightedPathId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset])

  // Phase 4B: heatmap-fields backend endpoint removed - ViewDataset.metrics
  // (already present in the single /dataset fetch) provides the same
  // candidate-metric information, data-driven from actual returned
  // values, with zero extra network calls (Section 12's explicit "no new
  // network calls" requirement).

  // Phase 4B: color-strategy computation is now handled by
  // buildHeatmapDisplay (ViewDataset.metrics already carries dataType/min/
  // max/distinctValues) - only the color mapping itself stays here.
  const HEATMAP_CATEGORICAL_PALETTE = ['#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22', '#34495e', '#16a085', '#c0392b']
  function numericGradientColor(value: number, min: number, max: number): string {
    // Green (low) -> amber -> red (high), a common risk/intensity ramp.
    const t = max === min ? 0.5 : (value - min) / (max - min)
    if (t < 0.5) {
      const s = t / 0.5
      return `rgb(${Math.round(46 + s * (243 - 46))}, ${Math.round(204 + s * (156 - 204))}, ${Math.round(113 + s * (18 - 113))})`
    }
    const s = (t - 0.5) / 0.5
    return `rgb(${Math.round(243 + s * (231 - 243))}, ${Math.round(156 + s * (76 - 156))}, ${Math.round(18 + s * (60 - 18))})`
  }

  // Graph handlers
  const onNodeMouseDown = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    const p = positions[id]||{x:100,y:100}
    setDragging({id, ox:e.clientX-p.x, oy:e.clientY-p.y})
    setSelected(data?.nodes?.find((n: any) => n.id===id)||null)
  }
  const onSvgMouseDown = (e: React.MouseEvent) => {
    if ((e.target as Element).closest('[data-node]')) return
    setPanStart({mx:e.clientX,my:e.clientY,px:pan.x,py:pan.y})
    setSelected(null)
  }
  const onMouseMove = (e: React.MouseEvent) => {
    if (dragging) setPositions(p=>({...p,[dragging.id]:{x:(e.clientX-dragging.ox)/zoom,y:(e.clientY-dragging.oy)/zoom}}))
    else if (panStart) setPan({x:panStart.px+e.clientX-panStart.mx,y:panStart.py+e.clientY-panStart.my})
  }
  const onMouseUp = () => { setDragging(null); setPanStart(null) }
  const onWheel = (e: React.WheelEvent) => { e.preventDefault(); setZoom(z=>Math.max(0.25,Math.min(2.5,z-e.deltaY*0.001))) }

  const getEdgePath = (e: any) => {
    const s=positions[e.sourceId]||{x:0,y:0}; const t=positions[e.targetId]||{x:0,y:0}
    return `M ${s.x+80} ${s.y+20} Q ${(s.x+80+t.x)/2} ${(s.y+20+t.y+20)/2} ${t.x} ${t.y+20}`
  }

  // Phase 4C: purely client-side over the already-fetched dataset - no
  // network call (replaces the old /object-context fetch entirely,
  // Section 27's explicit "no per-expansion API request" requirement).
  const expandNode = (nodeId: string, direction: ExpandDirection = 'both') => {
    if (!graphVisibleState) return
    const newState = expandNeighbors(graphIndexes, graphVisibleState, nodeId, direction)
    const newlyRevealedIds = [...newState.visibleObjectIds].filter(id => !graphVisibleState.visibleObjectIds.has(id))
    setGraphVisibleState(newState)
    setExpandedNodeIds(prev => new Set(prev).add(nodeId))
    // Place newly-revealed nodes near the expanded node so they don't
    // stack at the shared default origin before the next auto-layout run.
    const origin = positions[nodeId] || { x: 400, y: 300 }
    setPositions(prev => {
      const next = { ...prev }
      newlyRevealedIds.forEach((id, i) => {
        if (!next[id]) { const a = (i / Math.max(1, newlyRevealedIds.length)) * Math.PI * 2; next[id] = { x: origin.x + Math.cos(a) * 140, y: origin.y + Math.sin(a) * 140 } }
      })
      return next
    })
  }


  const expandNextHop = () => {
    if (!graphVisibleState) return
    setGraphVisibleState(expandAllNextPathHops(graphIndexes, graphVisibleState))
  }

  const collapseNode = (nodeId: string) => {
    if (!graphVisibleState || !graphFocusId) return
    setGraphVisibleState(collapseBranch(graphVisibleState, nodeId, graphFocusId))
    setExpandedNodeIds(prev => { const next = new Set(prev); next.delete(nodeId); return next })
  }

  const runAutoLayout = () => {
    if (filteredNodes.length > FORCE_LAYOUT_MAX_NODES) {
      alert(`Auto-layout works best under ${FORCE_LAYOUT_MAX_NODES} objects (currently ${filteredNodes.length}). Select a fact above to narrow the view first.`)
      return
    }
    setLayoutRunning(true)
    // A setTimeout(0) so the "Computing..." state actually paints before
    // the synchronous simulation below blocks the thread - the simulation
    // itself is still a single blocking pass (see computeForceLayout's doc
    // comment on why this isn't fully async).
    setTimeout(() => {
      const newPositions = computeForceLayout(filteredNodes, (data?.edges || []).filter((e: any) => filteredNodes.find((n: any) => n.id === e.sourceId) && filteredNodes.find((n: any) => n.id === e.targetId)), positions)
      setPositions(newPositions)
      setLayoutRunning(false)
    }, 0)
  }

  const graphSearchMatches = graphSearch.trim()
    ? filteredNodes.filter((n: any) => n.name.toLowerCase().includes(graphSearch.trim().toLowerCase()))
    : []
  const focusOnNode = (n: any) => {
    setSelected(n)
    const p = positions[n.id]
    if (p && graphContainerRef.current) {
      const rect = graphContainerRef.current.getBoundingClientRect()
      setPan({ x: rect.width / 2 - (p.x + 80) * zoom, y: rect.height / 2 - (p.y + 22) * zoom })
    }
  }

  const toggleFullscreen = () => {
    if (!graphContainerRef.current) return
    if (!document.fullscreenElement) { graphContainerRef.current.requestFullscreen(); setIsFullscreen(true) }
    else { document.exitFullscreen(); setIsFullscreen(false) }
  }

  // ── Export (JSON/CSV/SVG/PNG/PDF/PPTX - true binary XLSX remains
  // deferred, see exportUtils.ts's header comment for why). Every format's
  // content comes only from state already held by this component
  // (filteredNodes, data, view) - no export triggers a fresh fetch of its
  // own. Options offered depend on the active vizMode, since a roadmap-
  // shaped export doesn't make sense from graph data and vice versa.
  // PDF/PPTX/PNG generation is async (canvas rasterization, dynamic
  // library import) - exportingFormat tracks which one is in flight so
  // the menu can show a spinner and avoid double-clicks. ──────────────────
  const [exportingFormat, setExportingFormat] = useState<string | null>(null)
  const exportMeta = { viewName: view.name, architectureState: view.architectureState }
  const getMatrixSourcesTargets = () => {
    const sources = filteredNodes.filter((n: any) => view.rootObjectTypes?.includes(n.assetType))
    const targets = filteredNodes.filter((n: any) => view.relatedObjectTypes?.includes(n.assetType))
    const displayTargets = targets.length > 0 ? targets : filteredNodes.filter((n: any) => !view.rootObjectTypes?.includes(n.assetType))
    return { sources, targets: displayTargets }
  }
  const runExport = async (format: 'json' | 'csv' | 'svg' | 'png' | 'pdf' | 'pptx') => {
    setShowExportMenu(false)
    if (format === 'json') {
      const payload = isRoadmap ? { items: roadmapItems } : isDashboard ? { widgets: dashboardWidgets, results: dashboardResults } : { nodes: filteredNodes, edges: data?.edges || [] }
      exportAsJSON(payload, exportMeta)
      return
    }
    if (format === 'csv') {
      if (isRoadmap) { exportRoadmapAsCSV(roadmapItems, exportMeta); return }
      if (vizMode === 'MATRIX') { const { sources, targets } = getMatrixSourcesTargets(); exportMatrixAsCSV(sources, targets, data?.edges || [], exportMeta); return }
      exportNodesAsCSV(filteredNodes, exportMeta)
      return
    }
    // svg/png/pdf/pptx: graph-shaped formats need the live SVG element;
    // table-shaped formats (pdf/pptx only - svg/png are graph-only, see
    // the menu's own conditional rendering below) work from the same
    // node/matrix/roadmap data CSV already uses.
    setExportingFormat(format)
    try {
      if (format === 'svg') { if (graphSvgRef.current) exportGraphAsSVG(graphSvgRef.current, exportMeta); return }
      if (format === 'png') { if (graphSvgRef.current) await exportGraphAsPNG(graphSvgRef.current, exportMeta); return }
      if (format === 'pdf') {
        if (isRoadmap) { await exportRoadmapAsPDF(roadmapItems, exportMeta); return }
        if (vizMode === 'MATRIX') { const { sources, targets } = getMatrixSourcesTargets(); await exportMatrixAsPDF(sources, targets, data?.edges || [], exportMeta); return }
        if (vizMode === 'GRAPH' && graphSvgRef.current) { await exportGraphAsPDF(graphSvgRef.current, exportMeta); return }
        await exportNodesAsPDF(filteredNodes, exportMeta)
        return
      }
      if (format === 'pptx') {
        if (isRoadmap) { await exportRoadmapAsPPTX(roadmapItems, exportMeta); return }
        if (vizMode === 'MATRIX') { const { sources, targets } = getMatrixSourcesTargets(); await exportMatrixAsPPTX(sources, targets, data?.edges || [], exportMeta); return }
        if (vizMode === 'GRAPH' && graphSvgRef.current) { await exportGraphAsPPTX(graphSvgRef.current, exportMeta); return }
        await exportNodesAsPPTX(filteredNodes, exportMeta)
        return
      }
    } catch (e: any) {
      alert(`Export failed: ${e.message || 'unknown error'}`)
    } finally {
      setExportingFormat(null)
    }
  }

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  // Mini-map: nodes scaled into a small fixed box, plus a rectangle
  // showing the current viewport within the full node bounding box.
  const graphBounds = (() => {
    const pts = filteredNodes.map((n: any) => positions[n.id]).filter(Boolean) as { x: number; y: number }[]
    if (pts.length === 0) return { minX: 0, minY: 0, maxX: 1000, maxY: 1000 }
    return {
      minX: Math.min(...pts.map(p => p.x)) - 40, minY: Math.min(...pts.map(p => p.y)) - 40,
      maxX: Math.max(...pts.map(p => p.x)) + 200, maxY: Math.max(...pts.map(p => p.y)) + 80,
    }
  })()

  // ── Capability Map ──────────────────────────────────────────────────────────
  // ── Capability Map (Phase 4B) ────────────────────────────────────────
  //
  // Consumes buildCapabilityMapDisplay(dataset, eligibility) - real
  // hierarchy from ViewDataset.hierarchies, whatever depth is actually
  // present (Section 2's explicit "do not preserve the old artificial
  // two-level limit"). Deeper branches use the same collapse mechanism as
  // Tree (shared collapsedTreeNodes state) for progressive rendering
  // rather than becoming unusable.
  const renderCapabilityMap = () => {
    const display = buildCapabilityMapDisplay(dataset, eligibility)
    if (!display.eligible) {
      return <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40, maxWidth: 480, margin: '0 auto' }}>
        <div style={{ fontSize: 28, marginBottom: 12 }}>▦</div>
        <div>{display.reason}</div>
      </div>
    }
    const objectById = new Map<string, any>((dataset?.objects ?? []).map((o: any) => [o.id, o]))
    const toggle = (id: string) => setCollapsedTreeNodes(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next })

    const renderCapNode = (id: string, depth: number): React.ReactNode => {
      const cap = objectById.get(id)
      if (!cap) return null
      const children = display.childrenByParentId?.[id] ?? []
      const hasChildren = children.length > 0
      const isCollapsed = collapsedTreeNodes.has(id)
      const overlayCount = computeCapabilityOverlayCount(dataset, id, capabilityOverlayType)
      return (
        <div key={id} style={{ marginLeft: depth * 18 }}>
          <div onClick={() => setCapabilityDrilldown(buildCapabilityDrilldown(dataset, id))}
            style={{ display: 'flex', alignItems: 'center', gap: 8, background: depth === 0 ? '#3498db22' : 'var(--navy-light)', border: depth === 0 ? '2px solid #3498db44' : '1px solid var(--border)', borderRadius: 6, padding: '6px 10px', marginBottom: 6, cursor: 'pointer' }}>
            {hasChildren ? (
              <span onClick={e => { e.stopPropagation(); toggle(id) }} style={{ width: 14, textAlign: 'center', fontSize: 10, color: 'var(--text-dim)', cursor: 'pointer' }}>{isCollapsed ? '▶' : '▼'}</span>
            ) : <span style={{ width: 14 }} />}
            <span style={{ fontSize: depth === 0 ? 13 : 12, fontWeight: depth === 0 ? 700 : 500, color: depth === 0 ? '#3498db' : 'var(--text)' }}>{cap.name}</span>
            {overlayCount !== null && <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-dim)' }}>{capabilityOverlayType}: {overlayCount}</span>}
          </div>
          {hasChildren && !isCollapsed && children.map(c => renderCapNode(c, depth + 1))}
        </div>
      )
    }

    return (
      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center' }}>
          <label style={{ ...S.label, marginBottom: 0 }}>Overlay:</label>
          <select style={{ ...S.input, maxWidth: 200 }} value={capabilityOverlayType} onChange={e => setCapabilityOverlayType(e.target.value)}>
            {[...new Set((dataset?.objects ?? []).map((o: any) => o.semanticType || o.assetType))].filter((t: any) => t !== 'BusinessCapability').map((t: any) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 20, minWidth: 800 }}>
          {(display.rootIds ?? []).map(rootId => <div key={rootId} style={{ flex: 1, minWidth: 200 }}>{renderCapNode(rootId, 0)}</div>)}
        </div>
        {capabilityDrilldown && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setCapabilityDrilldown(null)}>
            <div style={{ background: 'var(--navy)', border: '1px solid var(--border)', borderRadius: 8, padding: 20, maxWidth: 480, maxHeight: '70vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              {capabilityDrilldown.parent && <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 8 }}>Parent: {capabilityDrilldown.parent.name}</div>}
              {capabilityDrilldown.children.length > 0 && <div style={{ fontSize: 12, marginBottom: 8 }}>Children: {capabilityDrilldown.children.map((c: any) => c.name).join(', ')}</div>}
              {capabilityDrilldown.related.length > 0 && <div style={{ fontSize: 12, marginBottom: 8 }}>Related: {capabilityDrilldown.related.map((r: any) => r.name).join(', ')}</div>}
              <button style={{ ...S.btn(), marginTop: 8 }} onClick={() => setCapabilityDrilldown(null)}>Close</button>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── Heatmap (Phase 4B) ────────────────────────────────────────────────
  //
  // Consumes buildHeatmapDisplay(dataset, eligibility, heatmapField) -
  // structure + metric, never just colored objects (Section 3). Missing
  // values render as a distinct muted tile, never coerced to 0 or a
  // fabricated color.
  const HEATMAP_STATUS: Record<string,string> = { APPROVED:'#2ecc71', ACTIVE:'#2ecc71', UNDER_REVIEW:'#f39c12', DRAFT:'#e67e22', DEPRECATED:'#e74c3c', PLANNED:'#3498db' }
  const renderHeatmap = () => {
    const display = buildHeatmapDisplay(dataset, eligibility, heatmapField)
    if (!display.eligible) {
      return <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40, maxWidth: 480, margin: '0 auto' }}>
        <div style={{ fontSize: 28, marginBottom: 12 }}>▤</div>
        <div>{display.reason}</div>
      </div>
    }
    const colorByValue: Record<string, string> = {}
    if (display.dataType === 'categorical' || display.dataType === 'status') {
      (display.distinctValues ?? []).forEach((v, i) => { colorByValue[v] = display.dataType === 'status' ? (HEATMAP_STATUS[v] || '#7f8c8d') : HEATMAP_CATEGORICAL_PALETTE[i % HEATMAP_CATEGORICAL_PALETTE.length] })
    }
    const getColor = (tile: { value: number | string | null }): string => {
      if (tile.value === null) return '#4b5563' // muted gray for genuinely missing values
      if (display.dataType === 'numeric') return numericGradientColor(tile.value as number, display.min!, display.max!)
      return colorByValue[String(tile.value)] || '#7f8c8d'
    }
    const objectById = new Map<string, any>((dataset?.objects ?? []).map((o: any) => [o.id, o]))
    const tileView = (tile: any) => {
      const color = getColor(tile)
      const obj = objectById.get(tile.objectId)
      return (
        <button type="button" key={tile.objectId} data-testid="heatmap-tile" onClick={() => setSelected(obj)} title={`${tile.name}: ${tile.displayValue}`}
          style={{ textAlign: 'start' as const, font: 'inherit', color: 'inherit', padding: '10px 12px', borderRadius: 8, background: color + '22', borderTop: `1px solid ${color}55`, borderRight: `1px solid ${color}55`, borderBottom: `1px solid ${color}55`, borderLeft: `4px solid ${color}`, cursor: 'pointer' }}>
          <div style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.3 }}>{isAR && obj?.nameAr ? obj.nameAr : tile.name}</div>
          <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>{tile.value === null ? t('eaviews.heatmap_no_value') : tile.displayValue}</div>
        </button>
      )
    }
    const missing = (display.tiles ?? []).filter(x => x.value === null).length
    return (
      <div data-testid="heatmap">
        {dataset?.provenance?.truncated && (
          <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 10 }}>{t('eaviews.heatmap_truncated')}</div>
        )}
        <div className="eav-heatmap-bar">
          <label htmlFor="heatmap-color-by" style={{ ...S.label, marginBottom: 0 }}>{t('eaviews.heatmap_color_by')}</label>
          <select id="heatmap-color-by" style={{ ...S.input, maxWidth: 280 }} value={display.metricKey} onChange={e => setHeatmapField(e.target.value)}>
            {(display.metricOptions ?? []).map(m => <option key={m.key} value={m.key}>{m.label} ({m.coveragePercent}%)</option>)}
          </select>
          <HelpTip text={t('eaviews.heatmap_help')} />
          <div className="eav-heatmap-legend">
            {(display.dataType === 'categorical' || display.dataType === 'status') && Object.entries(colorByValue).map(([k,c]) => <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-dim)' }}><div style={{ width: 10, height: 10, borderRadius: 2, background: c }} />{k}</div>)}
            {display.dataType === 'numeric' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--text-dim)' }}>
                <span>{display.min}</span>
                <div style={{ width: 100, height: 10, borderRadius: 5, background: 'linear-gradient(90deg, #2ecc71, #f39c12, #e74c3c)' }} />
                <span>{display.max}</span>
              </div>
            )}
            {missing > 0 && <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-dim)' }}><div style={{ width: 10, height: 10, borderRadius: 2, background: '#4b5563' }} />{t('eaviews.heatmap_no_value')} ({missing})</div>}
          </div>
        </div>
        {(display.groups ?? []).map(g => (
          <section key={g.assetType} style={{ marginBottom: 16 }}>
            {(display.groups ?? []).length > 1 && <h3 style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-dim)', margin: '0 0 8px' }}>{g.assetType.replace(/_/g, ' ')} ({g.tiles.length})</h3>}
            <div className="eav-heatmap-grid">{g.tiles.map(tileView)}</div>
          </section>
        ))}
      </div>
    )
  }

  // ── Matrix ───────────────────────────────────────────────────────────────────
  // ── Matrix (Phase 4A - DIRECT/PATH semantic matrix) ─────────────────────
  //
  // Consumes buildMatrix(dataset, eligibility) - never independently
  // invents row/column axes (Section 6). Ineligibility shows the
  // deterministic reason, not an empty grid that looks broken (Section
  // 11). Row/column limits are explicit ("Showing X of Y"), never a
  // silent slice (Section 12) - the old implementation's silent
  // .slice(0,30)/.slice(0,20) is exactly what this replaces.
  const MATRIX_ROW_LIMIT = 30
  const MATRIX_COL_LIMIT = 20
  const renderMatrix = () => {
    const result = buildMatrix(dataset, eligibility)
    if (!result.eligible) {
      return <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40, maxWidth: 480, margin: '0 auto' }}>
        <div style={{ fontSize: 28, marginBottom: 12 }}>⊞</div>
        <div>{result.reason}</div>
      </div>
    }
    const arranged = arrangeMatrix(result.rows ?? [], result.columns ?? [], result.cells, { order: matrixOrder, hideEmptyColumns: matrixHideEmptyCols })
    const allRows = arranged.rows
    const allCols = arranged.columns
    const totalRowPages = Math.max(1, Math.ceil(allRows.length / MATRIX_ROW_LIMIT))
    const totalColPages = Math.max(1, Math.ceil(allCols.length / MATRIX_COL_LIMIT))
    const clampedRowPage = Math.min(matrixRowPage, totalRowPages)
    const clampedColPage = Math.min(matrixColPage, totalColPages)
    const rows = allRows.slice((clampedRowPage - 1) * MATRIX_ROW_LIMIT, clampedRowPage * MATRIX_ROW_LIMIT)
    const cols = allCols.slice((clampedColPage - 1) * MATRIX_COL_LIMIT, clampedColPage * MATRIX_COL_LIMIT)
    const cell = (rowId: string, colId: string) => result.cells?.get(`${rowId}::${colId}`)

    return (
      <div>
        {result.relationMode === 'PATH' && result.pathSteps && (
          <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 10, padding: '6px 10px', background: 'var(--navy-mid)', borderRadius: 6, display: 'inline-block' }}>
            Path-based Matrix ({result.pathSteps.length} hops) — Derived through: {[result.pathSteps[0].from, ...result.pathSteps.map(s => s.to)].join(' → ')}
          </div>
        )}
        <div data-testid="matrix-controls" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' as const, fontSize: 12, marginBottom: 10 }}>
          <label htmlFor="matrix-order" style={{ color: 'var(--text-dim)' }}>{t('eaviews.matrix_order')}</label>
          <select id="matrix-order" style={{ ...S.input, width: 'auto', padding: '3px 6px', fontSize: 12 }} value={matrixOrder} onChange={e => { setMatrixOrder(e.target.value as MatrixOrder); setMatrixRowPage(1) }}>
            <option value="LINKED">{t('eaviews.matrix_order_linked')}</option>
            <option value="GAPS">{t('eaviews.matrix_order_gaps')}</option>
            <option value="NAME">{t('eaviews.matrix_order_name')}</option>
          </select>
          <input id="matrix-hide-empty" type="checkbox" checked={matrixHideEmptyCols} onChange={e => { setMatrixHideEmptyCols(e.target.checked); setMatrixColPage(1) }} />
          <label htmlFor="matrix-hide-empty">{t('eaviews.matrix_hide_empty')}</label>
          <HelpTip text={t('eaviews.matrix_help')} />
          {arranged.emptyRows > 0 && <span data-testid="matrix-gaps" style={{ color: 'var(--text-dim)' }}>{t('eaviews.matrix_gaps')}: {arranged.emptyRows}</span>}
          {arranged.hiddenColumns > 0 && <span style={{ color: 'var(--text-dim)' }}>{t('eaviews.matrix_hidden')}: {arranged.hiddenColumns}</span>}
        </div>
        {(allRows.length > MATRIX_ROW_LIMIT || allCols.length > MATRIX_COL_LIMIT) && (
          <div className="flex items-center gap-3 flex-wrap" style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 10 }}>
            <span>Showing {rows.length} of {allRows.length} {result.rowType} × {cols.length} of {allCols.length} {result.columnType}</span>
            {allRows.length > MATRIX_ROW_LIMIT && (
              <span className="flex items-center gap-1">
                Rows:
                <button className="btn btn-secondary btn-sm" disabled={clampedRowPage <= 1} onClick={() => setMatrixRowPage(p => p - 1)}>‹</button>
                <span style={{ fontFamily: 'var(--font-mono)' }}>{clampedRowPage}/{totalRowPages}</span>
                <button className="btn btn-secondary btn-sm" disabled={clampedRowPage >= totalRowPages} onClick={() => setMatrixRowPage(p => p + 1)}>›</button>
              </span>
            )}
            {allCols.length > MATRIX_COL_LIMIT && (
              <span className="flex items-center gap-1">
                Columns:
                <button className="btn btn-secondary btn-sm" disabled={clampedColPage <= 1} onClick={() => setMatrixColPage(p => p - 1)}>‹</button>
                <span style={{ fontFamily: 'var(--font-mono)' }}>{clampedColPage}/{totalColPages}</span>
                <button className="btn btn-secondary btn-sm" disabled={clampedColPage >= totalColPages} onClick={() => setMatrixColPage(p => p + 1)}>›</button>
              </span>
            )}
          </div>
        )}
        {result.relationMode === 'DIRECT' && [...(result.cells?.values() ?? [])].some(c => cellInteraction(c)) && (
          <div className="flex items-center gap-2" style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 10 }}>
            <span>Cells show the recorded interaction: C = create, R = read, U = update, D = delete.</span>
            <HelpTip text="Each cell shows what the application does with that data entity, as recorded on the relationship itself (for example CRUD, RU or R). A dot means the relationship exists but no interaction has been recorded for it." />
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
            <thead>
              <tr>
                <th style={{ padding: '8px 12px', background: 'var(--navy-mid)', borderBottom: '1px solid var(--border)', textAlign: 'left', fontSize: 11, color: 'var(--text-dim)', fontWeight: 600, position: 'sticky', left: 0, minWidth: 140 }}>{result.rowType} ↓ / {result.columnType} →</th>
                {cols.map((c: any) => (
                  <th key={c.id} style={{ padding: '6px 10px', background: 'var(--navy-mid)', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, color: TYPE_COLOR[c.assetType]||'var(--text)', whiteSpace: 'nowrap', minWidth: 100, maxWidth: 140 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
                    <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-dim)' }}>{arranged.columnTotals.get(c.id)}</div>
                  </th>
                ))}
                <th title={t('eaviews.matrix_total')} style={{ padding: '6px 10px', background: 'var(--navy-mid)', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--text-dim)' }}>Σ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any, ri: number) => (
                <tr key={r.id} className={arranged.rowTotals.get(r.id) === 0 ? 'matrix-gap-row' : undefined} style={{ background: ri % 2 === 0 ? 'var(--navy-light)' : 'transparent' }}>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 600, position: 'sticky', left: 0, background: ri % 2 === 0 ? 'var(--navy-light)' : 'var(--navy)', maxWidth: 140 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: TYPE_COLOR[r.assetType]||'var(--text)' }}>{r.name}</div>
                  </td>
                  {cols.map((c: any) => {
                    const populated = cell(r.id, c.id)
                    return (
                      <td key={c.id}
                        onClick={() => populated && setMatrixDrilldown({ rowObj: r, colObj: c, relationMode: result.relationMode, items: populated.items })}
                        style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', borderLeft: '1px solid var(--border)', textAlign: 'center', cursor: populated ? 'pointer' : 'default', background: populated ? 'rgba(3,105,161,0.12)' : 'transparent' }}>
                        {populated && <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)' }}>{(result.relationMode === 'DIRECT' && cellInteraction(populated)) || (populated.count > 1 ? `${populated.count} ${result.relationMode === 'PATH' ? 'paths' : ''}`.trim() : (result.relationMode === 'PATH' ? '1 path' : '●'))}</span>}
                      </td>
                    )
                  })}
                  <td data-testid={`row-total-${r.id}`} style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', borderLeft: '1px solid var(--border)', textAlign: 'center', fontSize: 11, color: 'var(--text-dim)' }}>{arranged.rowTotals.get(r.id)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {matrixDrilldown && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setMatrixDrilldown(null)}>
            <div style={{ background: 'var(--navy)', border: '1px solid var(--border)', borderRadius: 8, padding: 20, maxWidth: 500, maxHeight: '70vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{matrixDrilldown.rowObj.name} × {matrixDrilldown.colObj.name}</div>
              {matrixDrilldown.relationMode === 'DIRECT' ? (
                <div>
                  {matrixDrilldown.items.map((rel: any) => (
                    <div key={rel.id} style={{ fontSize: 12, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>{matrixDrilldown.rowObj.name} —{rel.label || rel.relationshipType}→ {matrixDrilldown.colObj.name}</div>
                  ))}
                </div>
              ) : (
                <div>
                  {matrixDrilldown.items.map((path: any) => (
                    <div key={path.id} style={{ fontSize: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                      {path.objectIds.map((oid: string) => (dataset?.objects ?? []).find((o: any) => o.id === oid)?.name || oid).join(' → ')}
                    </div>
                  ))}
                </div>
              )}
              <button style={{ ...S.btn(), marginTop: 12 }} onClick={() => setMatrixDrilldown(null)}>Close</button>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── Table ────────────────────────────────────────────────────────────────────
  // ── Table (Phase 4A - relationship-aware, with inventory fallback) ──────
  //
  // determineTableMode/buildRelationshipTable consume ViewDataset directly
  // (Section 1) - the inventory branch below (unchanged JSX) is the
  // genuine fallback for a single-object-set view, not the default for a
  // relational one.
  const renderTable = () => {
    const mode = determineTableMode(dataset)
    if (mode === 'relationship') {
      const table = buildRelationshipTable(dataset)
      // Interleaves object columns with the relationship label between
      // each consecutive pair, matching the spec's own worked example:
      // Business Capability | supported_by | Application | hosted_on | Technology
      const headerCells: string[] = []
      table.columns.forEach((c, i) => { headerCells.push(c); if (i < table.relationLabels.length) headerCells.push(table.relationLabels[i]) })
      return (
        <div style={{ overflowX: 'auto' }}>
          {dataset?.provenance?.truncated && (
            <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 10 }}>This result was truncated by a traversal size limit - not every matching path is shown.</div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {headerCells.map((h, i) => (
                  <th key={i} style={{ padding: '8px 12px', background: i % 2 === 1 ? 'transparent' : 'var(--navy-mid)', borderBottom: '1px solid var(--border)', textAlign: 'left', fontSize: i % 2 === 1 ? 10 : 11, color: 'var(--text-dim)', fontWeight: i % 2 === 1 ? 400 : 600, fontStyle: i % 2 === 1 ? 'italic' : 'normal' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, ri) => {
                const cells: any[] = []
                row.values.forEach((v, i) => {
                  cells.push(<td key={`o${i}`} style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer' }} onClick={() => setSelected((dataset?.objects ?? []).find((o: any) => o.id === v.id))}>{v.name}</td>)
                  if (i < table.relationLabels.length) cells.push(<td key={`r${i}`} style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--text-dim)', fontStyle: 'italic' }}>{table.relationLabels[i]}</td>)
                })
                return <tr key={row.id} style={{ background: ri % 2 === 0 ? 'var(--navy-light)' : 'transparent' }}>{cells}</tr>
              })}
            </tbody>
          </table>
        </div>
      )
    }
    // Inventory fallback - unchanged from before Phase 4A.
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[t("common.name"),t("common.type"),'Domain',t("common.status"),'Owner','Tags'].map(h => <th key={h} style={{ padding: '8px 12px', background: 'var(--navy-mid)', borderBottom: '1px solid var(--border)', textAlign: 'left', fontSize: 11, color: 'var(--text-dim)', fontWeight: 600 }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filteredNodes.map((n: any, i: number) => (
              <tr key={n.id} onClick={() => setSelected(n)} style={{ cursor: 'pointer', background: i%2===0?'var(--navy-light)':'transparent' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(3,105,161,0.05)')}
                onMouseLeave={e => (e.currentTarget.style.background = i%2===0?'var(--navy-light)':'transparent')}>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontWeight: 500 }}>{n.name}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}><span style={S.badge(TYPE_COLOR[n.assetType]||'#7f8c8d')}>{n.assetType.replace(/_/g,' ')}</span></td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text-dim)' }}>{n.domain}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}><span style={S.badge(HEATMAP_STATUS[n.status]||'#7f8c8d')}>{n.status}</span></td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text-dim)' }}>{n.owner||'—'}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{(n.tags||[]).slice(0,2).map((t: string) => <span key={t} style={{ ...S.badge('#7f8c8d'), marginRight: 4, fontSize: 10 }}>{t}</span>)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  // ── Tree/Hierarchy (uses metadata.parentId - the same convention the
  // capability hierarchy edges already rely on in the backend) ─────────────
  // ── Tree (Phase 4B) ───────────────────────────────────────────────────
  //
  // Consumes buildTreeDisplay(dataset, eligibility) - real hierarchy only
  // (Section 4). The render function itself also carries a defensive
  // visited-set guard during recursion (not just the utility's own cycle
  // detection) so malformed data can never cause infinite recursion here,
  // even if it somehow slipped past the utility layer.
  const renderTree = () => {
    const display = buildTreeDisplay(dataset, eligibility)
    if (!display.eligible) {
      return <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40, maxWidth: 480, margin: '0 auto' }}>
        <div style={{ fontSize: 28, marginBottom: 12 }}>⌵</div>
        <div>{display.reason}</div>
      </div>
    }
    const objectById = new Map<string, any>((dataset?.objects ?? []).map((o: any) => [o.id, o]))
    const toggle = (id: string) => setCollapsedTreeNodes(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next })
    const renderNode = (id: string, depth: number, ancestry: Set<string>): React.ReactNode => {
      if (ancestry.has(id)) return null // defensive cycle guard at render time too, independent of the utility's own detection
      const n = objectById.get(id)
      if (!n) return null
      const children = display.childrenByParentId?.[id] ?? []
      const hasChildren = children.length > 0
      const isCollapsed = collapsedTreeNodes.has(id)
      const color = TYPE_COLOR[n.assetType] || '#7f8c8d'
      return (
        <div key={id}>
          <div onClick={() => setSelected(n)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', marginLeft: depth * 22, borderRadius: 6, cursor: 'pointer' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(3,105,161,0.08)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
            {hasChildren ? (
              <span onClick={e => { e.stopPropagation(); toggle(id) }} style={{ width: 16, textAlign: 'center', fontSize: 10, color: 'var(--text-dim)', cursor: 'pointer' }}>{isCollapsed ? '▶' : '▼'}</span>
            ) : <span style={{ width: 16 }} />}
            <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
            <span style={{ fontSize: 13, fontWeight: depth === 0 ? 600 : 400 }}>{n.name}</span>
            <span style={{ ...S.badge(HEATMAP_STATUS[n.status] || '#7f8c8d'), fontSize: 10, marginLeft: 'auto' }}>{n.status}</span>
          </div>
          {hasChildren && !isCollapsed && children.map(c => renderNode(c, depth + 1, new Set([...ancestry, id])))}
        </div>
      )
    }
    return (
      <div>
        {display.malformed && <div style={{ fontSize: 12, color: '#f39c12', marginBottom: 10 }}>⚠ Some hierarchy data was malformed (a cycle or dangling reference) and was safely excluded.</div>}
        <div style={S.card}>{(display.rootIds ?? []).map(id => renderNode(id, 0, new Set()))}</div>
      </div>
    )
  }

  // ── Cards ─────────────────────────────────────────────────────────────────
  // ── Cards (Phase 4B) ──────────────────────────────────────────────────
  //
  // Consumes buildCardContext(dataset, objectId) for relationship
  // summaries - object + meaningful context, not an isolated inventory
  // tile (Section 5). Entirely derived from the already-fetched dataset;
  // no per-card fetch. A density warning (reusing the existing filter
  // bar, not new pagination infrastructure) appears above a threshold.
  const CARDS_WARN_THRESHOLD = 100
  // Empty results carry their meaning from the viewpoint contract: no data
  // in this state, nothing matching the scope, or objects not linked yet.
  const resultState = presentation?.resultState
  const resultEmpty = !!resultState && (resultState.state === 'NO_DATA' || resultState.state === 'FILTERED_OUT' || resultState.state === 'REVIEW_REQUIRED')
  const RESULT_STATE_TITLE: Record<string, string> = { NO_DATA: 'eaviews.result_no_data', FILTERED_OUT: 'eaviews.result_filtered_out', NO_RELATIONSHIPS: 'eaviews.result_no_relationships', REVIEW_REQUIRED: 'eaviews.review_required' }
  const renderResultState = () => (
    <div role="status" data-testid="result-state" style={{ ...S.card, textAlign: 'center', padding: 40 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{t(RESULT_STATE_TITLE[resultState.state])}</div>
      <div style={{ fontSize: 13, color: 'var(--text-dim)' }}>{resultState.message}</div>
    </div>
  )

  const renderLandscape = () => {
    const primaryIds = new Set<string>((dataset?.objects || []).filter((o: any) => o.role === 'PRIMARY').map((o: any) => o.id))
    const visibleIds = new Set(filteredNodes.map((n: any) => n.id))
    const edges = (data?.edges || []).filter((e: any) => visibleIds.has(e.sourceId) && visibleIds.has(e.targetId))
    return <LandscapeView nodes={filteredNodes} edges={edges} primaryIds={primaryIds.size > 0 ? primaryIds : new Set(filteredNodes.map((n: any) => n.id))} onSelect={(n) => setSelected(n)} />
  }

  const renderCards = () => (
    <div>
      {filteredNodes.length > CARDS_WARN_THRESHOLD && (
        <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 12 }}>
          {filteredNodes.length} cards - select a fact above to narrow this down for easier browsing.
        </div>
      )}
      {dataset?.provenance?.truncated && (
        <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 12 }}>Results are truncated - not every matching object is shown.</div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
        {filteredNodes.map((n: any) => {
          const color = TYPE_COLOR[n.assetType] || '#7f8c8d'
          const context = dataset ? buildCardContext(dataset, n.id) : null
          return (
            <div key={n.id} onClick={() => setSelected(n)} style={{ ...S.card, cursor: 'pointer', borderLeft: `3px solid ${color}` }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(3,105,161,0.05)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'var(--navy-light)')}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <span style={S.badge(color)}>{n.assetType.replace(/_/g, ' ')}</span>
                <span style={S.badge(HEATMAP_STATUS[n.status] || '#7f8c8d')}>{n.status}</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6, lineHeight: 1.3 }}>{n.name}</div>
              {n.description && <div style={{ fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.4, marginBottom: 8, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' }}>{n.description}</div>}
              {context && context.summaries.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  {context.summaries.map(s => (
                    <div key={s.relationshipType} style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 2 }}>
                      <span style={{ fontStyle: 'italic' }}>{s.label}:</span> {s.relatedNames.join(', ')}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-dim)' }}>
                <span>{n.domain}</span>
                <span>{n.owner || '—'}</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )

  // ── Comparison (Phase 5B) ─────────────────────────────────────────────
  //
  // A distinct rendering path, never touching the single-scenario viewer
  // state above. Table (Section 12, "one of the strongest comparison
  // renderers") and Matrix (Section 16) are implemented; Capability Map/
  // Heatmap/Tree/Cards comparison-specific treatments are deferred (see
  // completion report) - the underlying ComparisonDataset already
  // supports building them the same way, but doing so wasn't completed
  // in this pass given the phase's overall scope.
  const renderComparison = () => {
    const CHANGE_COLOR: Record<string, string> = { ADDED: '#2ecc71', REMOVED: '#e74c3c', MODIFIED: '#f39c12', UNCHANGED: '#7f8c8d' }
    return (
      <div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' as const }}>
          <label style={S.label} htmlFor="compare-from">{stateLines ? t('eaviews.compare_from') : 'Left'}</label>
          <select id="compare-from" style={{ ...S.input, maxWidth: 240 }} value={comparisonLeftId ?? ''} onChange={e => { setComparisonLeftId(e.target.value || null); if (stateLines) setComparisonRightId(null) }}>
            <option value="">Select…</option>
            {stateLines
              ? allStates(stateLines).map(s => <option key={s.scenarioId} value={s.scenarioId}>{stateTitle(s, t)}{s.label !== 'CURRENT' ? ` · ${s.name}` : ''}</option>)
              : scenarios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <span style={{ color: 'var(--text-dim)' }}>→</span>
          <label style={S.label} htmlFor="compare-to">{stateLines ? t('eaviews.compare_to') : 'Right'}</label>
          <select id="compare-to" style={{ ...S.input, maxWidth: 240 }} value={comparisonRightId ?? ''} onChange={e => setComparisonRightId(e.target.value || null)}>
            <option value="">Select…</option>
            {stateLines
              ? (comparisonLeftId ? comparableWith(stateLines, comparisonLeftId) : []).map(s => <option key={s.scenarioId} value={s.scenarioId}>{stateTitle(s, t)}{s.label !== 'CURRENT' ? ` · ${s.name}` : ''}</option>)
              : scenarios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button style={S.btn('primary')} disabled={!comparisonLeftId || !comparisonRightId} onClick={() => comparisonLeftId && comparisonRightId && runComparison(comparisonLeftId, comparisonRightId)}>
            Compare{comparisonLoading ? ' ⏳' : ''}
          </button>
          <button style={{ ...S.btn(), marginLeft: 'auto' }} onClick={() => { setComparisonMode(false); setComparisonData(null) }}>✕ Exit Comparison</button>
        </div>
        {comparisonError && <div style={{ fontSize: 12, color: '#e74c3c', marginBottom: 12 }}>⚠ {comparisonError}</div>}
        {evolution && <EvolutionSummary evolution={evolution} />}
        {!comparisonData ? (
          <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40 }}>Choose two scenarios and click Compare to see what changed architecturally between them.</div>
        ) : (() => {
          const oc = comparisonData.objects, rc = comparisonData.relationships
          const filtered = applyComparisonFilters(comparisonData, { changeTypes: [...comparisonChangeFilter] as any })
          return (
            <div>
              {/* Section 11: summary header */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: 13, alignItems: 'center' }}>
                <strong>{comparisonData.context?.leftScenario?.name}</strong><span style={{ color: 'var(--text-dim)' }}>→</span><strong>{comparisonData.context?.rightScenario?.name}</strong>
                <button style={{ ...S.btn(), fontSize: 11, marginLeft: 12 }} disabled={aiAssistLoading} onClick={runAiExplainComparison}>{aiAssistLoading ? '🤖 Explaining…' : '🤖 Explain Changes'}</button>
              </div>
              {aiAssistError && <div style={{ fontSize: 12, color: '#e74c3c', marginBottom: 10 }}>⚠ {aiAssistError}</div>}
              {aiComparisonExplanation && (
                <div style={{ marginBottom: 16 }}>
                  {(aiComparisonExplanation.claims ?? []).map((c: any, i: number) => {
                    const style = CLAIM_STYLE[c.classification as keyof typeof CLAIM_STYLE]
                    return (
                      <div key={i} style={{ ...S.card, padding: 10, marginBottom: 6, borderLeft: `3px solid ${style?.color || '#7f8c8d'}` }}>
                        <span style={S.badge(style?.color || '#7f8c8d')}>{style?.icon} {style?.label || c.classification}</span>
                        <div style={{ fontSize: 13, marginTop: 4 }}>{c.text}</div>
                      </div>
                    )
                  })}
                </div>
              )}
              <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' as const, fontSize: 12 }}>
                <span>Objects: <span style={{ color: CHANGE_COLOR.ADDED }}>+{oc.added.length} Added</span> <span style={{ color: CHANGE_COLOR.REMOVED }}>−{oc.removed.length} Removed</span> <span style={{ color: CHANGE_COLOR.MODIFIED }}>~{oc.modified.length} Modified</span> <span style={{ color: 'var(--text-dim)' }}>{oc.unchanged.length} Unchanged</span></span>
                <span>Relationships: <span style={{ color: CHANGE_COLOR.ADDED }}>+{rc.added.length} Added</span> <span style={{ color: CHANGE_COLOR.REMOVED }}>−{rc.removed.length} Removed</span></span>
              </div>
              {(comparisonData.warnings ?? []).map((w: any) => <div key={w.code} style={{ fontSize: 12, color: '#f39c12', marginBottom: 8 }}>⚠ {w.message}</div>)}
              <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' as const }}>
                <button style={comparisonView === 'TABLE' ? S.btn('primary') : S.btn()} onClick={() => setComparisonView('TABLE')}>Table</button>
                <button style={comparisonView === 'MATRIX' ? S.btn('primary') : S.btn()} onClick={() => setComparisonView('MATRIX')}>Matrix</button>
                <button style={comparisonView === 'GRAPH' ? S.btn('primary') : S.btn()} onClick={() => setComparisonView('GRAPH')}>Graph</button>
                <button style={comparisonView === 'CAPABILITY_MAP' ? S.btn('primary') : S.btn()} onClick={() => setComparisonView('CAPABILITY_MAP')}>Capability Map</button>
                <button style={comparisonView === 'HEATMAP' ? S.btn('primary') : S.btn()} onClick={() => setComparisonView('HEATMAP')}>Heatmap</button>
                <button style={comparisonView === 'TREE' ? S.btn('primary') : S.btn()} onClick={() => setComparisonView('TREE')}>Tree</button>
                <button style={comparisonView === 'CARDS' ? S.btn('primary') : S.btn()} onClick={() => setComparisonView('CARDS')}>Cards</button>
                {(['ADDED', 'REMOVED', 'MODIFIED', 'UNCHANGED'] as const).map(ct => (
                  <label key={ct} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
                    <input type="checkbox" checked={comparisonChangeFilter.has(ct)} onChange={() => setComparisonChangeFilter(prev => { const next = new Set(prev); next.has(ct) ? next.delete(ct) : next.add(ct); return next })} />
                    <span style={{ color: CHANGE_COLOR[ct] }}>{ct}</span>
                  </label>
                ))}
              </div>
              {comparisonView === 'TABLE' ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
                    <thead><tr>{['Object', t("common.type"), 'Change', 'Changed Properties', 'Before', 'After'].map(h => <th key={h} style={{ padding: '8px 12px', background: 'var(--navy-mid)', borderBottom: '1px solid var(--border)', textAlign: 'left', fontSize: 11, color: 'var(--text-dim)' }}>{h}</th>)}</tr></thead>
                    <tbody>
                      {buildChangeSummaryRows({ objects: filtered }, comparisonChangeFilter.has('UNCHANGED')).map((row, i) => (
                        <tr key={row.id} style={{ background: i % 2 === 0 ? 'var(--navy-light)' : 'transparent' }}>
                          <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontWeight: 500 }}>{row.name}</td>
                          <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text-dim)' }}>{row.type}</td>
                          <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}><span style={S.badge(CHANGE_COLOR[row.changeType])}>{row.changeType}</span></td>
                          <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>{row.changedProperties.join(', ')}</td>
                          <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text-dim)' }}>{row.before}</td>
                          <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>{row.after}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Relationship Changes</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>{['Source', 'Relationship', 'Target', 'Change'].map(h => <th key={h} style={{ padding: '8px 12px', background: 'var(--navy-mid)', borderBottom: '1px solid var(--border)', textAlign: 'left', fontSize: 11, color: 'var(--text-dim)' }}>{h}</th>)}</tr></thead>
                    <tbody>
                      {buildRelationshipChangeRows(comparisonData).map((row, i) => (
                        <tr key={row.key} style={{ background: i % 2 === 0 ? 'var(--navy-light)' : 'transparent' }}>
                          <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{row.source}</td>
                          <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12, fontStyle: 'italic', color: 'var(--text-dim)' }}>{row.relationship}</td>
                          <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{row.target}</td>
                          <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}><span style={S.badge(CHANGE_COLOR[row.changeType])}>{row.changeType}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : comparisonView === 'MATRIX' ? (() => {
                const m = buildComparisonMatrix(comparisonData)
                if (!m.eligible) return <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40 }}>{m.reason}</div>
                return (
                  <div>
                    {m.relationMode === 'PATH' && <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 10 }}>Path-based comparison ({m.rowType} × {m.columnType}) - counts real configured-path instances on each side, not direct relationships.</div>}
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ borderCollapse: 'collapse' }}>
                        <thead><tr><th style={{ padding: '6px 10px', background: 'var(--navy-mid)', fontSize: 11 }}>{m.rowType} ↓ / {m.columnType} →</th>{(m.columns ?? []).map(c => <th key={c.id} style={{ padding: '6px 10px', background: 'var(--navy-mid)', fontSize: 11 }}>{c.name}</th>)}</tr></thead>
                        <tbody>
                          {(m.rows ?? []).map(r => (
                            <tr key={r.id}>
                              <td style={{ padding: '6px 10px', fontWeight: 600, fontSize: 12 }}>{r.name}</td>
                              {(m.columns ?? []).map(c => {
                                const cell = m.cells?.get(`${r.id}::${c.id}`)
                                if (!cell || (cell.beforeCount === 0 && cell.afterCount === 0)) return <td key={c.id} style={{ padding: '6px 10px', borderLeft: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }} />
                                const deltaColor = cell.delta > 0 ? CHANGE_COLOR.ADDED : cell.delta < 0 ? CHANGE_COLOR.REMOVED : 'var(--text-dim)'
                                return <td key={c.id} style={{ padding: '6px 10px', borderLeft: '1px solid var(--border)', borderBottom: '1px solid var(--border)', textAlign: 'center', fontSize: 11 }}>{cell.beforeCount} → {cell.afterCount} <span style={{ color: deltaColor }}>({cell.delta >= 0 ? '+' : ''}{cell.delta})</span></td>
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              })() : comparisonView === 'GRAPH' ? (() => {
                const cgd = buildComparisonGraphDataset(comparisonData)
                const indexes = buildGraphIndexes(cgd)
                if (!comparisonGraphFocusId || !comparisonGraphVisible) return <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40 }}>No objects to compare.</div>
                const visibleObjects = cgd.objects.filter((o: any) => comparisonGraphVisible.visibleObjectIds.has(o.id))
                const visibleRels = cgd.relationships.filter((r: any) => comparisonGraphVisible.visibleRelationshipIds.has(r.id))
                const positions: Record<string, { x: number; y: number }> = {}
                visibleObjects.forEach((o: any, i: number) => { positions[o.id] = { x: 80 + (i % 5) * 150, y: 60 + Math.floor(i / 5) * 100 } })
                return (
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 10 }}>
                      Showing {visibleObjects.length} of {cgd.objects.length} objects. Legend: <span style={{ color: CHANGE_COLOR.ADDED }}>+ Added</span> <span style={{ color: CHANGE_COLOR.REMOVED }}>− Removed</span> <span style={{ color: CHANGE_COLOR.MODIFIED }}>~ Modified</span> <span style={{ color: 'var(--text-dim)' }}>Unchanged</span>
                      <button style={{ ...S.btn(), fontSize: 11, marginLeft: 12 }} onClick={() => setComparisonGraphVisible((prev: any) => expandAllNextPathHops(indexes, prev))}>Expand next hop</button>
                    </div>
                    <svg viewBox="0 0 800 400" style={{ width: '100%', height: 400, background: 'var(--navy-light)', borderRadius: 8 }}>
                      {visibleRels.map((r: any) => {
                        const from = positions[r.sourceId], to = positions[r.targetId]
                        if (!from || !to) return null
                        const color = CHANGE_COLOR[r._comparisonChangeType] || 'var(--text-dim)'
                        return <line key={r.id} x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke={color} strokeWidth={2} strokeDasharray={r._comparisonChangeType === 'REMOVED' ? '4,3' : undefined} />
                      })}
                      {visibleObjects.map((o: any) => {
                        const pos = positions[o.id]
                        const color = CHANGE_COLOR[o._comparisonChangeType] || 'var(--text-dim)'
                        return (
                          <g key={o.id} onClick={() => setSelected(o)} style={{ cursor: 'pointer' }}>
                            <circle cx={pos.x} cy={pos.y} r={26} fill="var(--navy)" stroke={color} strokeWidth={3} />
                            <text x={pos.x} y={pos.y - 34} textAnchor="middle" fontSize={11} fill="var(--text)">{CHANGE_TYPE_SYMBOL[o._comparisonChangeType]} {o.name}</text>
                          </g>
                        )
                      })}
                    </svg>
                  </div>
                )
              })() : comparisonView === 'CAPABILITY_MAP' ? (() => {
                const cm = buildComparisonCapabilityMap(comparisonData)
                if (!cm.eligible) return <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40 }}>{cm.reason}</div>
                return (
                  <div>
                    {(cm.nodes ?? []).sort((a, b) => a.depth - b.depth).map(n => (
                      <div key={n.id} style={{ marginLeft: n.depth * 24, padding: '6px 10px', borderLeft: `3px solid ${CHANGE_COLOR[n.changeType]}`, marginBottom: 4, background: 'var(--navy-light)', borderRadius: 4, fontSize: 13 }}>
                        <span style={S.badge(CHANGE_COLOR[n.changeType])}>{CHANGE_TYPE_SYMBOL[n.changeType]}</span> {n.name}
                        {n.onlyInSide && <span style={{ fontSize: 11, color: 'var(--text-dim)', marginLeft: 8 }}>(only in {n.onlyInSide === 'LEFT' ? comparisonData.context?.leftScenario?.name : comparisonData.context?.rightScenario?.name})</span>}
                      </div>
                    ))}
                  </div>
                )
              })() : comparisonView === 'HEATMAP' ? (() => {
                const availableMetrics = (comparisonData.leftMetrics ?? []).filter((m: any) => (comparisonData.rightMetrics ?? []).some((rm: any) => rm.key === m.key))
                const activeMetric = comparisonHeatmapMetric || availableMetrics[0]?.key || ''
                const hm = activeMetric ? buildHeatmapComparison(comparisonData, activeMetric) : { eligible: false, reason: 'No comparable metric is available on both sides.' }
                return (
                  <div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={S.label}>Metric</label>
                      <select style={{ ...S.input, maxWidth: 200 }} value={activeMetric} onChange={e => setComparisonHeatmapMetric(e.target.value)}>
                        {availableMetrics.map((m: any) => <option key={m.key} value={m.key}>{m.label || m.key}</option>)}
                      </select>
                    </div>
                    {!hm.eligible ? <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40 }}>{hm.reason}</div> : (
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>{['Object', 'Before', 'After', 'Change'].map(h => <th key={h} style={{ padding: '6px 10px', background: 'var(--navy-mid)', textAlign: 'left', fontSize: 11 }}>{h}</th>)}</tr></thead>
                        <tbody>
                          {(hm.cells ?? []).map(c => (
                            <tr key={c.objectId}>
                              <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>{c.name}</td>
                              <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>{String(c.before ?? '—')}</td>
                              <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>{String(c.after ?? '—')}</td>
                              <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>{c.dataType === 'numeric' ? (c.delta !== undefined ? `${c.delta >= 0 ? '+' : ''}${c.delta}` : '—') : (c.transitioned ? `${c.before} → ${c.after}` : 'no change')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )
              })() : comparisonView === 'TREE' ? (() => {
                const t = buildComparisonTree(comparisonData)
                if (!t.eligible) return <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40 }}>{t.reason}</div>
                const renderNode = (n: any): React.ReactNode => (
                  <div key={n.id} style={{ marginLeft: 20 }}>
                    <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={S.badge(CHANGE_COLOR[n.changeType])}>{CHANGE_TYPE_SYMBOL[n.changeType]}</span>
                      <span style={{ fontSize: 13 }}>{n.name}</span>
                      {n.moved && <span style={{ fontSize: 11, color: '#f39c12' }}>(moved)</span>}
                    </div>
                    {n.children.map((c: any) => renderNode(c))}
                  </div>
                )
                return <div>{(t.roots ?? []).map(n => renderNode(n))}</div>
              })() : (() => {
                const cards = buildComparisonCards(comparisonData, comparisonChangeFilter.has('UNCHANGED'))
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                    {cards.map(card => (
                      <div key={card.id} style={{ ...S.card, padding: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <strong style={{ fontSize: 13 }}>{card.name}</strong>
                          <span style={S.badge(CHANGE_COLOR[card.changeType])}>{card.changeType}</span>
                        </div>
                        {card.propertyChanges.length > 0 && (
                          <div style={{ fontSize: 12, marginBottom: 6 }}>
                            {card.propertyChanges.map(p => <div key={p.property}>{p.property}: {String(p.before)} → {String(p.after)}</div>)}
                          </div>
                        )}
                        {card.relationshipChanges.length > 0 && (
                          <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                            {card.relationshipChanges.map((r, i) => <div key={i}>{r.changeType === 'ADDED' ? '+' : '−'} {r.relationship} {r.target}</div>)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )
              })()}
            </div>
          )
        })()}
      </div>
    )
  }

  // ── Authoring (Phase 5C) ────────────────────────────────────────────────
  const renderAuthoring = () => {
    if (!committedScenarioId || !activeScenarioObj) return <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40 }}>Select a scenario to author.</div>
    const locked = isScenarioLocked(activeScenarioObj)
    const needsCurrentConfirm = requiresCurrentEditConfirmation(activeScenarioObj) && !authoringConfirmedCurrent
    const objects = dataset?.objects ?? []
    const relationships = buildRelationshipAuthoringRows(dataset?.relationships ?? [])
    const objectById = new Map<string, any>(objects.map((o: any) => [o.id, o]))
    const nameOf = (id: string) => objectById.get(id)?.name ?? id

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' as const }}>
          <strong style={{ fontSize: 15 }}>{activeScenarioObj.name}</strong>
          <span style={S.badge(activeScenarioObj.type === 'CURRENT' ? '#2ecc71' : '#3498db')}>{activeScenarioObj.type}</span>
          <span style={S.badge(locked ? '#e74c3c' : '#f39c12')}>{activeScenarioObj.status}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {!locked && <button style={S.btn()} onClick={() => setScenarioLifecycleStatus('APPROVED')} disabled={authoringBusy}>Approve</button>}
            {locked && <button style={S.btn('primary')} onClick={() => setScenarioLifecycleStatus('DRAFT')} disabled={authoringBusy}>Revert to Draft</button>}
            <button style={S.btn()} onClick={() => setShowNewScenarioForm(v => !v)}>+ New Scenario</button>
            <button style={{ ...S.btn(), marginLeft: 8 }} onClick={() => setAuthoringMode(false)}>✕ Exit Authoring</button>
          </div>
        </div>

        {showNewScenarioForm && (
          <div style={{ ...S.card, padding: 14, marginBottom: 16 }}>
            <div style={S.grid2}>
              <div><label style={S.label}>Name</label><input style={S.input} value={newScenarioForm.name} onChange={e => setNewScenarioForm(f => ({ ...f, name: e.target.value }))} /></div>
              <div><label style={S.label}>Type</label>
                <select style={S.input} value={newScenarioForm.type} onChange={e => setNewScenarioForm(f => ({ ...f, type: e.target.value as any }))}>
                  {['TRANSITION', 'TARGET', 'BASELINE'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div><label style={S.label}>Parent Scenario</label>
                <select style={S.input} value={newScenarioForm.parentScenarioId} onChange={e => setNewScenarioForm(f => ({ ...f, parentScenarioId: e.target.value }))}>
                  <option value="">Select…</option>
                  {scenarios.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div><label style={S.label}>Horizon Date (optional)</label><input type="date" style={S.input} value={newScenarioForm.horizonDate} onChange={e => setNewScenarioForm(f => ({ ...f, horizonDate: e.target.value }))} /></div>
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
              <button style={S.btn('primary')} disabled={!newScenarioForm.name || !newScenarioForm.parentScenarioId || authoringBusy} onClick={createScenario}>Create</button>
              <button style={S.btn()} onClick={() => setShowNewScenarioForm(false)}>Cancel</button>
            </div>
          </div>
        )}

        {needsCurrentConfirm && (
          <div style={{ ...S.card, padding: 14, marginBottom: 16, border: '1px solid #e74c3c', background: '#e74c3c11' }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#e74c3c' }}>⚠ You are about to edit the Current architecture</div>
            <div style={{ fontSize: 13, marginBottom: 10 }}>Changes made here are inherited by every Transition/Target scenario descended from Current, not just this one. This is different from editing a Target scenario, which stays isolated to that branch.</div>
            <button style={S.btn('primary')} onClick={() => setAuthoringConfirmedCurrent(true)}>I understand, continue editing Current</button>
          </div>
        )}

        {authoringError && <div style={{ fontSize: 12, color: '#e74c3c', marginBottom: 10 }}>⚠ {authoringError}</div>}
        {authoringWarnings.map((w: any, i: number) => <div key={i} style={{ fontSize: 12, color: '#f39c12', marginBottom: 6 }}>ℹ {w.message}</div>)}
        {locked && <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 14 }}>This scenario is {activeScenarioObj.status} and locked. Revert to Draft to make further changes.</div>}

        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Objects</div>
        <div style={{ marginBottom: 16 }}>
          {objects.map((o: any) => {
            const actions = determineAssetActions(true, locked)
            const fields = buildPropertyFieldDisplay(o)
            const isEditing = editingPropertiesFor?.id === o.id
            return (
              <div key={o.id} style={{ ...S.card, padding: 10, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <strong style={{ fontSize: 13, flex: 1 }}>{o.name}</strong>
                  <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{o.semanticType || o.assetType}</span>
                  {actions.canEditProperties && <button style={{ ...S.btn(), fontSize: 11 }} disabled={locked} onClick={() => { setEditingPropertiesFor(o); setPropertyFormValues(Object.fromEntries(fields.map(f => [f.key, String(f.value)]))) }}>✎ Edit</button>}
                  {actions.canRemove && <button style={{ ...S.btn('danger'), fontSize: 11 }} disabled={locked || authoringBusy} onClick={() => removeAssetFromScenario(o.id)}>✕ Remove</button>}
                  <button style={{ ...S.btn(), fontSize: 11 }} disabled={locked || authoringBusy} onClick={() => undoAssetDelta(o.id)} title="Undo all scenario changes to this asset">↩ Undo</button>
                </div>
                {isEditing && (
                  <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                    {fields.map(f => (
                      <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 12, width: 140 }}>{f.key}</span>
                        <input style={{ ...S.input, flex: 1 }} value={propertyFormValues[f.key] ?? ''} onChange={e => setPropertyFormValues(v => ({ ...v, [f.key]: e.target.value }))} />
                        <span style={S.badge(f.provenance === 'scenarioOverride' ? '#f39c12' : '#7f8c8d')}>{f.provenance === 'scenarioOverride' ? 'Overridden here' : f.provenance === 'repository' ? 'Inherited' : '—'}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button style={S.btn('primary')} disabled={authoringBusy} onClick={() => {
                        // Only send fields that actually changed, coerced
                        // back to their original type - propertyFormValues
                        // are all strings (from the text input), but a
                        // numeric/boolean attribute needs its real type or
                        // the backend's own INTEGER/BOOLEAN validation
                        // would incorrectly reject an unchanged, correctly-
                        // typed value that merely round-tripped through a
                        // text input.
                        const changedOverrides: Record<string, any> = {}
                        for (const f of fields) {
                          const raw = propertyFormValues[f.key]
                          if (raw === String(f.value)) continue
                          if (typeof f.value === 'number') changedOverrides[f.key] = Number(raw)
                          else if (typeof f.value === 'boolean') changedOverrides[f.key] = raw === 'true'
                          else changedOverrides[f.key] = raw
                        }
                        if (Object.keys(changedOverrides).length > 0) savePropertyOverrides(o.id, changedOverrides)
                        setEditingPropertiesFor(null)
                      }}>Save</button>
                      <button style={S.btn()} onClick={() => setEditingPropertiesFor(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <button style={{ ...S.btn(), marginBottom: 16 }} disabled={locked} onClick={() => setShowIntroducePicker(v => !v)}>+ Introduce Asset</button>
        {showIntroducePicker && (
          <div style={{ ...S.card, padding: 12, marginBottom: 16 }}>
            <input style={S.input} placeholder="🔍 Search repository assets…" value={introduceSearch} onChange={e => searchRepositoryToIntroduce(e.target.value)} />
            <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto' as const }}>
              {introduceResults.filter((a: any) => !objectById.has(a.id)).map((a: any) => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                  <span style={{ fontSize: 13, flex: 1 }}>{a.name}</span>
                  <button style={{ ...S.btn('primary'), fontSize: 11 }} disabled={!canIntroduce(false, locked) || authoringBusy} onClick={() => introduceAsset(a.id)}>+ Introduce</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {removedAssets.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Removed in this Scenario</div>
            {removedAssets.map((a: any) => (
              <div key={a.id} style={{ ...S.card, padding: 10, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, flex: 1, textDecoration: 'line-through', color: 'var(--text-dim)' }}>{a.name}</span>
                <span style={S.badge('#e74c3c')}>Removed here</span>
                <button style={{ ...S.btn('primary'), fontSize: 11 }} disabled={locked || authoringBusy} onClick={() => restoreAssetInScenario(a.id)}>↺ Restore</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Relationships</div>
        <div style={{ marginBottom: 12 }}>
          {relationships.map(r => (
            <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13 }}>
              <span>{nameOf(r.sourceId)}</span>
              <span style={{ color: 'var(--text-dim)', fontStyle: 'italic', fontSize: 12 }}>{r.label || r.relationshipType}</span>
              <span>{nameOf(r.targetId)}</span>
              <button style={{ ...S.btn('danger'), fontSize: 11, marginLeft: 'auto' }} disabled={locked || authoringBusy} onClick={() => removeScenarioRelationship(r.sourceId, r.targetId, r.relationshipType)}>✕ Remove</button>
            </div>
          ))}
        </div>
        <button style={S.btn()} disabled={locked} onClick={() => setShowAddRelForm(v => !v)}>+ Add Relationship</button>
        {showAddRelForm && (
          <div style={{ ...S.card, padding: 12, marginTop: 8 }}>
            <div style={S.grid3}>
              <div><label style={S.label}>Source</label>
                <select style={S.input} value={addRelForm.sourceId} onChange={e => setAddRelForm(f => ({ ...f, sourceId: e.target.value }))}>
                  <option value="">Select…</option>
                  {objects.map((o: any) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div><label style={S.label}>Target</label>
                <select style={S.input} value={addRelForm.targetId} onChange={e => setAddRelForm(f => ({ ...f, targetId: e.target.value }))}>
                  <option value="">Select…</option>
                  {objects.map((o: any) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div><label style={S.label}>Relationship Type</label><input style={S.input} value={addRelForm.relationshipType} onChange={e => setAddRelForm(f => ({ ...f, relationshipType: e.target.value }))} /></div>
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
              <button style={S.btn('primary')} disabled={!addRelForm.sourceId || !addRelForm.targetId || !addRelForm.relationshipType || authoringBusy} onClick={() => { addScenarioRelationship(addRelForm.sourceId, addRelForm.targetId, addRelForm.relationshipType); setShowAddRelForm(false); setAddRelForm({ sourceId: '', targetId: '', relationshipType: '' }) }}>Add</button>
              <button style={S.btn()} onClick={() => setShowAddRelForm(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── AI Assist (Phase 5D) ────────────────────────────────────────────────
  const renderAiAssist = () => {
    const CLASS_COLOR: Record<string, string> = { FACT: CLAIM_STYLE.FACT.color, INFERENCE: CLAIM_STYLE.INFERENCE.color, GENERAL_GUIDANCE: CLAIM_STYLE.GENERAL_GUIDANCE.color, UNVERIFIED: CLAIM_STYLE.UNVERIFIED.color }
    const objectById = new Map<string, any>((dataset?.objects ?? []).map((o: any) => [o.id, o]))
    const renderClaims = (explanation: any) => (
      <div>
        {(explanation.facts ?? []).length > 0 && (
          <div data-testid="ask-facts" style={{ ...S.card, padding: 10, marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>{t('eaviews.ask_facts')}<HelpTip text={t('eaviews.ask_facts_help')} /></div>
            <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 12 }}>
              {explanation.facts.map((f: any) => <li key={f.key}>{isAR ? f.textAr : f.text}</li>)}
            </ul>
          </div>
        )}
        {explanation.domainPerspective && (
          <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 10, fontStyle: 'italic' }}>Perspective: {explanation.domainPerspective.note}</div>
        )}
        {(explanation.claims ?? []).map((c: any, i: number) => (
          <div key={i} style={{ ...S.card, padding: 10, marginBottom: 8, borderLeft: `3px solid ${CLASS_COLOR[c.classification] || '#7f8c8d'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={S.badge(CLASS_COLOR[c.classification] || '#7f8c8d')}>{CLAIM_STYLE[c.classification as keyof typeof CLAIM_STYLE]?.icon} {CLAIM_STYLE[c.classification as keyof typeof CLAIM_STYLE]?.label || c.classification}</span>
            </div>
            <div style={{ fontSize: 13, marginBottom: (c.evidenceRefs ?? []).length > 0 ? 6 : 0 }}>{c.text}</div>
            {(c.evidenceRefs ?? []).length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const }}>
                {c.evidenceRefs.map((ref: any, ri: number) => {
                  // A cited counted fact shows as the fact itself.
                  if (ref.kind === 'INSIGHT') {
                    const fact = (explanation.facts ?? []).find((f: any) => f.key === ref.id)
                    return <span key={ri} data-testid="cited-fact" style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, border: '1px solid var(--border)', color: 'var(--text-dim)' }}>{fact ? (isAR ? fact.textAr : fact.text) : ref.id}</span>
                  }
                  const resolved = resolveEvidenceRef(ref, dataset)
                  return (
                    <span
                      key={ri}
                      onClick={() => resolved && highlightEvidence(ref)}
                      style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: resolved ? 'var(--navy-light)' : 'transparent', border: '1px solid var(--border)', cursor: resolved ? 'pointer' : 'default', color: resolved ? 'var(--text)' : 'var(--text-dim)', textDecoration: resolved ? 'underline' : 'none' }}
                      title={resolved ? 'Click to highlight' : 'Evidence not available in the current view'}
                    >
                      {resolved?.name || resolved?.key || ref.id}
                    </span>
                  )
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    )

    return (
      <div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' as const }}>
          {(['explain', 'risks', 'gaps', 'question', 'propose'] as const).map(tab => (
            <button key={tab} style={aiTab === tab ? S.btn('primary') : S.btn()} onClick={() => setAiTab(tab)}>
              {tab === 'explain' ? 'Explain' : tab === 'risks' ? 'Risks' : tab === 'gaps' ? 'Gaps' : tab === 'question' ? 'Ask' : 'Propose Changes'}
            </button>
          ))}
          <a data-testid="continue-in-copilot" style={{ ...S.btn(), marginLeft: 'auto', textDecoration: 'none' }}
            href={`/copilot?ask=${encodeURIComponent(`${view.name}${activeScenarioObj?.name ? ` (${activeScenarioObj.name})` : ''}: ${aiTab === 'question' && aiAssistQuestion.trim() ? aiAssistQuestion.trim() : 'explain what this view shows'}`)}`}>
            {t('eaviews.ask_continue')}
          </a>
          <button style={S.btn()} aria-label={t('eaviews.ask_close')} onClick={() => setAiAssistMode(false)}>✕</button>
        </div>

        {/* Self-contained highlight display - the graph renderer's own
            detail panel is scoped to that renderer only, so evidence
            click-to-highlight needs its own display here rather than
            depending on a component this panel never renders. */}
        {selected && (
          <div style={{ ...S.card, padding: 12, marginBottom: 14, border: '1px solid #3498db' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><strong style={{ fontSize: 13 }}>📍 {selected.name}</strong><span style={{ fontSize: 11, color: 'var(--text-dim)', marginLeft: 8 }}>{selected.semanticType || selected.assetType || selected.relationshipType}</span></div>
              <button style={{ ...S.btn(), fontSize: 11 }} onClick={() => setSelected(null)}>✕</button>
            </div>
          </div>
        )}

        {aiTab !== 'propose' ? (
          <div>
            {aiTab === 'question' && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <input style={{ ...S.input, flex: 1 }} placeholder="Ask a question about this architecture…" value={aiAssistQuestion} onChange={e => setAiAssistQuestion(e.target.value)} />
                <button style={S.btn('primary')} disabled={!aiAssistQuestion.trim()} onClick={() => runAiExplain('question')}>Ask</button>
              </div>
            )}
            {aiTab !== 'question' && (
              <button style={{ ...S.btn('primary'), marginBottom: 14 }} onClick={() => runAiExplain(aiTab as any)}>
                {`Generate ${aiTab === 'explain' ? 'Explanation' : aiTab === 'risks' ? 'Risk Analysis' : 'Gap Analysis'}`}
              </button>
            )}
            {aiAssistLoading && <span style={{ fontSize: 12, color: 'var(--text-dim)', marginLeft: 8 }}>⏳ Generating…</span>}
            {aiAssistError && <div style={{ fontSize: 12, color: '#e74c3c', marginBottom: 12 }}>⚠ {aiAssistError}</div>}
            {aiExplanation ? renderClaims(aiExplanation) : !aiAssistLoading && <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40 }}>{aiTab === 'question' ? 'Ask a question to get started.' : 'Click Generate to get started.'}</div>}
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <input style={{ ...S.input, flex: 1 }} placeholder="Describe the change you'd like to propose…" value={aiProposalInstruction} onChange={e => setAiProposalInstruction(e.target.value)} disabled={isScenarioLocked(activeScenarioObj)} />
              <button style={S.btn('primary')} disabled={!aiProposalInstruction.trim() || aiProposalBusy || isScenarioLocked(activeScenarioObj)} onClick={runAiPropose}>{aiProposalBusy ? 'Generating…' : 'Generate Proposal'}</button>
            </div>
            {isScenarioLocked(activeScenarioObj) && <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 12 }}>This scenario is {activeScenarioObj?.status} and locked - revert to Draft in ✎ Author to propose changes.</div>}
            {aiProposalError && <div style={{ fontSize: 12, color: '#e74c3c', marginBottom: 12 }}>⚠ {aiProposalError}</div>}
            {aiProposalNotice && <div style={{ fontSize: 12, color: '#2ecc71', marginBottom: 12 }}>✓ {aiProposalNotice}</div>}

            {aiCurrentProposal && (() => {
              const rows = buildProposedChangeRows(aiCurrentProposal, objectById)
              const canApprove = canApproveProposal(aiCurrentProposal)
              const status = aiCurrentProposal.status
              return (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={S.badge(status === 'APPLIED' ? '#2ecc71' : status === 'REJECTED' || status === 'REJECTED_BY_VALIDATION' ? '#e74c3c' : status === 'APPROVED' ? '#3498db' : '#f39c12')}>{status}</span>
                    {typeof aiCurrentProposal.confidence === 'number' && <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>Confidence: {Math.round(aiCurrentProposal.confidence * 100)}%</span>}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Proposed Changes</div>
                  {rows.map(row => (
                    <div key={row.index} style={{ ...S.card, padding: 10, marginBottom: 8 }}>
                      <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 4 }}>{row.summary}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: row.issues.length > 0 ? 6 : 0 }}>{row.rationale} · {row.evidenceCount} evidence reference{row.evidenceCount === 1 ? '' : 's'}</div>
                      {row.issues.map((iss, i) => <div key={i} style={{ fontSize: 12, color: iss.severity === 'ERROR' ? '#e74c3c' : '#f39c12' }}>{iss.severity === 'ERROR' ? '✕' : '⚠'} {iss.message}</div>)}
                    </div>
                  ))}
                  {(aiCurrentProposal.assumptions ?? []).length > 0 && (
                    <div style={{ fontSize: 12, marginBottom: 8 }}><strong>Assumptions:</strong> {aiCurrentProposal.assumptions.join('; ')}</div>
                  )}
                  {(aiCurrentProposal.missingInformation ?? []).length > 0 && (
                    <div style={{ fontSize: 12, marginBottom: 12, color: '#f39c12' }}><strong>Missing information:</strong> {aiCurrentProposal.missingInformation.join('; ')}</div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={S.btn('primary')} disabled={!canApprove || aiProposalBusy} onClick={approveAiProposal} title={!canApprove && status === 'VALIDATED' ? 'Cannot approve - unresolved ERROR-level issues' : undefined}>Approve</button>
                    <button style={S.btn()} disabled={status === 'APPLIED' || aiProposalBusy} onClick={rejectAiProposal}>Reject</button>
                    <button style={S.btn('primary')} disabled={status !== 'APPROVED' || aiProposalBusy} onClick={executeAiProposal}>{aiProposalBusy ? 'Applying…' : 'Execute'}</button>
                  </div>
                </div>
              )
            })()}
          </div>
        )}
      </div>
    )
  }

  // ── Graph visible subset (Phase 4C) ──────────────────────────────────
  //
  // graphVisibleState (progressive disclosure) -> relationship/object-type
  // filters -> pruneDanglingRelationships, all pure and over the already-
  // fetched dataset. graphEligible/graphIneligibleReason let the render
  // below show the deterministic reason instead of an empty canvas
  // (Section 19).
  const graphEval = eligibility?.eligible?.find((v: any) => v.visualization === 'GRAPH')
  const graphIneligibleReason = eligibility?.ineligible?.find((v: any) => v.visualization === 'GRAPH')?.reasons?.[0]
  const { graphVisibleObjects, graphVisibleEdges, graphHighlight } = (() => {
    if (!dataset || !graphVisibleState) return { graphVisibleObjects: [] as any[], graphVisibleEdges: [] as any[], graphHighlight: null as any }
    const filtered = applyGraphFilters(dataset, graphVisibleState.visibleObjectIds, graphVisibleState.visibleRelationshipIds, {
      relationshipTypes: graphRelTypeFilter.size > 0 ? [...graphRelTypeFilter] : undefined,
      objectTypes: graphObjectTypeFilter.size > 0 ? [...graphObjectTypeFilter] : undefined,
    })
    const prunedRelIds = pruneDanglingRelationships(dataset, filtered.objectIds, filtered.relationshipIds)
    const objs = (dataset.objects ?? []).filter((o: any) => filtered.objectIds.has(o.id))
    const edges = (dataset.relationships ?? []).filter((r: any) => prunedRelIds.has(r.id))
    const highlight = graphHighlightedPathId ? computePathHighlight(dataset, graphHighlightedPathId) : null
    return { graphVisibleObjects: objs, graphVisibleEdges: edges, graphHighlight: highlight }
  })()
  const graphAllRelTypes = [...new Set((dataset?.relationships ?? []).map((r: any) => r.relationshipType))] as string[]
  const graphAllObjectTypes = [...new Set((dataset?.objects ?? []).map((o: any) => o.semanticType || o.assetType))] as string[]

  const renderGraph = () => {
    if (eligibility && !graphEval) {
      return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 280px)', color: 'var(--text-dim)', textAlign: 'center' }}>
        <div style={{ maxWidth: 480 }}>
          <div style={{ fontSize: 28, marginBottom: 12 }}>⬡</div>
          <div>{graphIneligibleReason || 'Graph is not available for this view - it has no meaningful relationships to show.'}</div>
        </div>
      </div>
    }
    const totalObjects = dataset?.objects?.length ?? 0
    return (
    <div ref={graphContainerRef} style={{ display: 'flex', height: isFullscreen ? '100vh' : 'calc(100vh - 280px)', gap: 0, background: isFullscreen ? 'var(--navy)' : 'transparent' }}>
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', background: 'var(--navy)', border: '1px solid var(--border)', borderRadius: 10 }}>
        <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 10, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' as const, maxWidth: 'calc(100% - 200px)' }}>
          <button style={{ ...S.btn(), padding: '3px 10px', fontSize: 12 }} onClick={() => setZoom(z=>Math.min(2.5,z+0.15))}>+</button>
          <span style={{ fontSize: 12, color: 'var(--text-dim)', padding: '3px 6px' }}>{Math.round(zoom*100)}%</span>
          <button style={{ ...S.btn(), padding: '3px 10px', fontSize: 12 }} onClick={() => setZoom(z=>Math.max(0.25,z-0.15))}>−</button>
          <button style={{ ...S.btn(), padding: '3px 10px', fontSize: 12 }} onClick={() => { setZoom(1); setPan({x:0,y:0}) }}>⊡ Fit</button>
          <button style={{ ...S.btn(), padding: '3px 10px', fontSize: 12 }} disabled={layoutRunning} onClick={runAutoLayout}>{layoutRunning ? '⏳ Laying out...' : '🧭 Auto-Layout'}</button>
          <label htmlFor="canvas-layout" style={{ fontSize: 12, color: 'var(--text-dim)', padding: '3px 2px' }}>{t('eaviews.canvas_layout')}</label>
          <select id="canvas-layout" style={{ ...S.input, width: 'auto', padding: '3px 6px', fontSize: 12 }} value={canvasLayout}
            onChange={e => {
              const mode = e.target.value as 'QUESTION' | 'DOMAIN'
              setCanvasLayout(mode)
              setPositions(mode === 'QUESTION' ? layoutByQuestion(filteredNodes, dataset?.objects || [], dataset?.paths || []) : layoutByDomain(filteredNodes))
            }}>
            <option value="QUESTION">{t('eaviews.canvas_by_question')}</option>
            <option value="DOMAIN">{t('eaviews.canvas_by_domain')}</option>
          </select>
          <HelpTip text={t('eaviews.canvas_help')} />
          <button style={{ ...S.btn(), padding: '3px 10px', fontSize: 12 }} onClick={toggleFullscreen}>{isFullscreen ? '⤢ Exit Fullscreen' : '⛶ Fullscreen'}</button>
          {/* Section 12/13: visible-vs-total, distinct from dataset truncation */}
          <span style={{ fontSize: 11, color: 'var(--text-dim)', padding: '3px 6px' }} title="Objects currently shown vs. total in this dataset - expand relationships to explore more.">
            Showing {graphVisibleObjects.length} of {totalObjects} objects
          </span>
          {dataset?.provenance?.truncated && (
            <span style={{ fontSize: 11, color: '#f39c12', padding: '3px 6px' }} title="The underlying architecture result itself was cut off by a query limit - not every matching object exists in this dataset at all.">
              ⚠ Dataset truncated
            </span>
          )}
          <div style={{ position: 'relative' }}>
            <input value={graphSearch} onChange={e => setGraphSearch(e.target.value)} placeholder="🔍 Find node..." style={{ ...S.input, width: 150, padding: '4px 8px', fontSize: 12 }} />
            {graphSearch.trim() && (
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: 'var(--navy-light)', border: '1px solid var(--border)', borderRadius: 8, maxHeight: 200, overflowY: 'auto' as const, width: 220, zIndex: 20 }}>
                {graphSearchMatches.length === 0 && <div style={{ padding: 8, fontSize: 12, color: 'var(--text-dim)' }}>No matches</div>}
                {graphSearchMatches.slice(0, 8).map((n: any) => (
                  <div key={n.id} onClick={() => {
                    // Section 22: reveal the match if progressive disclosure
                    // currently hides it, then focus/center on it.
                    if (graphVisibleState && !graphVisibleState.visibleObjectIds.has(n.id)) setGraphVisibleState({ ...graphVisibleState, visibleObjectIds: new Set([...graphVisibleState.visibleObjectIds, n.id]) })
                    focusOnNode(n); setGraphSearch('')
                  }} style={{ padding: '6px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(3,105,161,0.1)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>{n.name}</div>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* Section 4/5/7/8/23: expand/filter/reset controls */}
        <div style={{ position: 'absolute', top: 46, left: 10, zIndex: 10, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' as const, maxWidth: 'calc(100% - 20px)' }}>
          <button style={{ ...S.btn(), padding: '3px 10px', fontSize: 11 }} onClick={expandNextHop} title="Advance every visible object one hop further along the View's configured path">⇥ Expand next hop</button>
          <button style={{ ...S.btn(), padding: '3px 10px', fontSize: 11 }} onClick={() => { if (graphFocusId) { setGraphVisibleState(computeInitialVisibleSet(graphIndexes, graphFocusId)); setGraphHighlightedPathId(null); setGraphRelTypeFilter(new Set()); setGraphObjectTypeFilter(new Set()) } }}>↺ Reset to focus</button>
          {graphAllRelTypes.length > 1 && (
            <details style={{ position: 'relative' }}>
              <summary style={{ ...S.btn(), padding: '3px 10px', fontSize: 11, display: 'inline-block', cursor: 'pointer', listStyle: 'none' }}>Relationships ▾</summary>
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: 'var(--navy-light)', border: '1px solid var(--border)', borderRadius: 8, padding: 8, zIndex: 20, minWidth: 160 }}>
                {graphAllRelTypes.map(t => (
                  <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '3px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={graphRelTypeFilter.size === 0 || graphRelTypeFilter.has(t)} onChange={() => setGraphRelTypeFilter(prev => { const next = new Set(prev.size === 0 ? graphAllRelTypes : prev); next.has(t) ? next.delete(t) : next.add(t); return next.size === graphAllRelTypes.length ? new Set() : next })} />
                    {t}
                  </label>
                ))}
              </div>
            </details>
          )}
          {graphAllObjectTypes.length > 1 && (
            <details style={{ position: 'relative' }}>
              <summary style={{ ...S.btn(), padding: '3px 10px', fontSize: 11, display: 'inline-block', cursor: 'pointer', listStyle: 'none' }}>Object types ▾</summary>
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: 'var(--navy-light)', border: '1px solid var(--border)', borderRadius: 8, padding: 8, zIndex: 20, minWidth: 160 }}>
                {graphAllObjectTypes.map(t => (
                  <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '3px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={graphObjectTypeFilter.size === 0 || graphObjectTypeFilter.has(t)} onChange={() => setGraphObjectTypeFilter(prev => { const next = new Set(prev.size === 0 ? graphAllObjectTypes : prev); next.has(t) ? next.delete(t) : next.add(t); return next.size === graphAllObjectTypes.length ? new Set() : next })} />
                    {t}
                  </label>
                ))}
              </div>
            </details>
          )}
          {(dataset?.paths?.length ?? 0) > 0 && (
            <select style={{ ...S.input, padding: '3px 8px', fontSize: 11, maxWidth: 180 }} value={graphHighlightedPathId ?? ''} onChange={e => setGraphHighlightedPathId(e.target.value || null)}>
              <option value="">Highlight path…</option>
              {(dataset.paths ?? []).map((p: any) => {
                const names = p.objectIds.map((oid: string) => (dataset.objects ?? []).find((o: any) => o.id === oid)?.name || oid)
                return <option key={p.id} value={p.id}>{names.join(' → ')}</option>
              })}
            </select>
          )}
        </div>
        {loading ? <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100%',color:'var(--text-dim)' }}>Loading view data...</div> : (
          <svg ref={graphSvgRef} style={{ width:'100%', height:'100%', cursor: panStart?'grabbing':dragging?'grabbing':'grab' }}
            onMouseDown={onSvgMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} onWheel={onWheel}>
            <defs><marker id="arrow2" markerWidth="8" markerHeight="8" refX="8" refY="3" orient="auto"><path d="M0,0 L0,6 L8,3 z" fill="rgba(3,105,161,0.4)" /></marker></defs>
            <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
              {graphVisibleEdges.map((e: any) => {
                const isHighlighted = graphHighlight?.relationshipIds.has(e.id)
                const dimmed = graphHighlight && !isHighlighted
                return (
                  <g key={e.id}>
                    <path d={getEdgePath(e)} stroke={isHighlighted ? 'var(--accent)' : 'rgba(3,105,161,0.25)'} strokeWidth={isHighlighted ? 2.5 : 1.5} fill="none" markerEnd="url(#arrow2)" opacity={dimmed ? 0.15 : 1} />
                    {isHighlighted && (() => { const s=positions[e.sourceId]||{x:0,y:0}; const t=positions[e.targetId]||{x:0,y:0}; return <text x={(s.x+t.x)/2+40} y={(s.y+t.y)/2+20} fontSize={9} fill="var(--accent)" textAnchor="middle">{e.label || e.relationshipType}</text> })()}
                  </g>
                )
              })}
              {graphVisibleObjects.map((n: any) => {
                const pos=positions[n.id]||{x:100,y:100}
                const isSel=selected?.id===n.id
                const isFocus = n.id === graphFocusId
                const isMatch = graphSearch.trim() && n.name.toLowerCase().includes(graphSearch.trim().toLowerCase())
                const isExpanded = expandedNodeIds.has(n.id)
                const isHighlighted = graphHighlight?.objectIds.has(n.id)
                const dimmed = graphHighlight && !isHighlighted
                const dc=DOMAIN_COLOR[n.domain]||'#3498db'
                return (
                  <g key={n.id} data-node="true" transform={`translate(${pos.x},${pos.y})`} onMouseDown={e=>onNodeMouseDown(e,n.id)}
                    onDoubleClick={() => isExpanded ? collapseNode(n.id) : expandNode(n.id)} style={{cursor: 'grab', opacity: dimmed ? 0.2 : 1}}>
                    <rect width={160} height={44} rx={8} fill="var(--navy-light)" stroke={isSel||isHighlighted?'var(--accent)':isFocus?'#f39c12':isMatch?'#f39c12':dc+'55'} strokeWidth={isSel||isMatch||isFocus||isHighlighted?2:1.5} />
                    <rect width={5} height={44} rx={2} fill={dc} />
                    <text x={16} y={18} fontSize={11} fontWeight={600} fill="var(--text)">{n.name.length>17?n.name.slice(0,16)+'…':n.name}</text>
                    <text x={16} y={32} fontSize={9} fill="rgba(100,116,139,0.7)">{(n.semanticType || n.assetType)?.replace(/_/g,' ')} · {n.domain}</text>
                    <circle cx={148} cy={10} r={5} fill={HEATMAP_STATUS[n.status]||'#7f8c8d'} />
                    {isExpanded ? <text x={148} y={38} fontSize={9} fill="rgba(100,116,139,0.6)">⊖<title>Double-click to collapse</title></text> : <text x={148} y={38} fontSize={9} fill="rgba(100,116,139,0.6)">⊕<title>Double-click to expand</title></text>}
                  </g>
                )
              })}
            </g>
          </svg>
        )}
        {/* Mini-map */}
        {!loading && graphVisibleObjects.length > 0 && (
          <svg width={140} height={100} style={{ position: 'absolute', bottom: 10, right: 10, background: 'rgba(15,23,42,0.85)', border: '1px solid var(--border)', borderRadius: 8 }}
            viewBox={`${graphBounds.minX} ${graphBounds.minY} ${graphBounds.maxX - graphBounds.minX} ${graphBounds.maxY - graphBounds.minY}`}>
            {graphVisibleObjects.map((n: any) => { const p = positions[n.id]; if (!p) return null; return <rect key={n.id} x={p.x} y={p.y} width={160} height={44} fill={DOMAIN_COLOR[n.domain] || '#3498db'} opacity={0.7} /> })}
          </svg>
        )}
      </div>
      {selected && (() => {
        // Section 16: reuses ViewDataset directly for incoming/outgoing
        // relationships and path memberships - no per-node API request.
        const cardCtx = dataset ? buildCardContext(dataset, selected.id) : null
        const memberPaths = graphIndexes.pathsByObject.get(selected.id) ?? []
        return (
        <div style={{ width: 260, background:'var(--navy-light)', border:'1px solid var(--border)', borderRadius:10, marginLeft:12, padding:16, overflowY:'auto' as const, flexShrink:0 }}>
          <div style={{ fontWeight:700, fontSize:14, marginBottom:12 }}>{selected.name}</div>
          {[{l:t("common.type"),v:(selected.semanticType || selected.assetType)?.replace(/_/g,' ')},{l:'Domain',v:selected.domain},{l:t("common.status"),v:selected.status},{l:'Owner',v:selected.owner||'—'}].map(f=>(
            <div key={f.l} style={{ marginBottom:10 }}><div style={S.label}>{f.l}</div><div style={{fontSize:13}}>{f.v}</div></div>
          ))}
          {selected.description && <><div style={S.label}>Description</div><div style={{fontSize:12,color:'var(--text-dim)',lineHeight:1.6}}>{selected.description}</div></>}
          {cardCtx && cardCtx.summaries.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={S.label}>Relationships ({cardCtx.relationshipCount})</div>
              {cardCtx.summaries.map(s => <div key={s.relationshipType} style={{ fontSize: 12, marginBottom: 4 }}><span style={{ fontStyle: 'italic', color: 'var(--text-dim)' }}>{s.label}:</span> {s.relatedNames.join(', ')}</div>)}
            </div>
          )}
          {memberPaths.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={S.label}>Path membership</div>
              {memberPaths.map((p: any) => <div key={p.id} onClick={() => setGraphHighlightedPathId(p.id)} style={{ fontSize: 11, color: 'var(--accent)', cursor: 'pointer', marginBottom: 2 }}>{p.objectIds.map((oid: string) => (dataset.objects ?? []).find((o: any) => o.id === oid)?.name || oid).join(' → ')}</div>)}
            </div>
          )}
          {selected.tags?.length > 0 && <><div style={{...S.label,marginTop:8}}>Tags</div><div style={{display:'flex',gap:4,flexWrap:'wrap' as const}}>{selected.tags.map((t:string)=><span key={t} style={{...S.badge('#7f8c8d'),fontSize:10}}>{t}</span>)}</div></>}
          <button style={{...S.btn(),marginTop:16,fontSize:12,width:'100%'}} onClick={()=>setSelected(null)}>Close</button>
        </div>
        )
      })()}
    </div>
    )
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
        <button style={{ ...S.btn(), padding:'6px 12px' }} onClick={onBack}>← Back</button>
        <div>
          <div style={{ fontSize:18, fontWeight:700 }}>{view.name}</div>
          {contract?.question && (
            <div data-testid="view-question" style={{ fontSize:13, color:'var(--text-dim)', marginTop:2, display:'flex', alignItems:'center', gap:6 }}>
              {isAR && contract.questionAr ? contract.questionAr : contract.question}
              <HelpTip text={t('eaviews.question_help')} />
            </div>
          )}
          <div style={{ display:'flex', gap:6, marginTop:2 }}>
            <span style={S.badge(CATEGORY_COLOR[view.category]||'#3498db')}>{view.category}</span>
            {isWorkspace
              ? <span data-testid="workspace-badge" style={S.badge('#7f8c8d')} title={t('eaviews.workspace_help')}>{t('eaviews.workspace')}</span>
              : <span style={S.badge(STATUS_COLOR[view.status])}>{view.status}</span>}
            {view.approvalStatus && view.approvalStatus !== 'NOT_REQUIRED' && (
              <span style={S.badge(view.approvalStatus === 'APPROVED' ? '#2ecc71' : view.approvalStatus === 'REJECTED' ? '#e74c3c' : '#f39c12')}>
                {view.approvalStatus === 'PENDING' ? '⏳ Pending Approval' : view.approvalStatus === 'APPROVED' ? '✓ Approved' : '✕ Rejected'}
              </span>
            )}
          </div>
          {/* ── Scenario Selector (Phase 5A) ─────────────────────────────
              Section 3: name/type/status/horizon + lineage context.
              Section 4: pendingScenarioId shows a loading state on the
              badge itself rather than blanking the page - the previously
              committed scenario/dataset stay fully visible underneath
              until the new one actually commits. */}
          <div style={{ marginTop: 6 }}>
            <details style={{ position: 'relative', display: 'inline-block' }}>
              <summary style={{ listStyle: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={S.badge(STATE_COLOR[scenarios.find(s => s.id === committedScenarioId)?.type] || '#7f8c8d')}>
                  {pendingScenarioId ? `Switching to ${scenarios.find(s => s.id === pendingScenarioId)?.name || '…'}…` : (scenarios.find(s => s.id === committedScenarioId)?.name || 'Current Architecture')}
                </span>
                {scenarios.find(s => s.id === committedScenarioId)?.status === 'DRAFT' && <span style={{ ...S.badge('#f39c12'), fontSize: 10 }}>DRAFT</span>}
                {view.scenarioId === committedScenarioId && committedScenarioId && <span title="This is the View's saved default scenario" style={{ fontSize: 11, color: 'var(--text-dim)' }}>★ default</span>}
                <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>▾</span>
              </summary>
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: 'var(--navy-light)', border: '1px solid var(--border)', borderRadius: 8, padding: 10, zIndex: 30, minWidth: 280, maxHeight: 360, overflowY: 'auto' as const }}>
                {stateLines && stateLines.length > 0 ? (
                  <StateMenu lines={stateLines} activeId={committedScenarioId} onPick={(id) => { if (id !== committedScenarioId) switchScenario(id) }} />
                ) : (() => {
                  const tree = buildScenarioLineageTree(scenarios)
                  const renderScenarioOption = (id: string, depth: number): React.ReactNode => {
                    const s = scenarios.find(x => x.id === id)
                    if (!s) return null
                    const children = tree.childrenByParentId[id] ?? []
                    const isActive = id === committedScenarioId
                    return (
                      <div key={id}>
                        <div onClick={() => !isActive && switchScenario(id)}
                          style={{ marginLeft: depth * 14, display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', borderRadius: 6, cursor: isActive ? 'default' : 'pointer', background: isActive ? 'rgba(3,105,161,0.12)' : 'transparent' }}
                          onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(3,105,161,0.06)' }}
                          onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent' }}>
                          <span style={{ fontSize: 12, fontWeight: isActive ? 600 : 400 }}>{s.name}</span>
                          <span style={{ ...S.badge(STATE_COLOR[s.type] || '#7f8c8d'), fontSize: 9 }}>{s.type}</span>
                          {s.status === 'DRAFT' && <span style={{ ...S.badge('#f39c12'), fontSize: 9 }}>DRAFT</span>}
                          {s.horizonDate && <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>{new Date(s.horizonDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short' })}</span>}
                          {view.scenarioId === s.id && <span style={{ fontSize: 10, color: 'var(--text-dim)' }} title="View's saved default">★</span>}
                        </div>
                        {children.map(c => renderScenarioOption(c, depth + 1))}
                      </div>
                    )
                  }
                  return tree.rootIds.map(id => renderScenarioOption(id, 0))
                })()}
                {scenarios.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: 8 }}>No architecture scenarios available.</div>}
                {committedScenarioId && view.scenarioId !== committedScenarioId && (
                  <button disabled={savingDefaultScenario} onClick={() => setAsDefaultScenario(committedScenarioId)} style={{ ...S.btn(), marginTop: 8, width: '100%', fontSize: 11 }}>
                    {savingDefaultScenario ? 'Saving…' : '★ Set as default for this View'}
                  </button>
                )}
              </div>
            </details>
            {/* Section 11: lightweight lineage context, e.g. "Current -> Transition 2027 -> Target A" */}
            {committedScenarioId && scenarios.length > 0 && (() => {
              const path = getScenarioLineagePath(scenarios, committedScenarioId)
              return path.length > 1 ? <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>{path.map((s: any) => s.name).join(' → ')}</div> : null
            })()}
            {scenarioSwitchError && <div style={{ fontSize: 11, color: '#e74c3c', marginTop: 4 }}>⚠ {scenarioSwitchError}</div>}
            {vizAutoSwitchNotice && <div style={{ fontSize: 11, color: '#f39c12', marginTop: 4 }} onClick={() => setVizAutoSwitchNotice(null)}>ℹ {vizAutoSwitchNotice} <span style={{ cursor: 'pointer', textDecoration: 'underline' }}>Dismiss</span></div>}
          </div>
        </div>
        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          {!isRoadmap && !isDashboard && (<>
            <button style={comparisonMode ? S.btn('primary') : S.btn()} onClick={() => { setComparisonMode(m => !m); setAuthoringMode(false); setAiAssistMode(false) }}>{t('eaviews.tb_compare')}</button>
            <button style={aiAssistMode ? S.btn('primary') : S.btn()} onClick={() => { setAiAssistMode(m => !m); setComparisonMode(false); setAuthoringMode(false) }}>{t('eaviews.tb_ask')}</button>
            {/* Editing a state is for people who author change. */}
            {canAuthorState && (
              <button style={authoringMode ? S.btn('primary') : S.btn()} onClick={() => { setAuthoringMode(m => !m); setComparisonMode(false); setAiAssistMode(false) }}>{t('eaviews.tb_edit_state')}</button>
            )}
          </>)}
          {isWorkspace && can('Views.Create') && (
            <button style={{ ...S.btn('primary'), fontSize:12 }} onClick={keepView}>{t('eaviews.keep')}</button>
          )}
          <HelpTip text={t('eaviews.tb_help')} />
          <details style={{ position:'relative' }}>
            <summary style={{ ...S.btn(), fontSize:12, listStyle:'none' }}>{t('eaviews.tb_more')}</summary>
            <div style={{ position:'absolute', top:'100%', insetInlineEnd:0, marginTop:4, background:'var(--navy-light)', border:'1px solid var(--border)', borderRadius:8, zIndex:20, minWidth:150, padding:4, display:'flex', flexDirection:'column' as const, gap:2 }}>
              <button style={{ ...S.btn(), fontSize:12, textAlign:'start' as const }} onClick={isRoadmap ? loadRoadmap : isDashboard ? loadDashboard : load}>{t('eaviews.tb_refresh')}</button>
              {canSnapshot && <button style={{ ...S.btn(), fontSize:12, textAlign:'start' as const }} onClick={takeSnapshot}>{t('eaviews.tb_snapshot')}</button>}
              <button style={{ ...S.btn(), fontSize:12, textAlign:'start' as const }} onClick={openVersionHistory}>{t('eaviews.tb_history')}</button>
            </div>
          </details>
          <div style={{ position:'relative' }}>
            <button style={{ ...S.btn(), fontSize:12 }} disabled={!!exportingFormat} onClick={() => setShowExportMenu(v => !v)}>{exportingFormat ? `⏳ Exporting ${exportingFormat.toUpperCase()}...` : '⬇ Export'}</button>
            {showExportMenu && (() => {
              const isGraphMode = vizMode === 'GRAPH' && !isRoadmap && !isDashboard
              const itemStyle = { display:'block' as const, width:'100%', textAlign:'left' as const, padding:'8px 12px', fontSize:12, background:'none', border:'none', color:'var(--text)', cursor:'pointer' }
              const hoverIn = (e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.background = 'rgba(3,105,161,0.1)')
              const hoverOut = (e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.background = 'none')
              return (
                <div style={{ position:'absolute', top:'100%', right:0, marginTop:4, background:'var(--navy-light)', border:'1px solid var(--border)', borderRadius:8, zIndex:20, minWidth:130, overflow:'hidden' }}>
                  <button style={itemStyle} onClick={() => runExport('json')} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>JSON</button>
                  {!isDashboard && <button style={itemStyle} onClick={() => runExport('csv')} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>CSV</button>}
                  {isGraphMode && <button style={itemStyle} onClick={() => runExport('svg')} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>SVG</button>}
                  {isGraphMode && <button style={itemStyle} onClick={() => runExport('png')} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>PNG</button>}
                  {!isDashboard && <button style={itemStyle} onClick={() => runExport('pdf')} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>PDF</button>}
                  {!isDashboard && <button style={itemStyle} onClick={() => runExport('pptx')} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>PPTX</button>}
                </div>
              )
            })()}
          </div>
          <button style={{ ...S.btn(), fontSize:12 }} onClick={shareView}>🔗 Share</button>
          {isDashboard && <button style={{ ...S.btn(), fontSize:12 }} onClick={() => setEditingDashboard(true)}>⚙ Edit Widgets</button>}
          {view.status === 'DRAFT' && view.approvalStatus === 'PENDING' && (
            <>
              <button style={{ ...S.btn(), fontSize:12, color:'#2ecc71' }} onClick={approveView}>✓ Approve</button>
              <button style={{ ...S.btn(), fontSize:12, color:'#e74c3c' }} onClick={rejectView}>✕ Reject</button>
            </>
          )}
          {view.status === 'DRAFT' && view.approvalStatus !== 'PENDING' && (
            <button style={{ ...S.btn(), fontSize:12 }} onClick={requestApproval}>📝 Request Approval</button>
          )}
          {view.status === 'DRAFT' && <button style={{ ...S.btn('primary'), fontSize:12 }} onClick={publish}>🚀 Publish</button>}
        </div>
      </div>

      {(() => {
        // Facts about the result itself: a size limit was reached, or the
        // view's scope leaves some objects out. Never hidden.
        const notices = [
          ...(dataset?.warnings ?? []).filter((w: any) => w.code === 'TRUNCATED' || w.code === 'SCOPE_EXCLUSION'),
          // Non-blocking Meta Model changes are shown here; blocking ones
          // replace the result (see renderResultState).
          ...(semanticReview?.status === 'REVIEW_REQUIRED' && !semanticReview.blocking
            ? (semanticReview.issues || []).map((i: any) => ({ code: 'REVIEW', message: `${t('eaviews.review_required')}: ${i.message}` })) : []),
        ]
        if (notices.length === 0) return null
        return (
          <div role="status" data-testid="result-notices" style={{ ...S.card, marginBottom:16, padding:'10px 14px', display:'flex', flexDirection:'column' as const, gap:4 }}>
            <div style={{ fontSize:12, fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
              {t('eaviews.result_notes')}
              <HelpTip text={t('eaviews.result_notes_help')} />
            </div>
            {notices.map((w: any, i: number) => (
              <div key={`${w.code}-${i}`} style={{ fontSize:12, color:'var(--text-dim)' }}>{w.message}</div>
            ))}
          </div>
        )
      })()}

      {showVersionHistory && (
        <div style={{ ...S.card, marginBottom:16, borderColor:'var(--accent)' }}>
          <div style={{ display:'flex', alignItems:'center', marginBottom:12 }}>
            <div style={{ fontWeight:600 }}>🕐 Version History</div>
            <button style={{ ...S.btn(), fontSize:11, marginLeft:'auto' }} onClick={() => setShowVersionHistory(false)}>Close</button>
          </div>
          {versionsLoading ? <div style={{ color:'var(--text-dim)', textAlign:'center', padding:20 }}>Loading...</div>
            : versions.length === 0 ? <div style={{ color:'var(--text-dim)', textAlign:'center', padding:20 }}>No version history yet.</div>
            : (
              <div style={{ display:'flex', flexDirection:'column' as const, gap:6 }}>
                {versions.map((v: any) => (
                  <div key={v.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 10px', borderRadius:6, background:'var(--navy-mid)' }}>
                    <span style={S.badge('#3498db')}>v{v.versionNumber}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13 }}>{v.changeReason || '(no reason given)'}</div>
                      <div style={{ fontSize:11, color:'var(--text-dim)' }}>{new Date(v.createdAt).toLocaleString()} · {v.status}</div>
                    </div>
                    {v.versionNumber !== view.currentVersionNumber && (
                      <button style={{ ...S.btn(), fontSize:11 }} onClick={() => restoreVersion(v.id)}>Restore</button>
                    )}
                  </div>
                ))}
              </div>
            )}
        </div>
      )}

      {isDashboard ? (
        editingDashboard ? (
          <DashboardBuilder widgets={dashboardWidgets} onSave={saveDashboardWidgets} onCancel={() => setEditingDashboard(false)} />
        ) : dashboardLoading ? <div style={{ color:'var(--text-dim)', textAlign:'center', padding:60 }}>Loading dashboard...</div>
        : (
          <DashboardGrid widgets={dashboardWidgets} results={dashboardResults} onEdit={() => setEditingDashboard(true)} />
        )
      ) : isRoadmap ? (
        roadmapLoading ? <div style={{ color:'var(--text-dim)', textAlign:'center', padding:60 }}>Loading roadmap...</div>
        : roadmapNeedsConfig ? (
          <RoadmapConfigPanel api={api} assetType={view.rootObjectTypes?.[0] || ''} initial={view.roadmapConfig || {}} onSave={saveRoadmapConfig} onCancel={onBack} />
        ) : (
          <RoadmapTimeline items={roadmapItems} onSelect={(item) => setSelected({ ...item, assetType: item.assetType, tags: [] })} />
        )
      ) : (
      <>
      {!comparisonMode && !authoringMode && !aiAssistMode && (<>
      {/* Viz mode selector */}
      <div style={{ display:'flex', gap:2, background:'var(--navy-light)', borderRadius:8, padding:3, marginBottom:16, width:'fit-content' }}>
        {(presentation?.available?.length ? presentation.available.filter((m: string) => RENDERABLE_MODES.includes(m)) : LEGACY_MODES).map((m: string) => (
          <button key={m} style={{ ...S.btn(), padding:'5px 12px', fontSize:12, background:vizMode===m?'var(--accent)':'none', color:vizMode===m?'var(--navy)':'var(--text-dim)' }} onClick={()=>setVizMode(m)}>
            {VIZ_ICONS[m]} {m.replace(/_/g,' ')}
          </button>
        ))}
      </div>

      <InsightsStrip insights={insights} stateChange={stateChange} focusKey={focusInsight?.key ?? null} onFocus={setFocusInsight} />
      {focusInsight && (
        <div role="status" data-testid="focus-chip" style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, marginBottom:12 }}>
          <span>{t('eaviews.focus_active')}: {isAR ? focusInsight.textAr : focusInsight.text}</span>
          <button style={{ ...S.btn(), fontSize:11 }} onClick={() => setFocusInsight(null)}>{t('eaviews.focus_clear')}</button>
        </div>
      )}

      {resultState?.state === 'NO_RELATIONSHIPS' && (
        <div role="status" data-testid="result-state-note" style={{ fontSize:12, color:'var(--text-dim)', marginBottom:12 }}>
          <strong>{t('eaviews.result_no_relationships')}:</strong> {resultState.message}
        </div>
      )}

      </>)}

      {/* Share panel */}
      {showSharePanel && shareData && (
        <div style={{ ...S.card, marginBottom:16, borderColor:'var(--accent)' }}>
          <div style={{ fontWeight:600, marginBottom:8 }}>🔗 Share Link Generated</div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <input style={{ ...S.input, fontFamily:'monospace', fontSize:12 }} readOnly value={`${window.location.origin}/shared/${shareData.token}`} />
            <button style={{ ...S.btn('primary'), whiteSpace:'nowrap' as const }} onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/shared/${shareData.token}`); alert('Copied!') }}>Copy</button>
          </div>
          <div style={{ fontSize:12, color:'var(--text-dim)', marginTop:8 }}>Expires: {shareData.expiry ? new Date(shareData.expiry).toLocaleDateString() : 'Never'}</div>
          <button style={{ ...S.btn(), marginTop:8, fontSize:12 }} onClick={()=>setShowSharePanel(false)}>Close</button>
        </div>
      )}

      {/* Visualization */}
      {loading ? <div style={{ color:'var(--text-dim)', textAlign:'center', padding:60 }}>Loading view data...</div>
        : comparisonMode ? renderComparison()
        : authoringMode ? renderAuthoring()
        : aiAssistMode ? renderAiAssist()
        : resultEmpty ? renderResultState()
        : vizMode === 'GRAPH' ? renderGraph()
        : vizMode === 'CAPABILITY_MAP' ? renderCapabilityMap()
        : vizMode === 'HEATMAP' ? renderHeatmap()
        : vizMode === 'MATRIX' ? renderMatrix()
        : vizMode === 'TREE' ? renderTree()
        : vizMode === 'CARDS' ? renderCards()
        : vizMode === 'LANDSCAPE' ? renderLandscape()
        : renderTable()}
      </>
      )}
    </div>
  )
}

// ── View Builder ──────────────────────────────────────────────────────────────
// The view builder is a guided interface over the tenant's published Meta
// Model (see eaviews/SemanticViewBuilder).
function ViewBuilder({ api, viewpoint, onCreated, onCancel }: { api: any, viewpoint: any, onCreated: (v: any) => void, onCancel: () => void }) {
  return <SemanticViewBuilder api={api} viewpoint={viewpoint} onCreated={onCreated} onCancel={onCancel} />
}

// ── Snapshots ─────────────────────────────────────────────────────────────────
function SnapshotsPanel({ api }: { api: any }) {
  const [views, setViews] = useState<any[]>([])
  const [snapshots, setSnapshots] = useState<any[]>([])
  const [selectedView, setSelectedView] = useState<string>('')
  const [loading, setLoading] = useState(false)

  useEffect(() => { api.get('/ea-views').then((d: any) => setViews(Array.isArray(d)?d:[])) }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!selectedView) return
    setLoading(true)
    api.get(`/ea-views/${selectedView}/snapshots`).then((d: any) => { setSnapshots(Array.isArray(d)?d:[]); setLoading(false) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView])

  return (
    <div>
      <div style={{ fontSize:18, fontWeight:700, marginBottom:6 }}>Snapshots</div>
      <div style={{ fontSize:13, color:'var(--text-dim)', marginBottom:20 }}>Immutable point-in-time captures of view data for audit and comparison</div>
      <div style={{ marginBottom:16 }}>
        <label style={S.label}>Select View</label>
        <select style={{ ...S.input, maxWidth:300 }} value={selectedView} onChange={e=>setSelectedView(e.target.value)}>
          <option value="">Choose a view...</option>
          {views.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      </div>
      {loading ? <div style={{ color:'var(--text-dim)', textAlign:'center', padding:40 }}>Loading...</div>
        : snapshots.length === 0 && selectedView ? <div style={{ ...S.card, textAlign:'center', color:'var(--text-dim)', padding:40 }}>No snapshots for this view yet. Open the view and click 📸 Snapshot.</div>
        : snapshots.map(s => (
          <div key={s.id} style={{ ...S.card, marginBottom:8, display:'flex', alignItems:'center', gap:16 }}>
            <div style={{ width:40, height:40, borderRadius:8, background:'rgba(3,105,161,0.12)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>📸</div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:600 }}>{s.name}</div>
              <div style={{ fontSize:12, color:'var(--text-dim)' }}>{s.objectCount} objects · {s.relationshipCount} relationships · {new Date(s.takenAt).toLocaleString()}</div>
            </div>
            <span style={S.badge(STATE_COLOR[s.architectureState]||'#7f8c8d')}>{s.architectureState}</span>
          </div>
        ))
      }
    </div>
  )
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function ViewsDashboard({ api, stats, onTab, onOpenView }: { api: any, stats: any, onTab: (t:string)=>void, onOpenView: (v:any)=>void }) {
  const [recent, setRecent] = useState<any[]>([])
  const [favorites, setFavorites] = useState<any[]>([])

  useEffect(() => {
    api.get('/ea-views').then((d: any) => {
      const views = Array.isArray(d) ? d : []
      setRecent(views.sort((a: any,b: any) => new Date(b.updatedAt).getTime()-new Date(a.updatedAt).getTime()).slice(0,4))
      setFavorites(views.filter((v: any) => v.isFavorite).slice(0,4))
    })
  }, [api])

  const repo = stats?.repositoryObjects || {}
  return (
    <div style={{ display:'flex', flexDirection:'column' as const, gap:20 }}>
      {/* Stats */}
      <div style={S.grid3}>
        {[
          { icon:'📊', label:'Total Views', value:stats?.total||0, color:'var(--accent)', tab:'my-views' },
          { icon:'🚀', label:'Published', value:stats?.published||0, color:'#2ecc71', tab:'my-views' },
          { icon:'⭐', label:'Favorites', value:stats?.favorites||0, color:'#f39c12', tab:'my-views' },
          { icon:'⬛', label:'Capabilities', value:repo.capabilities||0, color:'#3498db', tab:'' },
          { icon:'💻', label:'Applications', value:repo.applications||0, color:'#e67e22', tab:'' },
          { icon:'🗄', label:'Data Entities', value:repo.dataEntities||0, color:'#1abc9c', tab:'' },
        ].map(s=>(
          <div key={s.label} style={{ ...S.statCard, cursor:s.tab?'pointer':'default' }} onClick={()=>s.tab&&onTab(s.tab)}>
            <div style={{ display:'flex', gap:10, alignItems:'center' }}><span style={{fontSize:20}}>{s.icon}</span><span style={{fontSize:11,color:'var(--text-dim)',fontWeight:600}}>{s.label}</span></div>
            <div style={{ fontSize:28, fontWeight:700, color:s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Quick actions */}
      <div style={S.card}>
        <div style={{ fontSize:13, fontWeight:600, color:'var(--text-dim)', marginBottom:12 }}>QUICK ACTIONS</div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' as const }}>
          <button style={S.btn('primary')} onClick={()=>onTab('library')}>📚 Browse View Library</button>
          <button style={S.btn()} onClick={()=>onTab('builder')}>+ Custom View</button>
          <button style={S.btn()} onClick={()=>onTab('my-views')}>📋 My Views</button>
          <button style={S.btn()} onClick={()=>onTab('snapshots')}>📸 Snapshots</button>
        </div>
      </div>

      {/* Favorites */}
      {favorites.length > 0 && (
        <div>
          <div style={{ fontSize:15, fontWeight:600, marginBottom:12 }}>⭐ Favorites</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            {favorites.map(v=>(
              <div key={v.id} onClick={()=>onOpenView(v)} style={{ ...S.card, cursor:'pointer', display:'flex', gap:12, alignItems:'center', padding:'12px 16px' }}
                onMouseEnter={e=>(e.currentTarget.style.borderColor='var(--accent)')}
                onMouseLeave={e=>(e.currentTarget.style.borderColor='var(--border)')}>
                <div style={{ fontSize:22 }}>{VIZ_ICONS[v.visualization]||'📊'}</div>
                <div><div style={{ fontWeight:600 }}>{v.name}</div><div style={{ fontSize:11, color:'var(--text-dim)' }}>{v.category} · {v.architectureState}</div></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent */}
      {recent.length > 0 && (
        <div>
          <div style={{ fontSize:15, fontWeight:600, marginBottom:12 }}>🕐 Recently Updated</div>
          <div style={{ display:'flex', flexDirection:'column' as const, gap:6 }}>
            {recent.map(v=>(
              <div key={v.id} onClick={()=>onOpenView(v)} style={{ ...S.card, cursor:'pointer', display:'flex', gap:12, alignItems:'center', padding:'10px 16px' }}
                onMouseEnter={e=>(e.currentTarget.style.borderColor='var(--accent)')}
                onMouseLeave={e=>(e.currentTarget.style.borderColor='var(--border)')}>
                <div style={{ fontSize:18 }}>{VIZ_ICONS[v.visualization]||'📊'}</div>
                <div style={{ flex:1 }}><div style={{ fontWeight:500 }}>{v.name}</div><div style={{ fontSize:11, color:'var(--text-dim)' }}>{v.category} · {new Date(v.updatedAt).toLocaleDateString()}</div></div>
                <span style={S.badge(STATUS_COLOR[v.status])}>{v.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
// ── Object Context Viewer ("Open in View" from a repository asset) ────────
//
// Deliberately NOT reusing ViewViewer - that component's snapshot/share/
// publish/edit machinery is all saved-view concepts that don't apply to
// an ad-hoc "show me this object's dependencies" query. This is a smaller,
// standalone viewer: fetch, force-layout, pan/zoom/click - the exploration
// primitives, without the full Studio chrome around them.
// EA Repository Production Readiness, item 9: direct Dependency
// Explorer entry point - search for/select an architecture object,
// then hand off entirely to ObjectContextViewer (no second graph
// engine, reuses the exact same component the Repository's own
// "Explore Dependencies" button opens).
function DependencyExplorerEntry({ api, onBack }: { api: any; onBack: () => void }) {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [results, setResults] = useState<any[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    if (!debouncedQuery.trim()) { setResults([]); return }
    api.get(`/ea-repository/assets?search=${encodeURIComponent(debouncedQuery)}&pageSize=15&page=1`)
      .then((r: any) => setResults(Array.isArray(r) ? r : (r.items || [])))
      .catch(() => setResults([]))
  }, [debouncedQuery, api])

  if (selectedId) {
    return <ObjectContextViewer api={api} assetId={selectedId} onBack={() => setSelectedId(null)} />
  }

  return (
    <div style={S.page}>
      <div className="flex items-center justify-between mb-4">
        <div style={{ fontSize: 18, fontWeight: 600 }}>🕸 Dependency Explorer</div>
        <button style={S.btn('secondary')} onClick={onBack}>← Back</button>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 12 }}>Search for an architecture object to view its dependencies (1-hop by default, expandable).</div>
      <input className="form-input" style={{ width: '100%', maxWidth: 480 }} placeholder="Search by name..." value={query} onChange={e => setQuery(e.target.value)} autoFocus />
      {results.length > 0 && (
        <div style={{ marginTop: 12, maxWidth: 480 }}>
          {results.map((r: any) => (
            <div key={r.id} style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => setSelectedId(r.id)}>
              <div style={{ fontSize: 13 }}>{r.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{r.canonicalDisplayLabel || r.assetType}</div>
            </div>
          ))}
        </div>
      )}
      {debouncedQuery.trim() && results.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 12 }}>No matching objects found.</div>
      )}
    </div>
  )
}

function ObjectContextViewer({ api, assetId, onBack }: { api: any; assetId: string; onBack: () => void }) {
  const { t } = useLang()
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [depth, setDepth] = useState(1) // EA Repository Production Readiness: default changed from 2 to 1 hop
  const [direction, setDirection] = useState<'BOTH'|'UPSTREAM'|'DOWNSTREAM'>('BOTH')
  const [relTypeFilter, setRelTypeFilter] = useState<Set<string>>(new Set())
  const [positions, setPositions] = useState<Record<string,{x:number,y:number}>>({})
  const [selected, setSelected] = useState<any>(null)
  const [dragging, setDragging] = useState<{id:string,ox:number,oy:number}|null>(null)
  const [pan, setPan] = useState({x:0,y:0})
  const [panStart, setPanStart] = useState<{mx:number,my:number,px:number,py:number}|null>(null)
  const [zoom, setZoom] = useState(1)
  const [pathTargetSearch, setPathTargetSearch] = useState('')
  const [pathTargetOptions, setPathTargetOptions] = useState<any[]>([])
  const [pathResult, setPathResult] = useState<any>(null)
  const [pathSearching, setPathSearching] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    const params = new URLSearchParams({ depth: String(depth), direction })
    if (relTypeFilter.size > 0) params.set('relationshipTypes', [...relTypeFilter].join(','))
    api.get(`/ea-views/object-context/${assetId}?${params.toString()}`).then((d: any) => {
      setData(d)
      if (d?.nodes?.length) setPositions(computeForceLayout(d.nodes, d.edges || [], {}))
      setLoading(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, depth, direction, relTypeFilter])

  useEffect(() => { load() }, [load])

  const onNodeMouseDown = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    const p = positions[id]||{x:100,y:100}
    setDragging({id, ox:e.clientX-p.x, oy:e.clientY-p.y})
    setSelected(data?.nodes?.find((n: any) => n.id===id)||null)
  }
  const onSvgMouseDown = (e: React.MouseEvent) => {
    if ((e.target as Element).closest('[data-node]')) return
    setPanStart({mx:e.clientX,my:e.clientY,px:pan.x,py:pan.y})
    setSelected(null)
  }
  const onMouseMove = (e: React.MouseEvent) => {
    if (dragging) setPositions(p=>({...p,[dragging.id]:{x:(e.clientX-dragging.ox)/zoom,y:(e.clientY-dragging.oy)/zoom}}))
    else if (panStart) setPan({x:panStart.px+e.clientX-panStart.mx,y:panStart.py+e.clientY-panStart.my})
  }
  const onMouseUp = () => { setDragging(null); setPanStart(null) }
  const getEdgePath = (e: any) => {
    const s=positions[e.sourceId]||{x:0,y:0}; const t=positions[e.targetId]||{x:0,y:0}
    return `M ${s.x+80} ${s.y+20} Q ${(s.x+80+t.x)/2} ${(s.y+20+t.y+20)/2} ${t.x} ${t.y+20}`
  }

  const toggleRelType = (type: string) => setRelTypeFilter(prev => { const next = new Set(prev); next.has(type) ? next.delete(type) : next.add(type); return next })
  // Populated from the full-graph relationship types actually observed
  // once (not re-derived from the currently-filtered result, which would
  // shrink the checklist itself as filters get applied) - a simple
  // client-side cache of the type names seen the first time this
  // assetId+depth+direction combination loaded with no filter.
  const [knownRelTypes, setKnownRelTypes] = useState<string[]>([])
  useEffect(() => {
    if (relTypeFilter.size === 0 && data?.edges?.length) {
      setKnownRelTypes(prev => [...new Set([...prev, ...data.edges.map((e: any) => e.relationshipType)])])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const searchPathTargets = async (q: string) => {
    setPathTargetSearch(q)
    if (q.trim().length < 2) { setPathTargetOptions([]); return }
    // Searches the full repository, not just objects already loaded in
    // this exploration - a genuinely useful dependency-path question is
    // often "is there a path between this and something I haven't
    // navigated to yet", not just between two objects already on screen.
    const results = await api.get(`/ea-repository/assets?search=${encodeURIComponent(q.trim())}`)
    setPathTargetOptions((Array.isArray(results) ? results : []).filter((n: any) => n.id !== assetId).slice(0, 8))
  }
  const findPathTo = async (targetId: string) => {
    setPathSearching(true)
    setPathTargetOptions([])
    try {
      const result = await api.get(`/ea-views/shortest-path?from=${assetId}&to=${targetId}`)
      setPathResult(result)
    } finally {
      setPathSearching(false)
    }
  }

  const rootNode = (data?.nodes || []).find((n: any) => n.id === assetId)

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
        <button style={{ ...S.btn(), padding:'6px 12px' }} onClick={onBack}>← Back</button>
        <div>
          <div style={{ fontSize:18, fontWeight:700 }}>{rootNode ? `Dependencies of ${rootNode.name}` : 'Object Context'}</div>
          <div style={{ fontSize:12, color:'var(--text-dim)' }}>Ad-hoc exploration - not a saved view</div>
        </div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ display:'flex', gap:2, background:'var(--navy-light)', borderRadius:8, padding:2 }}>
            {(['BOTH','UPSTREAM','DOWNSTREAM'] as const).map(d => (
              <button key={d} style={{ ...S.btn(), padding:'4px 10px', fontSize:11, background:direction===d?'var(--accent)':'none', color:direction===d?'var(--navy)':'var(--text-dim)' }} onClick={()=>setDirection(d)} title={d==='UPSTREAM'?'What feeds into this object':d==='DOWNSTREAM'?'What this object feeds into':'Both directions'}>
                {d==='UPSTREAM'?'⬅ Upstream':d==='DOWNSTREAM'?'Downstream ➡':'↔ Both'}
              </button>
            ))}
          </div>
          <label style={{ fontSize:12, color:'var(--text-dim)' }}>Depth:</label>
          <select style={{ ...S.input, width:70 }} value={depth} onChange={e => setDepth(parseInt(e.target.value, 10))}>
            {[1,2,3,4].map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <button style={{ ...S.btn(), fontSize:12 }} onClick={load}>↻ Refresh</button>
        </div>
      </div>
      {knownRelTypes.length > 0 && (
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, flexWrap:'wrap' as const }}>
          <span style={{ fontSize:11, color:'var(--text-dim)' }}>Filter relationship types:</span>
          {knownRelTypes.map(t => (
            <span key={t} onClick={()=>toggleRelType(t)} style={{ ...S.badge(relTypeFilter.has(t)?'var(--accent)':'#7f8c8d'), cursor:'pointer', opacity:relTypeFilter.size===0||relTypeFilter.has(t)?1:0.5 }}>{t}</span>
          ))}
          {relTypeFilter.size > 0 && <button style={{ ...S.btn(), padding:'2px 8px', fontSize:10 }} onClick={()=>setRelTypeFilter(new Set())}>Clear</button>}
        </div>
      )}
      <div style={{ ...S.card, marginBottom:16, padding:12 }}>
        <div style={{ fontSize:12, fontWeight:600, marginBottom:8 }}>🔍 Find shortest path to another object</div>
        <div style={{ position:'relative', maxWidth:320 }}>
          <input style={S.input} value={pathTargetSearch} onChange={e => searchPathTargets(e.target.value)} placeholder="Search for an object by name..." />
          {pathTargetOptions.length > 0 && (
            <div style={{ position:'absolute', top:'100%', left:0, marginTop:4, background:'var(--navy-light)', border:'1px solid var(--border)', borderRadius:8, width:'100%', zIndex:20, maxHeight:200, overflowY:'auto' as const }}>
              {pathTargetOptions.map((n: any) => (
                <div key={n.id} onClick={() => { setPathTargetSearch(n.name); findPathTo(n.id) }} style={{ padding:'6px 10px', fontSize:12, cursor:'pointer', borderBottom:'1px solid var(--border)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(3,105,161,0.1)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>{n.name}</div>
              ))}
            </div>
          )}
        </div>
        {pathSearching && <div style={{ fontSize:12, color:'var(--text-dim)', marginTop:8 }}>Searching...</div>}
        {pathResult && !pathSearching && (
          pathResult.found ? (
            <div style={{ marginTop:10, fontSize:12 }}>
              <strong>Path found</strong> ({pathResult.nodes.length} objects, {pathResult.edges.length} hops):
              <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' as const, marginTop:6 }}>
                {pathResult.nodes.map((n: any, i: number) => (
                  <React.Fragment key={n.id}>
                    {i > 0 && <span style={{ color:'var(--text-dim)' }}>→ {pathResult.edges[i-1]?.label} →</span>}
                    <span style={S.badge(TYPE_COLOR[n.assetType]||'var(--accent)')}>{n.name}</span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ marginTop:10, fontSize:12, color:'var(--text-dim)' }}>No connecting path found between these two objects.</div>
          )
        )}
      </div>
      {data?.truncated && (
        <div style={{ ...S.card, borderColor:'#f39c12', marginBottom:16, fontSize:12 }}>⚠ This object has more connections than can be shown at once - results were truncated. Try a lower depth.</div>
      )}
      <div style={{ display: 'flex', height: 'calc(100vh - 280px)', gap: 0 }}>
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden', background: 'var(--navy)', border: '1px solid var(--border)', borderRadius: 10 }}>
          <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 10, display: 'flex', gap: 6 }}>
            <button style={{ ...S.btn(), padding: '3px 10px', fontSize: 12 }} onClick={() => setZoom(z=>Math.min(2.5,z+0.15))}>+</button>
            <span style={{ fontSize: 12, color: 'var(--text-dim)', padding: '3px 6px' }}>{Math.round(zoom*100)}%</span>
            <button style={{ ...S.btn(), padding: '3px 10px', fontSize: 12 }} onClick={() => setZoom(z=>Math.max(0.25,z-0.15))}>−</button>
            <span style={{ fontSize: 11, color: 'var(--text-dim)', padding: '3px 6px' }}>{data?.nodes?.length||0} objects</span>
          </div>
          {loading ? <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100%',color:'var(--text-dim)' }}>Loading...</div> : (
            <svg style={{ width:'100%', height:'100%', cursor: panStart?'grabbing':dragging?'grabbing':'grab' }}
              onMouseDown={onSvgMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}>
              <defs><marker id="arrow3" markerWidth="8" markerHeight="8" refX="8" refY="3" orient="auto"><path d="M0,0 L0,6 L8,3 z" fill="rgba(3,105,161,0.4)" /></marker></defs>
              <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
                {(data?.edges||[]).map((e: any) => <path key={e.id} d={getEdgePath(e)} stroke="rgba(3,105,161,0.25)" strokeWidth={1.5} fill="none" markerEnd="url(#arrow3)" />)}
                {(data?.nodes||[]).map((n: any) => {
                  const pos=positions[n.id]||{x:100,y:100}
                  const isSel=selected?.id===n.id
                  const isRoot = n.id === assetId
                  const dc=DOMAIN_COLOR[n.domain]||'#3498db'
                  return (
                    <g key={n.id} data-node="true" transform={`translate(${pos.x},${pos.y})`} onMouseDown={e=>onNodeMouseDown(e,n.id)} style={{cursor:'grab'}}>
                      <rect width={160} height={44} rx={8} fill="var(--navy-light)" stroke={isRoot?'var(--accent)':isSel?'#f39c12':dc+'55'} strokeWidth={isRoot||isSel?2.5:1.5} />
                      <rect width={5} height={44} rx={2} fill={dc} />
                      <text x={16} y={18} fontSize={11} fontWeight={600} fill="var(--text)">{n.name.length>17?n.name.slice(0,16)+'…':n.name}</text>
                      <text x={16} y={32} fontSize={9} fill="rgba(100,116,139,0.7)">{n.assetType.replace(/_/g,' ')} · {n.domain}</text>
                    </g>
                  )
                })}
              </g>
            </svg>
          )}
        </div>
        {selected && (
          <div style={{ width: 240, background:'var(--navy-light)', border:'1px solid var(--border)', borderRadius:10, marginLeft:12, padding:16, overflowY:'auto' as const, flexShrink:0 }}>
            <div style={{ fontWeight:700, fontSize:14, marginBottom:12 }}>{selected.name}</div>
            {[{l:t("common.type"),v:selected.assetType?.replace(/_/g,' ')},{l:'Domain',v:selected.domain},{l:t("common.status"),v:selected.status},{l:'Owner',v:selected.owner||'—'}].map(f=>(
              <div key={f.l} style={{ marginBottom:10 }}><div style={S.label}>{f.l}</div><div style={{fontSize:13}}>{f.v}</div></div>
            ))}
            <button style={{...S.btn(),marginTop:16,fontSize:12,width:'100%'}} onClick={()=>setSelected(null)}>Close</button>
          </div>
        )}
      </div>
    </div>
  )
}

export default function EaViewsPage() {
  const api = useViewsApi()
  const [tab, setTab] = useState('dashboard')
  const [stats, setStats] = useState<any>(null)
  const [activeView, setActiveView] = useState<any>(null)
  const [selectedViewpoint, setSelectedViewpoint] = useState<any>(null)
  const [, setShowBuilder] = useState(false)
  const [objectContextId, setObjectContextId] = useState<string | null>(null)
  const [initialArchState, setInitialArchState] = useState<string | undefined>(undefined)
  const [searchParams, setSearchParams] = useSearchParams()

  // "Open in View" entry point from repository asset pages (Object Context
  // View, spec section 51) - reacts to the objectContext query param via
  // useSearchParams (not a mount-only window.location.search read), so
  // clicking "Show Dependencies" for a different asset while already on
  // this page works correctly too, not just on the initial navigation in.
  useEffect(() => {
    const id = searchParams.get('objectContext')
    if (id) { setObjectContextId(id); setTab('object-context') }
  }, [searchParams])

  // "Related Architecture Views" entry point from ADM cycle pages (spec
  // section 70) - lands on My Views pre-filtered to the requested state.
  useEffect(() => {
    const state = searchParams.get('architectureState')
    if (state) { setInitialArchState(state); setTab('my-views') }
  }, [searchParams])

  // ADM architecture integration returns this stable URL after creating,
  // refreshing, or linking a TenantView. Resolve it through the existing
  // tenant-scoped EA Views API and open the normal viewer; no ADM-specific
  // renderer or copied dataset is introduced here.
  useEffect(() => {
    const viewId = searchParams.get('viewId')
    if (!viewId) return
    let cancelled = false
    api.get(`/ea-views/${encodeURIComponent(viewId)}`).then((view: any) => {
      if (!cancelled && view?.id && !view?.statusCode) {
        setActiveView(view)
        setTab('viewer')
      }
    }).catch(() => {})
    return () => { cancelled = true }
    // useViewsApi returns lightweight request functions on each render; the
    // query parameter is the navigation identity that should trigger reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const loadStats = useCallback(() => { api.get('/ea-views/stats').then(setStats) }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadStats() }, [loadStats])

  const openView = (v: any) => { setActiveView(v); setTab('viewer') }

  const handleLibraryCreate = (viewpoint: any) => {
    setSelectedViewpoint(viewpoint)
    setShowBuilder(true)
    setTab('builder')
  }

  const handleViewCreated = (v: any) => {
    setShowBuilder(false)
    setSelectedViewpoint(null)
    loadStats()
    openView(v)
  }

  const TABS = [
    { id:'dashboard', label:'🏠 Dashboard' },
    { id:'library', label:'📚 View Library' },
    { id:'my-views', label:'📋 My Views' },
    { id:'packs', label:'📦 Architecture Packs' },
    { id:'snapshots', label:'📸 Snapshots' },
    // EA Repository Production Readiness, item 9: a direct entry point,
    // not reachable only via an object's own "Explore Dependencies"
    // button in the Repository. Reuses ObjectContextViewer entirely
    // (via DependencyExplorerEntry below) - no second graph engine.
    { id:'dependency-explorer', label:'🕸 Dependency Explorer' },
  ]

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:20, fontWeight:700 }}>🗺 EA Views & Viewpoints Studio</div>
          <div style={{ fontSize:12, color:'var(--text-dim)' }}>Dynamic architecture views powered by your EA repository</div>
        </div>
        <button style={S.btn('primary')} onClick={()=>{ setSelectedViewpoint(null); setShowBuilder(true); setTab('builder') }}>+ New View</button>
      </div>

      {tab !== 'viewer' && tab !== 'builder' && tab !== 'object-context' && (
        <div style={S.tabs}>
          {TABS.map(t=><button key={t.id} style={S.tab(tab===t.id)} onClick={()=>setTab(t.id)}>{t.label}</button>)}
        </div>
      )}
      {tab !== 'viewer' && tab !== 'builder' && tab !== 'object-context' && (() => {
        const TAB_HELP: Record<string, string> = {
          dashboard: "A quick summary of the diagrams and views that have been created from your architecture data.",
          library: "Ready-made templates for common types of diagrams - pick one to quickly build a view without starting from scratch.",
          'my-views': "The diagrams and views you've already created. Open one to view, edit, or export it.",
          snapshots: "A saved copy of a view at a specific point in time, so you can look back at how something looked before it changed.",
        }
        return TAB_HELP[tab] ? (
          <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '10px 20px 0', display: 'flex', alignItems: 'center' }}>
            <HelpTip text={TAB_HELP[tab]} />
          </div>
        ) : null
      })()}

      <div style={S.content}>
        {tab === 'dashboard' && <ViewsDashboard api={api} stats={stats} onTab={setTab} onOpenView={openView} />}
        {tab === 'library' && <ViewLibrary api={api} onCreate={handleLibraryCreate} onOpen={openView} />}
        {tab === 'my-views' && <MyViews api={api} onOpen={openView} initialArchitectureState={initialArchState} />}
        {tab === 'packs' && <CollectionsPanel api={api} onOpenView={openView} />}
        {tab === 'snapshots' && <SnapshotsPanel api={api} />}
        {tab === 'builder' && <ViewBuilder api={api} viewpoint={selectedViewpoint} onCreated={handleViewCreated} onCancel={()=>setTab('my-views')} />}
        {tab === 'viewer' && activeView && <ViewViewer api={api} view={activeView} onBack={()=>setTab('my-views')} onRefresh={loadStats} />}
        {tab === 'object-context' && objectContextId && <ObjectContextViewer api={api} assetId={objectContextId} onBack={() => { setTab('dashboard'); setSearchParams({}) }} />}
        {tab === 'dependency-explorer' && <DependencyExplorerEntry api={api} onBack={() => setTab('dashboard')} />}
      </div>
    </div>
  )
}
